// @flow
import React from 'react';
import styled, { css } from 'styled-components';
import theme, { colors } from '../../shared/theme';
import { Tooltip } from '../../shared/globals';

type Props = {
  glyph: string,
  size?: number | string,
  fill?: string,
  tipText?: string,
  style?: any,
  tipLocation?: string,
  count?: ?string,
  color?: ?string,
  viewBox?: ?string,
  onClick?: Function,
  dataCy?: string
};

export const InlineSvg = styled.svg`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
  color: inherit;
  fill: ${props =>
    props.color
      ? `${colors[props.color]}`
      : props.fill
      ? props.fill
      : 'currentColor'};
`;

export const SvgWrapper = styled.div`
  display: inline-block;
  flex: 0 0 ${props => (props.size ? `${props.size}px` : '32px')};
  width: ${props => (props.size ? `${props.size}px` : '32px')};
  height: ${props => (props.size ? `${props.size}px` : '32px')};
  min-width: ${props => (props.size ? `${props.size}px` : '32px')};
  min-height: ${props => (props.size ? `${props.size}px` : '32px')};
  position: relative;
  color: inherit;

  @media (min-width: 768px) {
    ${props => (props.tipText ? Tooltip(props) : '')};
  }

  ${props =>
    props.count &&
    css`
      background-color: transparent;
      &:after {
        content: ${props.count ? `'${props.count}'` : `''`};
        position: absolute;
        left: calc(100% - 12px);
        top: -2px;
        font-size: 14px;
        font-weight: 600;
        background: ${theme.bg.default};
        color: ${({ theme: thm }) =>
          process.env.NODE_ENV === 'production'
            ? thm.text.default
            : thm.warn.alt};
        border-radius: 8px;
        padding: 2px 4px;
        border: 2px solid
          ${({ theme: thm }) =>
            process.env.NODE_ENV === 'production'
              ? thm.text.default
              : thm.warn.alt};
      }
    `};
`;

type GlyphProps = {
  glyph: string
};

export const Glyph = ({ glyph }: GlyphProps) => {
  switch (glyph) {
    case 'manageSubscription':
      return (
        <>
          <rect
            x="12.5"
            y="12.5"
            width="35"
            height="26"
            rx="3.5"
            fill="#ffffff"
            stroke="#363D42"
          />
          <path
            d="M12.0337 5.24363C13.2055 5.24363 14.1555 4.29366 14.1555 3.12182C14.1555 1.94997 13.2055 1 12.0337 1C10.8618 1 9.91187 1.94997 9.91187 3.12182C9.91187 4.29366 10.8618 5.24363 12.0337 5.24363Z"
            fill="white"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14.7928 24.8994C14.7928 25.1116 14.7928 25.3238 14.7928 25.536C14.5807 26.809 13.3076 27.87 12.0345 27.87C10.5492 27.87 9.4883 26.809 9.27612 25.536C9.27612 25.3238 9.27612 25.1116 9.27612 24.8994"
            fill="white"
          />
          <path
            d="M14.7928 24.8994C14.7928 25.1116 14.7928 25.3238 14.7928 25.536C14.5807 26.809 13.3076 27.87 12.0345 27.87C10.5492 27.87 9.4883 26.809 9.27612 25.536C9.27612 25.3238 9.27612 25.1116 9.27612 24.8994"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.27508 24.8988H22.8547C19.4598 21.7161 19.4598 20.0187 19.4598 13.0167C19.4598 11.1071 18.8232 9.19742 17.338 7.71215C15.8527 6.22688 13.9431 5.59033 12.0334 5.59033C10.1238 5.59033 8.21417 6.43906 6.7289 7.71215C5.24363 8.98524 4.3949 10.8949 4.3949 13.0167C4.3949 19.8065 4.3949 21.7161 1 24.8988H9.27508Z"
            fill="white"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7.93506 16.7956L10.5147 19.4925L16.411 13.3281"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="28.5"
            y1="19.5"
            x2="36.5"
            y2="19.5"
            stroke="black"
            strokeLinecap="round"
          />
          <line
            x1="28.5"
            y1="25.5"
            x2="36.5"
            y2="25.5"
            stroke="black"
            strokeLinecap="round"
          />
          <line
            x1="21.5"
            y1="31.5"
            x2="36.5"
            y2="31.5"
            stroke="black"
            strokeLinecap="round"
          />
          <circle cx="41.5" cy="19.5" r="1" fill="white" stroke="#363D42" />
          <circle cx="41.5" cy="25.5" r="1" fill="white" stroke="#363D42" />
          <circle cx="41.5" cy="31.5" r="1" fill="white" stroke="#363D42" />
        </>
      );

    case 'emailNotification':
      return (
        <>
          <path
            d="M40 29.8125H41.5312C42.4688 29.8125 43.25 29.0313 43.25 28.0938V2.46875C43.25 1.53125 42.4688 0.75 41.5312 0.75H2.46875C1.53125 0.75 0.75 1.53125 0.75 2.46875V28.0938C0.75 29.0313 1.53125 29.8125 2.46875 29.8125H38"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42.1562 1.6875L23.0938 19.1875C22.4688 19.8125 21.5313 19.8125 20.9063 19.1875L1.84375 1.6875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42.1562 28.875L27.3125 15.2812"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16.6875 15.2812L1.84375 28.875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M39.8348 22.0134C40.6669 22.0134 41.3415 21.3388 41.3415 20.5067C41.3415 19.6746 40.6669 19 39.8348 19C39.0027 19 38.3281 19.6746 38.3281 20.5067C38.3281 21.3388 39.0027 22.0134 39.8348 22.0134Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M41.7944 35.4238C41.7944 35.5745 41.7944 35.7252 41.7944 35.8758C41.6437 36.7799 40.7397 37.5332 39.8357 37.5332C38.781 37.5332 38.0276 36.7799 37.877 35.8758C37.877 35.7252 37.877 35.5745 37.877 35.4238"
            fill="none"
            fillOpacity="0.3"
          />
          <path
            d="M41.7944 35.4238C41.7944 35.5745 41.7944 35.7252 41.7944 35.8758C41.6437 36.7799 40.7397 37.5332 39.8357 37.5332C38.781 37.5332 38.0276 36.7799 37.877 35.8758C37.877 35.7252 37.877 35.5745 37.877 35.4238"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M37.8761 35.4238H47.519C45.1083 33.1638 45.1083 31.9584 45.1083 26.9863C45.1083 25.6303 44.6562 24.2743 43.6016 23.2196C42.5469 22.1649 41.1908 21.7129 39.8348 21.7129C38.4788 21.7129 37.1228 22.3156 36.0681 23.2196C35.0134 24.1236 34.4107 25.4796 34.4107 26.9863C34.4107 31.8078 34.4107 33.1638 32 35.4238H37.8761Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'emailSettings':
      return (
        <>
          <path
            d="M40 29.8125H41.5312C42.4688 29.8125 43.25 29.0313 43.25 28.0938V2.46875C43.25 1.53125 42.4688 0.75 41.5312 0.75H2.46875C1.53125 0.75 0.75 1.53125 0.75 2.46875V28.0938C0.75 29.0313 1.53125 29.8125 2.46875 29.8125H29"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42.1562 1.6875L23.0938 19.1875C22.4688 19.8125 21.5313 19.8125 20.9063 19.1875L1.84375 1.6875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42.1562 28.875L27.3125 15.2812"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16.6875 15.2812L1.84375 28.875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M46.75 30.0938V26.1875H43.7813C43.625 25.875 43.4687 25.4063 43.3125 25.0938L45.3438 22.9062L42.6875 20.25L40.5 22.4375C40.1875 22.2813 39.875 22.125 39.4062 21.9688V19H35.5V21.9688C35.1875 22.125 34.7188 22.2813 34.4063 22.4375L32.2187 20.4063L29.4062 23.2188L31.4375 25.25C31.2812 25.5625 31.125 25.875 30.9687 26.3438H28V30.25H30.9687C31.125 30.5625 31.2812 31.0313 31.4375 31.3438L29.4062 33.5313L32.2187 36.3438L34.4063 34.3125C34.7188 34.4688 35.0312 34.625 35.5 34.7813V37.75H39.4062V34.7813C39.7187 34.625 40.1875 34.4688 40.5 34.3125L42.5312 36.3438L45.3438 33.5313L43.3125 31.5C43.4687 31.1875 43.625 30.875 43.7813 30.4063L46.75 30.0938ZM37.5313 30.875C36.125 30.875 34.875 29.625 34.875 28.2188C34.875 26.8125 36.125 25.5625 37.5313 25.5625C38.9375 25.5625 40.1875 26.8125 40.1875 28.2188C40.1875 29.625 38.9375 30.875 37.5313 30.875Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'no-results':
      return (
        <>
          <g opacity="0.8" clipPath="url(#clip0)">
            <path
              d="M67.6923 77.5281C55.2067 81.3449 42.5295 84.5057 29.7134 86.9974C28.5857 87.2136 27.4189 86.9924 26.4534 86.3796C25.4879 85.7668 24.797 84.8086 24.5231 83.7026L12.2352 34.4187C11.9662 33.3397 12.1138 32.1983 12.6491 31.219C13.1843 30.2397 14.0684 29.4935 15.1275 29.127C27.5666 24.7713 40.3833 21.5757 53.4114 19.5818C54.5185 19.4082 55.6493 19.652 56.5817 20.2654C57.5141 20.8788 58.1802 21.8173 58.4492 22.8963L70.7378 72.183C71.0146 73.2893 70.8529 74.4606 70.2864 75.4551C69.7198 76.4493 68.7913 77.1914 67.6923 77.5281Z"
              fill="#E9E9E9"
            />
            <path
              d="M57.5588 13.9588C44.531 15.9527 31.714 19.1483 19.2751 23.504C18.216 23.8704 17.3319 24.6167 16.7967 25.596C16.2614 26.5752 16.1138 27.7167 16.3828 28.7957L27.554 73.6009C27.6291 73.9079 27.7653 74.1969 27.9546 74.4508C28.1439 74.7047 28.3824 74.9181 28.6563 75.079L37.1468 79.9984C37.6725 80.2998 38.295 80.391 38.8874 80.2528C46.2344 78.5831 61.8969 74.9348 71.8255 71.9088C72.9284 71.575 73.8612 70.8331 74.4306 69.8367C75.0001 68.8405 75.1628 67.6662 74.8848 66.5571L62.5969 17.2732C62.3279 16.1942 61.6617 15.2557 60.7294 14.6423C59.797 14.0289 58.6659 13.7852 57.5588 13.9588Z"
              fill="white"
            />
            <path
              d="M56.0707 76.7818C49.6588 78.3805 43.3551 79.8451 39.0165 80.8321C38.2782 80.9996 37.5044 80.8834 36.8511 80.5062L28.3606 75.5867C28.0181 75.3858 27.7194 75.1195 27.4816 74.8026C27.2439 74.486 27.0719 74.1253 26.9757 73.7417L15.8052 28.9395C15.5005 27.7172 15.6676 26.4242 16.2735 25.3147C16.8795 24.2053 17.8805 23.3595 19.0799 22.9436C31.5547 18.582 44.4059 15.3779 57.4683 13.3723C58.7228 13.1756 60.0045 13.452 61.0609 14.1471C62.1172 14.8421 62.8724 15.9056 63.1772 17.1283L75.4658 66.4151C75.781 67.672 75.5967 69.0028 74.9512 70.132C74.3059 71.2609 73.2486 72.1018 71.9988 72.4802C67.4593 73.8599 61.7197 75.3734 56.0707 76.7818ZM38.3394 18.4262C31.9535 20.0184 25.6596 21.8935 19.4731 24.0632C18.5519 24.381 17.7832 25.0303 17.3191 25.8825C16.855 26.7347 16.7293 27.7278 16.967 28.6651L28.1346 73.4559C28.1919 73.6867 28.2953 73.9036 28.4385 74.0937C28.5818 74.284 28.7621 74.4436 28.9688 74.5634L37.4563 79.4836C37.8454 79.7059 38.3048 79.7746 38.7439 79.6765C46.8847 77.827 61.9877 74.2847 71.6385 71.3434C72.5947 71.0545 73.4039 70.4116 73.8982 69.548C74.3922 68.6845 74.5336 67.6662 74.2926 66.7045L62.0047 17.4206C61.7724 16.4875 61.1977 15.6753 60.3926 15.1432C59.5876 14.6111 58.6103 14.3974 57.6524 14.544C51.1671 15.5399 44.7166 16.8362 38.3394 18.4262Z"
              fill="#262626"
            />
            <path
              d="M33.6661 73.1108L27.7969 74.5742L38.0148 80.6596L36.5628 74.8358C36.4104 74.2248 36.0196 73.6997 35.4764 73.3762C34.9332 73.0527 34.282 72.9573 33.6661 73.1108Z"
              fill="#262626"
            />
            <path
              d="M64.9465 62.5457L39.1821 68.9695C39.0281 69.0079 38.8956 69.1055 38.8137 69.2407C38.7317 69.3759 38.7071 69.5376 38.7452 69.6903C38.7833 69.8432 38.881 69.9744 39.0168 70.0553C39.1526 70.1362 39.3154 70.1602 39.4694 70.1218L65.2338 63.698C65.3879 63.6596 65.5204 63.562 65.6024 63.4268C65.6843 63.2916 65.7089 63.1299 65.6708 62.9769C65.6327 62.8243 65.5351 62.693 65.3993 62.6122C65.2635 62.5313 65.1006 62.5073 64.9465 62.5457Z"
              fill="#262626"
            />
            <path
              d="M38.5507 25.3215L23.7471 29.0125C23.5931 29.0509 23.4606 29.1484 23.3786 29.2835C23.2967 29.4187 23.272 29.5805 23.3101 29.7333C23.3482 29.8861 23.4459 30.0173 23.5817 30.0982C23.7176 30.1791 23.8803 30.203 24.0343 30.1646L38.838 26.4736C38.992 26.4352 39.1246 26.3377 39.2065 26.2025C39.2885 26.0673 39.3131 25.9056 39.275 25.7528C39.2369 25.6 39.1392 25.4687 39.0034 25.3878C38.8676 25.307 38.7048 25.2831 38.5507 25.3215Z"
              fill="#262626"
            />
            <path
              d="M32.0249 36.3314L25.9582 37.844C25.8042 37.8824 25.6717 37.9799 25.5898 38.1151C25.5078 38.2502 25.4832 38.412 25.5213 38.5648C25.5594 38.7176 25.6571 38.8489 25.7929 38.9297C25.9287 39.0106 26.0915 39.0345 26.2455 38.9961L32.3121 37.4835C32.4661 37.4451 32.5986 37.3476 32.6806 37.2125C32.7625 37.0773 32.7871 36.9155 32.749 36.7627C32.711 36.61 32.6133 36.4787 32.4774 36.3978C32.3417 36.3169 32.1789 36.293 32.0249 36.3314Z"
              fill="#262626"
            />
            <path
              d="M53.2963 58.3196C61.663 56.2335 66.7673 47.8123 64.6974 39.5105C62.6275 31.2085 54.1672 26.1695 45.8005 28.2556C37.4335 30.3417 32.3291 38.7628 34.399 47.0647C36.4689 55.3665 44.9293 60.4057 53.2963 58.3196Z"
              fill="white"
            />
            <path
              d="M55.3973 56.8757C53.0589 57.4587 50.6014 57.3405 48.3359 56.5361C46.0703 55.732 44.0981 54.2776 42.6688 52.357C41.2395 50.4365 40.4173 48.1362 40.3061 45.7468C40.1949 43.3574 40.7997 40.9864 42.0441 38.9335C43.2886 36.8806 45.1166 35.238 47.2971 34.2136C49.4775 33.1891 51.9126 32.8288 54.2944 33.178C56.676 33.5274 58.8973 34.5706 60.6775 36.1759C62.4577 37.7811 63.7164 39.8764 64.295 42.1967C65.067 45.3079 64.5639 48.6004 62.8958 51.3523C61.2275 54.1043 58.531 56.0907 55.3973 56.8757ZM49.8654 34.6884C47.7813 35.2081 45.8969 36.3293 44.4503 37.9103C43.004 39.4912 42.0605 41.4609 41.7393 43.5703C41.4181 45.6798 41.7336 47.8341 42.6459 49.7611C43.5582 51.6878 45.0263 53.3007 46.8647 54.3955C48.7028 55.4903 50.8288 56.0181 52.9733 55.9119C55.1181 55.8058 57.1852 55.0704 58.9132 53.799C60.6415 52.5275 61.9531 50.777 62.6821 48.7688C63.4112 46.7606 63.5253 44.585 63.0097 42.5171C62.3157 39.7455 60.5419 37.3647 58.0776 35.897C55.6133 34.4294 52.6598 33.9947 49.8654 34.6884Z"
              fill="#262626"
            />
            <path
              d="M62.6704 50.5013L61.8017 51.9343L73.0441 58.6304L73.9128 57.1972L62.6704 50.5013Z"
              fill="#262626"
            />
            <path
              d="M66.8922 51.9445L65.0681 54.9537L75.2753 61.0329L77.0993 58.024L66.8922 51.9445Z"
              fill="#262626"
            />
          </g>
          <defs>
            <clipPath id="clip0">
              <rect
                width="77"
                height="78.7907"
                fill="white"
                transform="translate(0.612549 19.4839) rotate(-14)"
              />
            </clipPath>
          </defs>
        </>
      );
    case 'warning':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.7398 1.57585L15.5698 9.99585C16.1352 10.9711 16.1364 12.1741 15.5729 13.1505C15.0094 14.1268 13.9671 14.7276 12.8398 14.7259H3.1598C2.0325 14.7276 0.990201 14.1268 0.426686 13.1505C-0.136828 12.1741 -0.135642 10.9711 0.429798 9.99585L5.2598 1.57585C5.82743 0.60021 6.87104 0 7.9998 0C9.12855 0 10.1722 0.60021 10.7398 1.57585ZM12.8398 13.3259C13.4661 13.3306 14.0466 12.9983 14.3598 12.4559V12.4159C14.6683 11.8658 14.6529 11.1914 14.3198 10.6559L9.4798 2.27585C9.16339 1.74932 8.59408 1.42724 7.9798 1.42724C7.36551 1.42724 6.7962 1.74932 6.4798 2.27585L1.6398 10.6959C1.32422 11.2383 1.32326 11.9082 1.63728 12.4515C1.95131 12.9949 2.53224 13.3285 3.1598 13.3259H12.8398ZM7.2998 7.99585V4.38585C7.2998 4.13577 7.43322 3.90468 7.6498 3.77964C7.86638 3.65459 8.13322 3.65459 8.3498 3.77964C8.56638 3.90468 8.6998 4.13577 8.6998 4.38585V7.99585C8.6998 8.38245 8.3864 8.69585 7.9998 8.69585C7.6132 8.69585 7.2998 8.38245 7.2998 7.99585ZM6.9498 10.7959C6.9498 10.216 7.4199 9.74585 7.9998 9.74585C8.28248 9.73133 8.55807 9.83728 8.75822 10.0374C8.95837 10.2376 9.06432 10.5132 9.0498 10.7959C9.0498 11.3758 8.5797 11.8459 7.9998 11.8459C7.4199 11.8459 6.9498 11.3758 6.9498 10.7959Z"
            fill="black"
          />
          <mask
            id="mask0"
            mask-type="alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="16"
            height="15"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10.7398 1.57585L15.5698 9.99585C16.1352 10.9711 16.1364 12.1741 15.5729 13.1505C15.0094 14.1268 13.9671 14.7276 12.8398 14.7259H3.1598C2.0325 14.7276 0.990201 14.1268 0.426686 13.1505C-0.136828 12.1741 -0.135642 10.9711 0.429798 9.99585L5.2598 1.57585C5.82743 0.60021 6.87104 0 7.9998 0C9.12855 0 10.1722 0.60021 10.7398 1.57585ZM12.8398 13.3259C13.4661 13.3306 14.0466 12.9983 14.3598 12.4559V12.4159C14.6683 11.8658 14.6529 11.1914 14.3198 10.6559L9.4798 2.27585C9.16339 1.74932 8.59408 1.42724 7.9798 1.42724C7.36551 1.42724 6.7962 1.74932 6.4798 2.27585L1.6398 10.6959C1.32422 11.2383 1.32326 11.9082 1.63728 12.4515C1.95131 12.9949 2.53224 13.3285 3.1598 13.3259H12.8398ZM7.2998 7.99585V4.38585C7.2998 4.13577 7.43322 3.90468 7.6498 3.77964C7.86638 3.65459 8.13322 3.65459 8.3498 3.77964C8.56638 3.90468 8.6998 4.13577 8.6998 4.38585V7.99585C8.6998 8.38245 8.3864 8.69585 7.9998 8.69585C7.6132 8.69585 7.2998 8.38245 7.2998 7.99585ZM6.9498 10.7959C6.9498 10.216 7.4199 9.74585 7.9998 9.74585C8.28248 9.73133 8.55807 9.83728 8.75822 10.0374C8.95837 10.2376 9.06432 10.5132 9.0498 10.7959C9.0498 11.3758 8.5797 11.8459 7.9998 11.8459C7.4199 11.8459 6.9498 11.3758 6.9498 10.7959Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask0)">
            <rect
              x="-0.000244141"
              y="-1.00391"
              width="16"
              height="16"
              fill="#C82124"
            />
          </g>
        </>
      );
    case 'empty-fields':
      return (
        <>
          <path
            d="M0 25.4675V17.9008C0 16.3683 1.69669 15.4378 2.98289 16.2725L12.9441 22.7172C14.4766 23.7024 13.9156 26.0558 12.1094 26.2611L2.14822 27.3967C0.998854 27.5199 0 26.6168 0 25.4675Z"
            fill="#F4F4F4"
          />
          <path
            d="M24.4437 20.9965L13.2921 17.6579C11.0344 16.9874 10.4186 14.073 12.2111 12.5405L26.072 0.718423C28.1928 -1.08773 31.422 0.759471 30.9294 3.50974L28.2065 18.6704C27.9055 20.4219 26.1404 21.5028 24.4437 20.9965Z"
            fill="#F4F4F4"
          />
          <path
            d="M123.863 0.715762L129.323 5.81951C130.431 6.84573 129.952 8.69293 128.488 9.06237L117.145 11.9084C115.407 12.3463 114.08 10.3623 115.147 8.92554L121.031 0.975737C121.701 0.0589786 123.028 -0.0504843 123.863 0.715762Z"
            fill="#F4F4F4"
          />
          <path
            d="M6.23866 8.24883C8.49817 8.24883 10.3299 6.41713 10.3299 4.15762C10.3299 1.8981 8.49817 0.0664062 6.23866 0.0664062C3.97915 0.0664062 2.14746 1.8981 2.14746 4.15762C2.14746 6.41713 3.97915 8.24883 6.23866 8.24883Z"
            fill="#F4F4F4"
          />
          <path
            d="M146 63.0564C146 64.11 145.918 65.1362 145.768 66.1487H3.31444C2.53451 63.5763 2.12402 60.8397 2.12402 58.0074C2.12402 42.4088 14.7534 29.7795 30.3383 29.7795C30.7624 29.7795 31.1729 29.7931 31.5834 29.8068C34.7989 29.9437 37.7134 28.0007 38.849 25.0041C43.2276 13.5652 54.3108 5.4375 67.2959 5.4375C78.1738 5.4375 87.7245 11.1433 93.1156 19.7362C94.7165 22.2812 97.672 23.6632 100.641 23.1432C102.078 22.8969 103.556 22.7601 105.061 22.7601C116.267 22.7601 125.79 29.93 129.293 39.9322C130.005 41.9436 131.524 43.5856 133.508 44.3928C140.842 47.4168 146 54.6277 146 63.0564Z"
            fill="#EFEFEF"
          />
          <path
            d="M102.33 66.1267H68.9576L52.5654 57.9853L86.8412 56.7949L102.33 66.1267Z"
            fill="#D0D0D0"
          />
          <path
            d="M83.0854 38.1172H51.1904V47.4626H83.0854V38.1172Z"
            fill="#F7F9FC"
          />
          <path
            d="M83.0854 26.5273H51.1904V35.8728H83.0854V26.5273Z"
            fill="#F7F9FC"
          />
          <path
            d="M84.4604 50.7246H52.5654V58.004H84.4604V50.7246Z"
            fill="#E4E4E4"
          />
          <path
            d="M96.3451 49.6797H64.4502V59.0252H96.3451V49.6797Z"
            fill="#F7F9FC"
          />
          <path
            d="M67.1372 44.5947C68.1348 44.5947 68.9434 43.7861 68.9434 42.7886C68.9434 41.7911 68.1348 40.9824 67.1372 40.9824C66.1397 40.9824 65.3311 41.7911 65.3311 42.7886C65.3311 43.7861 66.1397 44.5947 67.1372 44.5947Z"
            fill="#D0D0D0"
          />
          <path
            d="M80.2261 56.5498C81.2236 56.5498 82.0323 55.7412 82.0323 54.7437C82.0323 53.7461 81.2236 52.9375 80.2261 52.9375C79.2286 52.9375 78.4199 53.7461 78.4199 54.7437C78.4199 55.7412 79.2286 56.5498 80.2261 56.5498Z"
            fill="#D0D0D0"
          />
          <path
            d="M67.1372 32.9971C68.1348 32.9971 68.9434 32.1884 68.9434 31.1909C68.9434 30.1934 68.1348 29.3848 67.1372 29.3848C66.1397 29.3848 65.3311 30.1934 65.3311 31.1909C65.3311 32.1884 66.1397 32.9971 67.1372 32.9971Z"
            fill="#D0D0D0"
          />
        </>
      );
    case 'webhooks-logo':
      return (
        <>
          <g clipPath="url(#clip0)">
            <path
              d="M49.497 41.6145C45.1038 48.9998 40.8949 56.1515 36.5981 63.2493C35.4947 65.0716 34.9485 66.556 35.8301 68.8723C38.2639 75.2712 34.8305 81.4983 28.3778 83.1885C22.2923 84.7831 16.3633 80.7836 15.1559 74.2684C14.086 68.5017 18.5612 62.8485 24.9195 61.9471C25.452 61.8709 25.9961 61.8622 26.8913 61.7951L36.5629 45.5771C30.48 39.5285 26.8594 32.4584 27.6606 23.6968C28.227 17.5037 30.6626 12.1515 35.1137 7.76491C43.6393 -0.635596 56.6458 -1.99579 66.6843 4.45199C76.3254 10.6455 80.7409 22.7097 76.9771 33.0352C74.1391 32.2658 71.2813 31.4899 68.1398 30.6377C69.3215 24.8968 68.4474 19.7417 64.5755 15.3253C62.0174 12.4095 58.7348 10.8811 55.0024 10.318C47.5195 9.18763 40.1727 13.9949 37.9927 21.3395C35.5183 29.6746 39.2635 36.4839 49.497 41.615V41.6145Z"
              fill="#C73A63"
            />
            <path
              d="M62.0435 32.8807C65.1386 38.341 68.2814 43.8836 71.396 49.3753C87.1386 44.5047 99.0081 53.2195 103.266 62.5495C108.41 73.8199 104.894 87.1684 94.7926 94.1218C84.4244 101.259 71.3123 100.04 62.1263 90.871C64.4674 88.9112 66.8201 86.9428 69.3339 84.8398C78.4069 90.7161 86.3424 90.4395 92.2337 83.4808C97.2575 77.5444 97.1486 68.693 91.979 62.8812C86.0132 56.1746 78.0222 55.9701 68.3629 62.4079C64.3561 55.2993 60.2796 48.2474 56.3994 41.0874C55.091 38.6743 53.6467 37.2739 50.6978 36.7634C45.7733 35.9096 42.5942 31.6808 42.4033 26.9431C42.2157 22.2575 44.9763 18.0221 49.2908 16.372C53.5648 14.7369 58.5803 16.0565 61.4551 19.6903C63.8045 22.6592 64.5511 26.0006 63.3147 29.6622C62.971 30.6833 62.5259 31.6712 62.0435 32.8811V32.8807Z"
              fill="#4B4B4B"
            />
            <path
              d="M69.4412 77.5167H50.4895C48.673 84.988 44.7494 91.0201 37.989 94.856C32.7333 97.8372 27.0689 98.848 21.0402 97.8745C9.93998 96.0845 0.863733 86.0919 0.0645921 74.8402C-0.83972 62.0937 7.92143 50.7636 19.5992 48.2188C20.4054 51.1467 21.2203 54.1026 22.0265 57.0234C11.3122 62.4899 7.60384 69.3774 10.6025 77.99C13.2421 85.569 20.7404 89.7233 28.8821 88.1167C37.1965 86.4762 41.3884 79.5663 40.8767 68.4769C48.7587 68.4769 56.6475 68.3953 64.5304 68.517C67.6085 68.5655 69.9848 68.2462 72.3036 65.5324C76.1212 61.0672 83.1483 61.4701 87.2599 65.6873C91.4618 69.9973 91.2606 76.932 86.814 81.0602C82.5239 85.0435 75.7461 84.8307 71.7322 80.5385C70.9074 79.6541 70.2573 78.6036 69.4412 77.5167Z"
              fill="#4A4A4A"
            />
          </g>
          <defs>
            <clipPath id="clip0">
              <rect width="106" height="98.9609" fill="white" />
            </clipPath>
          </defs>
        </>
      );
    case 'freshdesk-logo':
      return (
        <>
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.2 0H24.8918C26.8965 0 28.4835 1.58706 28.4835 3.59176V14.2835C28.4835 22.1353 22.1353 28.4835 14.2835 28.4835H14.2C6.34823 28.4835 0 22.1353 0 14.2835C0 6.43176 6.34823 0.0835294 14.2 0Z"
            fill="#20A849"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.2409 6.23077C10.8065 6.23077 8.01099 9.03297 8.01099 12.4757C8.01099 12.4757 8.01099 12.4757 8.01099 12.5557V16.719C8.01099 17.8399 8.96944 18.8006 10.0876 18.8006H11.8448V13.9168H9.44866V12.5557C9.52853 9.8336 11.8448 7.75197 14.4805 7.91209C17.0364 7.99216 19.0332 10.0738 19.113 12.5557V13.9168H16.7169V18.8006H18.3143V18.8807C18.3143 19.9215 17.4358 20.8022 16.3974 20.8022H14.4805C14.3208 20.8022 14.161 20.8823 14.161 21.0424C14.161 21.2025 14.3208 21.3626 14.4805 21.3626H16.3974C17.7552 21.3626 18.8734 20.2418 18.8734 18.8807V18.7206C19.8319 18.4804 20.4709 17.6798 20.4709 16.719V12.5557C20.5507 9.03297 17.7552 6.23077 14.2409 6.23077C14.2409 6.23077 14.3208 6.23077 14.2409 6.23077Z"
            fill="white"
          />
        </>
      );
    case 'delete':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.90714 1.42857H9.5C9.77614 1.42857 10 1.65243 10 1.92857C10 2.20471 9.77614 2.42857 9.5 2.42857H9.28571V9.52143C9.28572 10.5719 8.43621 11.4246 7.38571 11.4286H2.61429C1.56379 11.4246 0.714278 10.5719 0.714286 9.52143V2.42857H0.5C0.223858 2.42857 0 2.20471 0 1.92857C0 1.65243 0.223858 1.42857 0.5 1.42857H3.07143V1.21429C3.07143 0.543654 3.61508 0 4.28571 0H5.71429C6.37653 0.0116885 6.90725 0.551934 6.90714 1.21429V1.42857ZM4.28571 1C4.17411 1.00764 4.08874 1.1025 4.09286 1.21429V1.42857H5.92857V1.21429C5.93059 1.15686 5.90866 1.10117 5.86803 1.06054C5.8274 1.01991 5.77171 0.997983 5.71429 1H4.28571ZM7.38571 10.4286C7.88392 10.4246 8.28573 10.0197 8.28571 9.52143V2.42857H1.71429V9.52143C1.71427 10.0197 2.11608 10.4246 2.61429 10.4286H7.38571ZM3.69286 3.57143C3.41671 3.57143 3.19286 3.79529 3.19286 4.07143V8.78571C3.22329 9.04075 3.43958 9.23278 3.69643 9.23278C3.95328 9.23278 4.16957 9.04075 4.2 8.78571V4.07143C4.20001 3.93757 4.14636 3.8093 4.05104 3.71533C3.95572 3.62135 3.8267 3.56952 3.69286 3.57143ZM5.8 4.07143V8.78572C5.83043 9.04075 6.04672 9.23278 6.30357 9.23278C6.56042 9.23278 6.77671 9.04075 6.80714 8.78572V4.07143C6.77671 3.81639 6.56042 3.62437 6.30357 3.62437C6.04672 3.62437 5.83043 3.81639 5.8 4.07143Z"
          fill="#12344D"
        />
      );
    case 'setting-info':
      return (
        <>
          <g clipPath="url(#clip0)">
            <path
              d="M13.2834 5.53137L12.3007 5.36467C12.1984 5.03419 12.0668 4.71541 11.9059 4.41126L12.485 3.60115C12.7306 3.25605 12.6926 2.7852 12.3914 2.48689L11.5199 1.61537C11.3561 1.45159 11.1397 1.36093 10.9086 1.36093C10.7273 1.36093 10.5548 1.4165 10.4085 1.52178L9.5955 2.10085C9.27965 1.93415 8.94917 1.79669 8.607 1.69433L8.44322 0.723376C8.37303 0.305162 8.01331 0.00100708 7.58925 0.00100708H6.35801C5.93394 0.00100708 5.57422 0.305162 5.50403 0.723376L5.33441 1.71773C5.00686 1.82009 4.68808 1.95462 4.38392 2.1184L3.57967 1.53933C3.43344 1.43405 3.25797 1.37848 3.07664 1.37848C2.8456 1.37848 2.62626 1.46914 2.46541 1.63292L1.59096 2.50444C1.29266 2.80275 1.25171 3.2736 1.49738 3.6187L2.08229 4.4405C1.92144 4.74758 1.79276 5.06636 1.69332 5.39684L0.722368 5.56062C0.304155 5.6308 0 5.99053 0 6.41459V7.64583C0 8.0699 0.304155 8.42962 0.722368 8.49981L1.71672 8.66943C1.81908 8.99699 1.95361 9.31576 2.11739 9.61992L1.54125 10.4213C1.29558 10.7664 1.3336 11.2372 1.63483 11.5355L2.50635 12.407C2.67013 12.5708 2.88655 12.6615 3.11759 12.6615C3.29891 12.6615 3.47146 12.6059 3.61769 12.5006L4.43949 11.9157C4.73487 12.0707 5.04488 12.1965 5.36365 12.2959L5.52743 13.2786C5.59762 13.6968 5.95734 14.0009 6.3814 14.0009H7.61557C8.03963 14.0009 8.39935 13.6968 8.46954 13.2786L8.63624 12.2959C8.96672 12.1935 9.2855 12.0619 9.58965 11.9011L10.3998 12.4801C10.546 12.5854 10.7215 12.641 10.9028 12.641C11.1338 12.641 11.3502 12.5503 11.514 12.3866L12.3855 11.515C12.6838 11.2167 12.7248 10.7459 12.4791 10.4008L11.9001 9.58775C12.0609 9.28067 12.1954 8.96189 12.2949 8.63434L13.2775 8.47056C13.6957 8.40037 13.9999 8.04065 13.9999 7.61659V6.38534C14.0057 5.96128 13.7016 5.60156 13.2834 5.53137ZM13.2161 7.61659C13.2161 7.65461 13.1898 7.68678 13.1518 7.69263L11.9235 7.89735C11.7685 7.92367 11.6456 8.03773 11.6076 8.18688C11.4965 8.61679 11.3268 9.02916 11.0987 9.41227C11.0198 9.5468 11.0256 9.71351 11.1163 9.84219L11.8386 10.8599C11.8591 10.8892 11.8562 10.9331 11.8299 10.9594L10.9584 11.8309C10.9379 11.8514 10.9174 11.8543 10.9028 11.8543C10.8852 11.8543 10.8706 11.8484 10.8589 11.8397L9.84409 11.1173C9.71833 11.0266 9.54871 11.0208 9.41418 11.0998C9.03106 11.3279 8.6187 11.4975 8.18879 11.6086C8.03671 11.6466 7.92265 11.7724 7.89925 11.9245L7.69161 13.1528C7.68576 13.1908 7.65359 13.2171 7.61557 13.2171H6.38433C6.34631 13.2171 6.31414 13.1908 6.30829 13.1528L6.10357 11.9245C6.07725 11.7695 5.96319 11.6466 5.81404 11.6086C5.39582 11.5004 4.99223 11.3337 4.61497 11.1173C4.55355 11.0822 4.48336 11.0647 4.4161 11.0647C4.33713 11.0647 4.25524 11.0881 4.18798 11.1378L3.16438 11.866C3.14976 11.8748 3.13514 11.8806 3.12051 11.8806C3.10881 11.8806 3.08542 11.8777 3.06495 11.8572L2.19342 10.9857C2.1671 10.9594 2.16418 10.9184 2.18465 10.8863L2.90409 9.87728C2.99476 9.7486 3.00061 9.57897 2.92164 9.44444C2.69353 9.06425 2.51805 8.65189 2.40692 8.22197C2.36597 8.07282 2.24314 7.95876 2.09106 7.93244L0.853973 7.72187C0.815954 7.71602 0.789633 7.68385 0.789633 7.64583V6.41459C0.789633 6.37657 0.815954 6.3444 0.853973 6.33855L2.07352 6.13383C2.22852 6.10751 2.35428 5.99345 2.3923 5.84137C2.5005 5.41146 2.6672 4.99617 2.8924 4.61305C2.97136 4.47852 2.96259 4.31182 2.87192 4.18607L2.14371 3.16247C2.12324 3.13322 2.12616 3.08935 2.15248 3.06303L3.024 2.19151C3.04447 2.17104 3.06495 2.16811 3.07957 2.16811C3.09712 2.16811 3.11174 2.17396 3.12344 2.18274L4.13241 2.90218C4.26109 2.99284 4.43072 2.99869 4.56525 2.91973C4.94544 2.69161 5.35781 2.51614 5.78772 2.405C5.93687 2.36406 6.05093 2.24123 6.07725 2.08915L6.28782 0.852057C6.29367 0.814038 6.32584 0.787717 6.36386 0.787717H7.5951C7.63312 0.787717 7.66529 0.814038 7.67114 0.852057L7.87586 2.0716C7.90218 2.22661 8.01624 2.35236 8.16831 2.39038C8.60992 2.50151 9.03106 2.67406 9.42295 2.90511C9.55748 2.98407 9.72418 2.97822 9.85286 2.88756L10.8618 2.16226C10.8765 2.15349 10.8911 2.14764 10.9057 2.14764C10.9174 2.14764 10.9408 2.15057 10.9613 2.17104L11.8328 3.04256C11.8591 3.06888 11.862 3.10983 11.8416 3.142L11.1192 4.15682C11.0285 4.28258 11.0227 4.4522 11.1017 4.58673C11.3298 4.96985 11.4994 5.38222 11.6105 5.81213C11.6485 5.96421 11.7743 6.07826 11.9264 6.10166L13.1547 6.30931C13.1927 6.31515 13.219 6.34732 13.219 6.38534V7.61659H13.2161Z"
              fill="#475867"
            />
            <path
              d="M6.58824 7.08823C6.58824 6.81209 6.81209 6.58823 7.08824 6.58823C7.36438 6.58823 7.58824 6.81209 7.58824 7.08823V9.08823C7.58824 9.36438 7.36438 9.58823 7.08824 9.58823C6.81209 9.58823 6.58824 9.36438 6.58824 9.08823V7.08823Z"
              fill="#475867"
            />
            <path
              d="M7.58824 5.44118C7.58824 5.71732 7.36438 5.94117 7.08824 5.94117C6.81209 5.94117 6.58824 5.71732 6.58824 5.44118C6.58824 5.16503 6.81209 4.94118 7.08824 4.94118C7.36438 4.94118 7.58824 5.16503 7.58824 5.44118Z"
              fill="#475867"
            />
            <path
              d="M11 7C11 9.20914 9.20914 11 7 11C4.79086 11 3 9.20914 3 7C3 4.79086 4.79086 3 7 3C9.20914 3 11 4.79086 11 7ZM3.79106 7C3.79106 8.77225 5.22775 10.2089 7 10.2089C8.77225 10.2089 10.2089 8.77225 10.2089 7C10.2089 5.22775 8.77225 3.79106 7 3.79106C5.22775 3.79106 3.79106 5.22775 3.79106 7Z"
              fill="#475867"
            />
          </g>
          <defs>
            <clipPath id="clip0">
              <rect width="14" height="14" fill="white" />
            </clipPath>
          </defs>
        </>
      );
    case 'caution':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.4815 11.8225L12.7459 1.87133C12.0718 0.712755 10.8325 0 9.49212 0C8.15172 0 6.91244 0.712755 6.23837 1.87133L0.502744 11.8225C-0.166875 12.9823 -0.167628 14.4111 0.500768 15.5716C1.16917 16.7321 2.40539 17.4485 3.74462 17.4513H15.2396C16.5789 17.4485 17.8151 16.7321 18.4835 15.5716C19.1519 14.4111 19.1511 12.9823 18.4815 11.8225ZM17.0446 14.7438C16.674 15.3907 15.9852 15.7895 15.2396 15.7888H3.74462C2.99883 15.787 2.3105 15.3879 1.93837 14.7416C1.56625 14.0953 1.56672 13.2997 1.93962 12.6538L7.68712 2.70257C8.06285 2.07732 8.73891 1.69485 9.46837 1.69485C10.1978 1.69485 10.8739 2.07732 11.2496 2.70257L16.9971 12.6538C17.3927 13.2897 17.4109 14.0905 17.0446 14.7438ZM9.49212 10.3144C9.95121 10.3144 10.3234 9.94225 10.3234 9.48325V5.16071C10.3234 4.70162 9.95121 4.32945 9.49212 4.32945C9.03304 4.32945 8.66087 4.70162 8.66087 5.16071V9.43575C8.64779 9.66425 8.72956 9.88815 8.88689 10.0544C9.04422 10.2207 9.26318 10.3148 9.49212 10.3144ZM8.36464 12.7983C8.34331 12.1507 8.84501 11.6056 9.49212 11.5732C9.81722 11.5569 10.1348 11.6748 10.3705 11.8993C10.6062 12.1237 10.7394 12.4351 10.739 12.7607C10.7382 13.4086 10.2182 13.9363 9.57031 13.9465C8.92246 13.9568 8.38598 13.4458 8.36464 12.7983Z"
          fill="black"
        />
      );
    case 'fservice-grey':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 7V2.275C13.9962 1.02015 12.9799 0.00384337 11.725 0H7C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14C10.866 14 14 10.866 14 7ZM11.725 0.979818C12.4403 0.979818 13.02 1.55961 13.02 2.27482V6.99982C13.02 10.3246 10.3248 13.0198 7.00005 13.0198C3.67529 13.0198 0.980046 10.3246 0.980046 6.99982C0.980046 3.67506 3.67529 0.979818 7.00005 0.979818H11.725ZM7.165 9.67445C7.09167 9.74778 6.98167 9.78445 6.90833 9.71111C6.835 9.67445 6.79833 9.56445 6.835 9.49111L7.56834 7.29109H4.59831C4.52497 7.29109 4.48831 7.29109 4.45164 7.25442C4.37831 7.18109 4.34164 7.03442 4.41497 6.92442L4.89164 6.33775L6.87167 3.58772C6.90833 3.51438 7.01833 3.47772 7.09167 3.51438C7.165 3.55105 7.20167 3.66105 7.165 3.73439L6.43166 5.93441H9.40169C9.47503 5.93441 9.51169 5.93441 9.54836 5.97108C9.62169 6.04441 9.65836 6.19108 9.58503 6.30108L9.14502 6.92442L7.165 9.67445Z"
            fill="#264966"
          />
        </>
      );
    case 'fping-grey':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 2.275V7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0H11.725C12.9799 0.00384337 13.9962 1.02015 14 2.275ZM7 11.375C9.41625 11.375 11.375 9.41625 11.375 7C11.375 4.58375 9.41625 2.625 7 2.625C4.58375 2.625 2.625 4.58375 2.625 7C2.625 9.41625 4.58375 11.375 7 11.375Z"
            fill="#264966"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.48191 9.24384C6.66257 9.21523 6.81319 9.09027 6.87465 8.918L7.5089 7.14019L7.99857 7.75573C8.09343 7.87498 8.23749 7.94446 8.38986 7.94446H11.375C11.6511 7.94446 11.875 7.7206 11.875 7.44446C11.875 7.16832 11.6511 6.94446 11.375 6.94446H8.63102L7.73151 5.81372C7.61849 5.67164 7.43735 5.60199 7.25826 5.63175C7.07918 5.66151 6.93029 5.786 6.86929 5.95698L6.24055 7.71935L5.79332 7.1392C5.69866 7.0164 5.55238 6.94446 5.39732 6.94446H2.625C2.34886 6.94446 2.125 7.16832 2.125 7.44446C2.125 7.7206 2.34886 7.94446 2.625 7.94446H5.15144L6.00773 9.05525C6.1194 9.20011 6.30126 9.27244 6.48191 9.24384Z"
            fill="#264966"
          />
        </>
      );
    case 'home':
      return (
        <path
          d="M12.5273 5.88621L7.21432 1.0561C7.01917 0.877408 6.76416 0.778302 6.49956 0.77832C6.23496 0.778338 5.97996 0.877478 5.78484 1.0562L0.47279 5.88618C0.363348 5.98584 0.275893 6.10724 0.216009 6.2426C0.156125 6.37797 0.125129 6.52433 0.125 6.67236V12.8123C0.124999 12.9518 0.152486 13.09 0.20589 13.2189C0.259293 13.3479 0.337568 13.465 0.436245 13.5637C0.534921 13.6623 0.652066 13.7406 0.78099 13.794C0.909914 13.8474 1.04809 13.8748 1.18763 13.8748L4.37468 13.8743C4.51556 13.8743 4.65067 13.8183 4.75029 13.7187C4.8499 13.6191 4.90587 13.484 4.90586 13.3431V9.09307H8.09336V13.3431C8.09336 13.484 8.14932 13.6191 8.24894 13.7187C8.34855 13.8183 8.48366 13.8743 8.62455 13.8743L11.8124 13.8748H11.8125C11.952 13.8748 12.0902 13.8473 12.2191 13.7939C12.348 13.7405 12.4651 13.6623 12.5638 13.5636C12.6625 13.4649 12.7407 13.3478 12.7941 13.2189C12.8475 13.09 12.875 12.9518 12.875 12.8123V6.67238C12.8749 6.52437 12.8439 6.37801 12.784 6.24265C12.7241 6.10729 12.6367 5.98589 12.5273 5.88621Z"
          fill="#454B60"
        />
      );
    case 'pagination-right':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.750799 0.50876C0.950536 0.508732 1.14188 0.589096 1.28169 0.731736L4.7856 4.22502C5.07147 4.52123 5.07147 4.99061 4.7856 5.28681L1.28169 8.7801C0.988488 9.0733 0.513109 9.0733 0.219904 8.7801C-0.0733008 8.48689 -0.0733008 8.01151 0.219904 7.71831L3.20353 4.75592L0.219905 1.78291C0.0134565 1.56654 -0.0455749 1.24859 0.0694477 0.97254C0.18447 0.696486 0.451796 0.514524 0.750799 0.50876Z"
          fill="#616161"
        />
      );
    case 'pagination-left':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.2492 9.00003C4.04947 9.00006 3.85812 8.91969 3.71831 8.77705L0.214403 5.28377C-0.0714678 4.98756 -0.0714678 4.51818 0.214403 4.22198L3.71831 0.728693C4.01151 0.435488 4.48689 0.435488 4.7801 0.728693C5.0733 1.0219 5.0733 1.49728 4.7801 1.79048L1.79647 4.75287L4.7801 7.72588C4.98654 7.94225 5.04558 8.26019 4.93055 8.53625C4.81553 8.8123 4.5482 8.99426 4.2492 9.00003Z"
          fill="#616161"
        />
      );
    case 'close-mini':
      return (
        <>
          <rect opacity="0.01" width="20" height="20" fill="#C4C4C4" />
          <path
            d="M9.9746 10.493L13.2434 13.7618C13.51 14.0284 13.9422 14.0284 14.2088 13.7618C14.4754 13.4952 14.4754 13.063 14.2088 12.7964L10.94 9.52762L14.2469 6.22073C14.5158 5.95181 14.5158 5.5158 14.2469 5.24687C13.978 4.97795 13.5419 4.97795 13.273 5.24687L9.96613 8.55376L6.68888 5.27651C6.42229 5.00993 5.99007 5.00993 5.72349 5.27651C5.4569 5.5431 5.4569 5.97532 5.72349 6.2419L9.00074 9.51915L5.6261 12.8938C5.35718 13.1627 5.35718 13.5987 5.6261 13.8676C5.89503 14.1366 6.33104 14.1366 6.59996 13.8676L9.9746 10.493Z"
            fill="#676767"
          />
        </>
      );
    case 'close-mini-white':
      return (
        <path
          d="M9.9746 10.493L13.2434 13.7618C13.51 14.0284 13.9422 14.0284 14.2088 13.7618C14.4754 13.4952 14.4754 13.063 14.2088 12.7964L10.94 9.52762L14.2469 6.22073C14.5158 5.95181 14.5158 5.5158 14.2469 5.24687C13.978 4.97795 13.5419 4.97795 13.273 5.24687L9.96613 8.55376L6.68888 5.27651C6.42229 5.00993 5.99007 5.00993 5.72349 5.27651C5.4569 5.5431 5.4569 5.97532 5.72349 6.2419L9.00074 9.51915L5.6261 12.8938C5.35718 13.1627 5.35718 13.5987 5.6261 13.8676C5.89503 14.1366 6.33104 14.1366 6.59996 13.8676L9.9746 10.493Z"
          fill="#fff"
        />
      );
    case 'blue-filter':
      return (
        <>
          <rect
            x="0.5"
            y="0.5"
            width="19"
            height="19"
            rx="2.5"
            fill="#E5ECFF"
            stroke="#CFD7DF"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.40002 5.3999V7.7499L8.83002 12.1799C9.09409 12.4455 9.24161 12.8053 9.24002 13.1799V15.58L10.76 15V13.1699C10.7577 12.7932 10.9094 12.4319 11.18 12.1699L15.6 7.7499V5.3999H4.40002Z"
            fill="#1B1B96"
          />
        </>
      );
    case 'sort-icon':
      return (
        <>
          <line
            x1="0.75"
            y1="1.25"
            x2="9.25"
            y2="1.25"
            stroke="#12344D"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 4.5H8"
            stroke="#12344D"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 7.5H6"
            stroke="#12344D"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'color-tick':
      return (
        <path d="M1.32822 2.66573C1.01202 2.37184 0.513608 2.38586 0.214979 2.69703C-0.08365 3.00821 -0.0694096 3.49871 0.246786 3.7926L2.60928 5.98843C2.92548 6.28232 3.42389 6.26831 3.72252 5.95713L8.18502 1.30713C8.48365 0.995958 8.46941 0.505456 8.15321 0.211567C7.83702 -0.0823222 7.3386 -0.0683079 7.03998 0.242869L3.11819 4.32943L1.32822 2.66573Z" />
      );
    case 'import-subs':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.4285 0.085345C10.552 0.132561 10.6641 0.205619 10.7571 0.29963L16.4285 5.95677C16.6242 6.14285 16.7349 6.40105 16.7349 6.67106C16.7349 6.94108 16.6242 7.19927 16.4285 7.38535C16.0331 7.77757 15.3954 7.77757 14.9999 7.38535L11.0428 3.41392L11.0428 14.0711C11.0428 14.6233 10.5951 15.0711 10.0428 15.0711C9.49051 15.0711 9.04279 14.6233 9.04279 14.0711L9.0428 3.41392L5.04279 7.44249C4.64737 7.83471 4.00965 7.83471 3.61422 7.44249C3.41856 7.25641 3.30779 6.99822 3.30779 6.7282C3.30779 6.45819 3.41856 6.19999 3.61422 6.01392L9.32851 0.29963C9.42153 0.205619 9.53355 0.132561 9.65708 0.0853449C9.90164 -0.0284483 10.184 -0.0284483 10.4285 0.085345ZM18 13.2C18 12.6477 18.4477 12.2 19 12.2C19.5523 12.2 20 12.6477 20 13.2V16.5714C19.9922 18.4617 18.4617 19.9922 16.5714 20H3.42857C1.53828 19.9922 0.00782741 18.4617 0 16.5714V13.2C0 12.8427 0.190599 12.5126 0.5 12.334C0.809401 12.1553 1.1906 12.1553 1.5 12.334C1.8094 12.5126 2 12.8427 2 13.2V16.5714C2 17.3604 2.63959 18 3.42857 18H16.5714C17.3604 18 18 17.3604 18 16.5714V13.2Z"
            fill="#12344D"
          />
        </>
      );
    case 'import':
      return (
        <>
          <path
            d="M6.58 9.78833C6.66197 9.8704 6.77317 9.91656 6.88917 9.91667H7.11083C7.22657 9.91532 7.33733 9.86935 7.42 9.78833L10.4125 6.79C10.4398 6.76289 10.4615 6.73063 10.4763 6.69508C10.4912 6.65954 10.4988 6.62142 10.4988 6.58292C10.4988 6.54441 10.4912 6.50629 10.4763 6.47075C10.4615 6.43521 10.4398 6.40295 10.4125 6.37583L9.99833 5.96167C9.97172 5.9345 9.93995 5.91293 9.9049 5.89819C9.86984 5.88346 9.83219 5.87587 9.79417 5.87587C9.75614 5.87587 9.71849 5.88346 9.68344 5.89819C9.64838 5.91293 9.61661 5.9345 9.59 5.96167L7.58333 7.97417V2.04167C7.58333 1.96431 7.5526 1.89013 7.49791 1.83543C7.44321 1.78073 7.36902 1.75 7.29167 1.75H6.70833C6.63098 1.75 6.55679 1.78073 6.50209 1.83543C6.4474 1.89013 6.41667 1.96431 6.41667 2.04167V7.97417L4.41 5.96167C4.38289 5.93433 4.35063 5.91263 4.31508 5.89782C4.27954 5.88302 4.24142 5.87539 4.20292 5.87539C4.16441 5.87539 4.12629 5.88302 4.09075 5.89782C4.05521 5.91263 4.02295 5.93433 3.99583 5.96167L3.5875 6.37583C3.56016 6.40295 3.53846 6.43521 3.52366 6.47075C3.50885 6.50629 3.50123 6.54441 3.50123 6.58292C3.50123 6.62142 3.50885 6.65954 3.52366 6.69508C3.53846 6.73063 3.56016 6.76289 3.5875 6.79L6.58 9.78833ZM12.25 11.0833V9.04167C12.25 8.96431 12.2193 8.89012 12.1646 8.83543C12.1099 8.78073 12.0357 8.75 11.9583 8.75H11.375C11.2976 8.75 11.2235 8.78073 11.1688 8.83543C11.1141 8.89012 11.0833 8.96431 11.0833 9.04167V11.0833H2.91667V9.04167C2.91667 8.96431 2.88594 8.89012 2.83124 8.83543C2.77654 8.78073 2.70235 8.75 2.625 8.75H2.04167C1.96431 8.75 1.89013 8.78073 1.83543 8.83543C1.78073 8.89012 1.75 8.96431 1.75 9.04167V11.0833C1.75 11.3928 1.87292 11.6895 2.09171 11.9083C2.3105 12.1271 2.60725 12.25 2.91667 12.25H11.0833C11.3928 12.25 11.6895 12.1271 11.9083 11.9083C12.1271 11.6895 12.25 11.3928 12.25 11.0833Z"
            fill="#616161"
          />
        </>
      );
    case 'export':
      return (
        <>
          <path
            d="M12.25 11.0833V9.04167C12.25 8.96431 12.2193 8.89013 12.1646 8.83543C12.1099 8.78073 12.0357 8.75 11.9583 8.75H11.375C11.2976 8.75 11.2235 8.78073 11.1688 8.83543C11.1141 8.89013 11.0833 8.96431 11.0833 9.04167V11.0833H2.91667V9.04167C2.91667 8.96431 2.88594 8.89013 2.83124 8.83543C2.77654 8.78073 2.70235 8.75 2.625 8.75H2.04167C1.96431 8.75 1.89013 8.78073 1.83543 8.83543C1.78073 8.89013 1.75 8.96431 1.75 9.04167V11.0833C1.75 11.3928 1.87292 11.6895 2.09171 11.9083C2.3105 12.1271 2.60725 12.25 2.91667 12.25H11.0833C11.3928 12.25 11.6895 12.1271 11.9083 11.9083C12.1271 11.6895 12.25 11.3928 12.25 11.0833Z"
            fill="#616161"
          />
          <path
            d="M7.11083 1.74984C7.22682 1.74994 7.33802 1.7961 7.41999 1.87817L10.4125 4.87651C10.4398 4.90362 10.4615 4.93588 10.4763 4.97142C10.4911 5.00696 10.4988 5.04509 10.4988 5.08359C10.4988 5.12209 10.4911 5.16022 10.4763 5.19576C10.4615 5.2313 10.4398 5.26356 10.4125 5.29067L10.0042 5.70484C9.97705 5.73218 9.94479 5.75388 9.90925 5.76868C9.8737 5.78349 9.83558 5.79111 9.79708 5.79111C9.75857 5.79111 9.72045 5.78349 9.68491 5.76868C9.64937 5.75388 9.61711 5.73218 9.58999 5.70484L7.58333 3.69234L7.58333 9.62484C7.58333 9.70219 7.5526 9.77638 7.4979 9.83108C7.4432 9.88577 7.36901 9.9165 7.29166 9.9165L6.70833 9.9165C6.63097 9.9165 6.55678 9.88577 6.50209 9.83108C6.44739 9.77638 6.41666 9.70219 6.41666 9.62484L6.41666 3.69234L4.40999 5.70484C4.38338 5.732 4.35161 5.75358 4.31655 5.76831C4.2815 5.78304 4.24385 5.79063 4.20582 5.79063C4.1678 5.79063 4.13015 5.78304 4.0951 5.76831C4.06004 5.75358 4.02827 5.732 4.00166 5.70484L3.58749 5.29067C3.56015 5.26356 3.53846 5.2313 3.52365 5.19576C3.50884 5.16022 3.50122 5.12209 3.50122 5.08359C3.50122 5.04509 3.50884 5.00696 3.52365 4.97142C3.53846 4.93588 3.56015 4.90362 3.58749 4.87651L6.57999 1.87817C6.66266 1.79716 6.77342 1.75119 6.88916 1.74984L7.11083 1.74984Z"
            fill="#616161"
          />
        </>
      );
    case 'incidents':
      return (
        <>
          <path
            d="M11.3795 4.58333H19.25C19.4931 4.58333 19.7263 4.67991 19.8982 4.85182C20.0701 5.02373 20.1667 5.25688 20.1667 5.5V18.3333C20.1667 18.5764 20.0701 18.8096 19.8982 18.9815C19.7263 19.1534 19.4931 19.25 19.25 19.25H2.75001C2.5069 19.25 2.27374 19.1534 2.10183 18.9815C1.92992 18.8096 1.83334 18.5764 1.83334 18.3333V3.66667C1.83334 3.42355 1.92992 3.19039 2.10183 3.01849C2.27374 2.84658 2.5069 2.75 2.75001 2.75H9.54618L11.3795 4.58333ZM3.66668 4.58333V17.4167H18.3333V6.41667H10.6205L8.78718 4.58333H3.66668Z"
            fill="#F3F2FF"
          />
          <path
            className="warning"
            d="M11.8333 13.5H10V15.3333H11.8333V13.5Z"
            fill="#F3F2FF"
          />
          <path
            className="warning"
            d="M11.8333 8H10V12.5833H11.8333V8Z"
            fill="#F3F2FF"
          />
        </>
      );
    case 'shape':
      return (
        <>
          <path
            d="M11.0912 11L11 19.8353"
            stroke="#F3F2FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.9062 15.0676V6.93238C18.9062 6.81063 18.8739 6.69107 18.8126 6.58591C18.7512 6.48076 18.663 6.39378 18.557 6.33387L11.3383 2.25371C11.2351 2.19537 11.1185 2.16472 11 2.16472C10.8815 2.16472 10.7649 2.19537 10.6617 2.25371L3.44296 6.33387C3.33697 6.39378 3.24879 6.48076 3.18744 6.58591C3.12608 6.69107 3.09375 6.81063 3.09375 6.93238V15.0676C3.09375 15.1894 3.12608 15.3089 3.18744 15.4141C3.24879 15.5192 3.33697 15.6062 3.44296 15.6661L10.6617 19.7463C10.7649 19.8046 10.8815 19.8353 11 19.8353C11.1185 19.8353 11.2351 19.8046 11.3383 19.7463L18.557 15.6661C18.663 15.6062 18.7512 15.5192 18.8126 15.4141C18.8739 15.3089 18.9063 15.1894 18.9062 15.0676V15.0676Z"
            stroke="#F3F2FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.8121 6.58514L11.0913 11L3.18854 6.58419"
            stroke="#F3F2FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'green-tick':
      return (
        <>
          <path
            d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z"
            stroke="#2A952A"
            fill="#fff"
          />
          <path
            d="M9.53598 5.43704L9.0306 4.94251C8.96127 4.87462 8.87698 4.8407 8.77791 4.8407C8.6788 4.8407 8.59451 4.87462 8.52518 4.94251L6.0874 7.33163L4.99486 6.25887C4.92549 6.19099 4.84124 6.15706 4.74218 6.15706C4.64306 6.15706 4.55882 6.19099 4.48944 6.25887L3.98406 6.75341C3.91469 6.82129 3.88002 6.90373 3.88002 7.00072C3.88002 7.09766 3.91469 7.18015 3.98406 7.24799L5.32929 8.56435L5.83472 9.05889C5.90405 9.12678 5.98829 9.1607 6.0874 9.1607C6.18647 9.1607 6.27072 9.12673 6.34009 9.05889L6.84552 8.56435L9.53598 5.93162C9.60531 5.86373 9.64002 5.7813 9.64002 5.68431C9.64007 5.58737 9.60531 5.50493 9.53598 5.43704Z"
            fill="#2A952A"
          />
        </>
      );
    case 'maintenance-alert':
      return (
        <>
          <circle cx="5.5" cy="5.5" r="5" stroke="white" fill="none" />
          <path
            d="M7.99969 4.26634V4.37646C7.99969 4.74152 7.85438 5.09163 7.59574 5.34977C7.33709 5.6079 6.98629 5.75292 6.6205 5.75292C6.43552 5.75363 6.25239 5.71614 6.08262 5.64281L3.84131 7.9002C3.77708 7.96378 3.6904 7.99962 3.59995 8H3.52754C3.43708 7.99962 3.3504 7.96378 3.28618 7.9002L3.09999 7.71438C3.03629 7.65029 3.00038 7.56378 3 7.4735V7.41156C3.00038 7.32129 3.03629 7.23478 3.09999 7.17068L5.34131 4.91328C5.27138 4.7431 5.23734 4.56036 5.24131 4.37646C5.24131 4.0114 5.38663 3.66129 5.64527 3.40316C5.90392 3.14502 6.25472 3 6.6205 3H6.73084C6.77743 3.00011 6.82208 3.01868 6.85496 3.05162L6.91358 3.11012C6.92974 3.12611 6.94256 3.14514 6.95132 3.16611C6.96007 3.18708 6.96458 3.20956 6.96458 3.23228C6.96458 3.25499 6.96007 3.27748 6.95132 3.29845C6.94256 3.31941 6.92974 3.33844 6.91358 3.35444L6.3757 3.89126C6.34411 3.92342 6.32642 3.96666 6.32642 4.0117C6.32642 4.05673 6.34411 4.09998 6.3757 4.13214L6.86531 4.62079C6.89795 4.65152 6.94112 4.66864 6.98599 4.66864C7.03086 4.66864 7.07403 4.65152 7.10667 4.62079L7.6549 4.08396C7.67092 4.06784 7.68999 4.05504 7.711 4.0463C7.73201 4.03757 7.75454 4.03307 7.7773 4.03307C7.80006 4.03307 7.82259 4.03757 7.8436 4.0463C7.86461 4.05504 7.88367 4.06784 7.8997 4.08396L7.95831 4.14246C7.98756 4.17691 8.00238 4.22128 7.99969 4.26634Z"
            fill="white"
          />
        </>
      );
    case 'mini-settings':
      return (
        <>
          <g opacity="0.99" clipPath="url(#clip0)">
            <mask
              id="path-1-outside-1"
              maskUnits="userSpaceOnUse"
              x="-1"
              y="-1"
              width="12"
              height="12"
              fill="black"
            >
              <rect fill="white" x="-1" y="-1" width="12" height="12" />
              <path d="M9.27413 3.72292L8.78646 3.64021C8.74305 3.51936 8.69399 3.4008 8.63975 3.28552L8.92692 2.88376C9.17678 2.53273 9.13665 2.05864 8.83247 1.75743L8.24645 1.17134C8.08082 1.00571 7.86125 0.914459 7.62824 0.914459C7.44476 0.914459 7.26928 0.970993 7.12204 1.07697L6.71875 1.36421C6.59401 1.30524 6.46743 1.25313 6.34002 1.20811L6.25938 0.730286C6.18843 0.307083 5.82542 0 5.39619 0H4.56825C4.1391 0 3.77609 0.307083 3.70529 0.729446L3.62053 1.22597C3.50082 1.26968 3.3828 1.31935 3.26721 1.37482L2.87124 1.08963C2.72278 0.982742 2.54661 0.926285 2.36175 0.926285C2.12646 0.926285 1.90681 1.01768 1.74301 1.1837L1.15562 1.76918C0.852279 2.07253 0.81253 2.54623 1.06117 2.89551L1.353 3.30551C1.29921 3.42125 1.25084 3.54034 1.20811 3.66188L0.730209 3.74252C0.307083 3.81355 0 4.17656 0 4.60571V5.43365C0 5.86281 0.307083 6.22581 0.729446 6.29669L1.22597 6.38138C1.26968 6.50116 1.31943 6.61926 1.37482 6.73477L1.0907 7.12997C0.840835 7.481 0.880966 7.95517 1.18515 8.2563L1.77116 8.84239C1.9368 9.00803 2.15637 9.09927 2.38937 9.09927C2.57286 9.09927 2.74833 9.04274 2.89551 8.93684L3.30551 8.64502C3.41324 8.69507 3.52493 8.74092 3.6396 8.7822L3.72085 9.26979C3.79188 9.69292 4.15489 10 4.58404 10H5.41389C5.84304 10 6.20613 9.69292 6.277 9.27032L6.35971 8.78258C6.48056 8.73917 6.59912 8.69011 6.7144 8.63586L7.11486 8.92212C7.26333 9.02901 7.43949 9.08554 7.62435 9.08554C7.85728 9.08554 8.07685 8.99429 8.24249 8.82866L8.8285 8.24257C9.13192 7.93922 9.17159 7.46552 8.92295 7.11624L8.63548 6.71257C8.69117 6.59462 8.74038 6.47614 8.78272 6.35834L9.26971 6.27716C9.69283 6.20613 9.99992 5.84312 9.99992 5.41397V4.58771C10.0047 4.1581 9.69985 3.79463 9.27413 3.72292ZM9.41398 5.41397C9.41398 5.5558 9.31251 5.67581 9.17304 5.69923L8.52714 5.80688C8.40797 5.82672 8.31184 5.9108 8.27606 6.02638C8.21868 6.21231 8.13987 6.40236 8.04199 6.59096C7.9866 6.69731 7.99545 6.82404 8.06503 6.92154L8.44566 7.45605C8.52775 7.57141 8.51455 7.72797 8.41422 7.82829L7.82821 8.41431C7.77244 8.47008 7.70194 8.4996 7.62435 8.4996C7.56317 8.4996 7.50541 8.48129 7.45643 8.44604L6.9242 8.06564C6.82632 7.99538 6.69906 7.98653 6.59217 8.04245C6.41319 8.13614 6.22299 8.21495 6.02684 8.27652C5.9124 8.31238 5.82885 8.4082 5.80894 8.52615L5.69923 9.17274C5.67581 9.31259 5.5558 9.41406 5.41389 9.41406H4.58404C4.44214 9.41406 4.3222 9.31259 4.29878 9.17313L4.19113 8.52722C4.17144 8.40881 4.08813 8.31284 3.97369 8.27667C3.78036 8.21564 3.59627 8.13995 3.42636 8.05168C3.38005 8.02765 3.32992 8.01582 3.28003 8.01582C3.21548 8.01582 3.15124 8.03566 3.09624 8.0748L2.55447 8.46039C2.50564 8.49556 2.4501 8.51334 2.38937 8.51334C2.31178 8.51334 2.24129 8.48389 2.18551 8.42804L1.59843 7.84103C1.49826 7.74185 1.48552 7.58575 1.56723 7.47093L1.94557 6.94466C2.01675 6.84586 2.02545 6.71768 1.96808 6.60988C1.87187 6.42944 1.79214 6.24023 1.73103 6.04744C1.69487 5.93376 1.59943 5.85068 1.48193 5.83061L0.827178 5.71899C0.687408 5.6955 0.585937 5.57549 0.585937 5.43365V4.60571C0.585937 4.46388 0.687408 4.34387 0.827407 4.32037L1.46538 4.21272C1.58462 4.19266 1.68068 4.10835 1.71615 3.99239C1.77635 3.79539 1.85379 3.60489 1.94633 3.42636C2.00142 3.32024 1.99257 3.19382 1.92322 3.09624L1.53854 2.55577C1.45637 2.44041 1.46957 2.28386 1.56959 2.18384L2.15759 1.59782L2.15973 1.59561C2.2126 1.54182 2.28432 1.51222 2.36175 1.51222C2.42294 1.51222 2.48077 1.53053 2.52884 1.56517L3.0574 1.94565C3.15635 2.01691 3.28468 2.02545 3.3921 1.96808C3.57254 1.87187 3.76175 1.79214 3.95431 1.73111C4.06799 1.69502 4.15107 1.59973 4.17129 1.48193L4.28299 0.827179C4.30641 0.687408 4.42642 0.585938 4.56833 0.585938H5.39627C5.5381 0.585938 5.65811 0.687408 5.68161 0.827408L5.78926 1.46561C5.8094 1.58493 5.89386 1.68091 6.0099 1.71631C6.20773 1.77643 6.40503 1.8576 6.59645 1.9577C6.7028 2.01324 6.82952 2.00455 6.92749 1.93489L7.46322 1.55334C7.51197 1.51817 7.56751 1.5004 7.62832 1.5004C7.70584 1.5004 7.77633 1.52985 7.83218 1.58569L8.41918 2.1727C8.51936 2.27188 8.53218 2.42798 8.44993 2.54341L8.06915 3.07617C7.99926 3.17413 7.99056 3.30124 8.04634 3.40775C8.1401 3.58673 8.21884 3.77693 8.28048 3.97316C8.31642 4.0876 8.41216 4.17107 8.53012 4.19098L9.17617 4.30061L9.17671 4.30069C9.31609 4.32411 9.41596 4.44237 9.41398 4.58191V5.41397Z" />
            </mask>
            <path
              d="M9.27413 3.72292L8.78646 3.64021C8.74305 3.51936 8.69399 3.4008 8.63975 3.28552L8.92692 2.88376C9.17678 2.53273 9.13665 2.05864 8.83247 1.75743L8.24645 1.17134C8.08082 1.00571 7.86125 0.914459 7.62824 0.914459C7.44476 0.914459 7.26928 0.970993 7.12204 1.07697L6.71875 1.36421C6.59401 1.30524 6.46743 1.25313 6.34002 1.20811L6.25938 0.730286C6.18843 0.307083 5.82542 0 5.39619 0H4.56825C4.1391 0 3.77609 0.307083 3.70529 0.729446L3.62053 1.22597C3.50082 1.26968 3.3828 1.31935 3.26721 1.37482L2.87124 1.08963C2.72278 0.982742 2.54661 0.926285 2.36175 0.926285C2.12646 0.926285 1.90681 1.01768 1.74301 1.1837L1.15562 1.76918C0.852279 2.07253 0.81253 2.54623 1.06117 2.89551L1.353 3.30551C1.29921 3.42125 1.25084 3.54034 1.20811 3.66188L0.730209 3.74252C0.307083 3.81355 0 4.17656 0 4.60571V5.43365C0 5.86281 0.307083 6.22581 0.729446 6.29669L1.22597 6.38138C1.26968 6.50116 1.31943 6.61926 1.37482 6.73477L1.0907 7.12997C0.840835 7.481 0.880966 7.95517 1.18515 8.2563L1.77116 8.84239C1.9368 9.00803 2.15637 9.09927 2.38937 9.09927C2.57286 9.09927 2.74833 9.04274 2.89551 8.93684L3.30551 8.64502C3.41324 8.69507 3.52493 8.74092 3.6396 8.7822L3.72085 9.26979C3.79188 9.69292 4.15489 10 4.58404 10H5.41389C5.84304 10 6.20613 9.69292 6.277 9.27032L6.35971 8.78258C6.48056 8.73917 6.59912 8.69011 6.7144 8.63586L7.11486 8.92212C7.26333 9.02901 7.43949 9.08554 7.62435 9.08554C7.85728 9.08554 8.07685 8.99429 8.24249 8.82866L8.8285 8.24257C9.13192 7.93922 9.17159 7.46552 8.92295 7.11624L8.63548 6.71257C8.69117 6.59462 8.74038 6.47614 8.78272 6.35834L9.26971 6.27716C9.69283 6.20613 9.99992 5.84312 9.99992 5.41397V4.58771C10.0047 4.1581 9.69985 3.79463 9.27413 3.72292ZM9.41398 5.41397C9.41398 5.5558 9.31251 5.67581 9.17304 5.69923L8.52714 5.80688C8.40797 5.82672 8.31184 5.9108 8.27606 6.02638C8.21868 6.21231 8.13987 6.40236 8.04199 6.59096C7.9866 6.69731 7.99545 6.82404 8.06503 6.92154L8.44566 7.45605C8.52775 7.57141 8.51455 7.72797 8.41422 7.82829L7.82821 8.41431C7.77244 8.47008 7.70194 8.4996 7.62435 8.4996C7.56317 8.4996 7.50541 8.48129 7.45643 8.44604L6.9242 8.06564C6.82632 7.99538 6.69906 7.98653 6.59217 8.04245C6.41319 8.13614 6.22299 8.21495 6.02684 8.27652C5.9124 8.31238 5.82885 8.4082 5.80894 8.52615L5.69923 9.17274C5.67581 9.31259 5.5558 9.41406 5.41389 9.41406H4.58404C4.44214 9.41406 4.3222 9.31259 4.29878 9.17313L4.19113 8.52722C4.17144 8.40881 4.08813 8.31284 3.97369 8.27667C3.78036 8.21564 3.59627 8.13995 3.42636 8.05168C3.38005 8.02765 3.32992 8.01582 3.28003 8.01582C3.21548 8.01582 3.15124 8.03566 3.09624 8.0748L2.55447 8.46039C2.50564 8.49556 2.4501 8.51334 2.38937 8.51334C2.31178 8.51334 2.24129 8.48389 2.18551 8.42804L1.59843 7.84103C1.49826 7.74185 1.48552 7.58575 1.56723 7.47093L1.94557 6.94466C2.01675 6.84586 2.02545 6.71768 1.96808 6.60988C1.87187 6.42944 1.79214 6.24023 1.73103 6.04744C1.69487 5.93376 1.59943 5.85068 1.48193 5.83061L0.827178 5.71899C0.687408 5.6955 0.585937 5.57549 0.585937 5.43365V4.60571C0.585937 4.46388 0.687408 4.34387 0.827407 4.32037L1.46538 4.21272C1.58462 4.19266 1.68068 4.10835 1.71615 3.99239C1.77635 3.79539 1.85379 3.60489 1.94633 3.42636C2.00142 3.32024 1.99257 3.19382 1.92322 3.09624L1.53854 2.55577C1.45637 2.44041 1.46957 2.28386 1.56959 2.18384L2.15759 1.59782L2.15973 1.59561C2.2126 1.54182 2.28432 1.51222 2.36175 1.51222C2.42294 1.51222 2.48077 1.53053 2.52884 1.56517L3.0574 1.94565C3.15635 2.01691 3.28468 2.02545 3.3921 1.96808C3.57254 1.87187 3.76175 1.79214 3.95431 1.73111C4.06799 1.69502 4.15107 1.59973 4.17129 1.48193L4.28299 0.827179C4.30641 0.687408 4.42642 0.585938 4.56833 0.585938H5.39627C5.5381 0.585938 5.65811 0.687408 5.68161 0.827408L5.78926 1.46561C5.8094 1.58493 5.89386 1.68091 6.0099 1.71631C6.20773 1.77643 6.40503 1.8576 6.59645 1.9577C6.7028 2.01324 6.82952 2.00455 6.92749 1.93489L7.46322 1.55334C7.51197 1.51817 7.56751 1.5004 7.62832 1.5004C7.70584 1.5004 7.77633 1.52985 7.83218 1.58569L8.41918 2.1727C8.51936 2.27188 8.53218 2.42798 8.44993 2.54341L8.06915 3.07617C7.99926 3.17413 7.99056 3.30124 8.04634 3.40775C8.1401 3.58673 8.21884 3.77693 8.28048 3.97316C8.31642 4.0876 8.41216 4.17107 8.53012 4.19098L9.17617 4.30061L9.17671 4.30069C9.31609 4.32411 9.41596 4.44237 9.41398 4.58191V5.41397Z"
              fill="#1B1B96"
            />
            <path
              d="M9.27413 3.72292L8.78646 3.64021C8.74305 3.51936 8.69399 3.4008 8.63975 3.28552L8.92692 2.88376C9.17678 2.53273 9.13665 2.05864 8.83247 1.75743L8.24645 1.17134C8.08082 1.00571 7.86125 0.914459 7.62824 0.914459C7.44476 0.914459 7.26928 0.970993 7.12204 1.07697L6.71875 1.36421C6.59401 1.30524 6.46743 1.25313 6.34002 1.20811L6.25938 0.730286C6.18843 0.307083 5.82542 0 5.39619 0H4.56825C4.1391 0 3.77609 0.307083 3.70529 0.729446L3.62053 1.22597C3.50082 1.26968 3.3828 1.31935 3.26721 1.37482L2.87124 1.08963C2.72278 0.982742 2.54661 0.926285 2.36175 0.926285C2.12646 0.926285 1.90681 1.01768 1.74301 1.1837L1.15562 1.76918C0.852279 2.07253 0.81253 2.54623 1.06117 2.89551L1.353 3.30551C1.29921 3.42125 1.25084 3.54034 1.20811 3.66188L0.730209 3.74252C0.307083 3.81355 0 4.17656 0 4.60571V5.43365C0 5.86281 0.307083 6.22581 0.729446 6.29669L1.22597 6.38138C1.26968 6.50116 1.31943 6.61926 1.37482 6.73477L1.0907 7.12997C0.840835 7.481 0.880966 7.95517 1.18515 8.2563L1.77116 8.84239C1.9368 9.00803 2.15637 9.09927 2.38937 9.09927C2.57286 9.09927 2.74833 9.04274 2.89551 8.93684L3.30551 8.64502C3.41324 8.69507 3.52493 8.74092 3.6396 8.7822L3.72085 9.26979C3.79188 9.69292 4.15489 10 4.58404 10H5.41389C5.84304 10 6.20613 9.69292 6.277 9.27032L6.35971 8.78258C6.48056 8.73917 6.59912 8.69011 6.7144 8.63586L7.11486 8.92212C7.26333 9.02901 7.43949 9.08554 7.62435 9.08554C7.85728 9.08554 8.07685 8.99429 8.24249 8.82866L8.8285 8.24257C9.13192 7.93922 9.17159 7.46552 8.92295 7.11624L8.63548 6.71257C8.69117 6.59462 8.74038 6.47614 8.78272 6.35834L9.26971 6.27716C9.69283 6.20613 9.99992 5.84312 9.99992 5.41397V4.58771C10.0047 4.1581 9.69985 3.79463 9.27413 3.72292ZM9.41398 5.41397C9.41398 5.5558 9.31251 5.67581 9.17304 5.69923L8.52714 5.80688C8.40797 5.82672 8.31184 5.9108 8.27606 6.02638C8.21868 6.21231 8.13987 6.40236 8.04199 6.59096C7.9866 6.69731 7.99545 6.82404 8.06503 6.92154L8.44566 7.45605C8.52775 7.57141 8.51455 7.72797 8.41422 7.82829L7.82821 8.41431C7.77244 8.47008 7.70194 8.4996 7.62435 8.4996C7.56317 8.4996 7.50541 8.48129 7.45643 8.44604L6.9242 8.06564C6.82632 7.99538 6.69906 7.98653 6.59217 8.04245C6.41319 8.13614 6.22299 8.21495 6.02684 8.27652C5.9124 8.31238 5.82885 8.4082 5.80894 8.52615L5.69923 9.17274C5.67581 9.31259 5.5558 9.41406 5.41389 9.41406H4.58404C4.44214 9.41406 4.3222 9.31259 4.29878 9.17313L4.19113 8.52722C4.17144 8.40881 4.08813 8.31284 3.97369 8.27667C3.78036 8.21564 3.59627 8.13995 3.42636 8.05168C3.38005 8.02765 3.32992 8.01582 3.28003 8.01582C3.21548 8.01582 3.15124 8.03566 3.09624 8.0748L2.55447 8.46039C2.50564 8.49556 2.4501 8.51334 2.38937 8.51334C2.31178 8.51334 2.24129 8.48389 2.18551 8.42804L1.59843 7.84103C1.49826 7.74185 1.48552 7.58575 1.56723 7.47093L1.94557 6.94466C2.01675 6.84586 2.02545 6.71768 1.96808 6.60988C1.87187 6.42944 1.79214 6.24023 1.73103 6.04744C1.69487 5.93376 1.59943 5.85068 1.48193 5.83061L0.827178 5.71899C0.687408 5.6955 0.585937 5.57549 0.585937 5.43365V4.60571C0.585937 4.46388 0.687408 4.34387 0.827407 4.32037L1.46538 4.21272C1.58462 4.19266 1.68068 4.10835 1.71615 3.99239C1.77635 3.79539 1.85379 3.60489 1.94633 3.42636C2.00142 3.32024 1.99257 3.19382 1.92322 3.09624L1.53854 2.55577C1.45637 2.44041 1.46957 2.28386 1.56959 2.18384L2.15759 1.59782L2.15973 1.59561C2.2126 1.54182 2.28432 1.51222 2.36175 1.51222C2.42294 1.51222 2.48077 1.53053 2.52884 1.56517L3.0574 1.94565C3.15635 2.01691 3.28468 2.02545 3.3921 1.96808C3.57254 1.87187 3.76175 1.79214 3.95431 1.73111C4.06799 1.69502 4.15107 1.59973 4.17129 1.48193L4.28299 0.827179C4.30641 0.687408 4.42642 0.585938 4.56833 0.585938H5.39627C5.5381 0.585938 5.65811 0.687408 5.68161 0.827408L5.78926 1.46561C5.8094 1.58493 5.89386 1.68091 6.0099 1.71631C6.20773 1.77643 6.40503 1.8576 6.59645 1.9577C6.7028 2.01324 6.82952 2.00455 6.92749 1.93489L7.46322 1.55334C7.51197 1.51817 7.56751 1.5004 7.62832 1.5004C7.70584 1.5004 7.77633 1.52985 7.83218 1.58569L8.41918 2.1727C8.51936 2.27188 8.53218 2.42798 8.44993 2.54341L8.06915 3.07617C7.99926 3.17413 7.99056 3.30124 8.04634 3.40775C8.1401 3.58673 8.21884 3.77693 8.28048 3.97316C8.31642 4.0876 8.41216 4.17107 8.53012 4.19098L9.17617 4.30061L9.17671 4.30069C9.31609 4.32411 9.41596 4.44237 9.41398 4.58191V5.41397Z"
              stroke="#1B1B96"
              strokeWidth="0.2"
              mask="url(#path-1-outside-1)"
            />
            <path
              d="M4.99994 3.00566C3.90024 3.00566 3.00566 3.90024 3.00566 4.99994C3.00566 6.09964 3.90024 6.99421 4.99994 6.99421C6.09963 6.99421 6.99421 6.09964 6.99421 4.99994C6.99421 3.90024 6.09963 3.00566 4.99994 3.00566ZM4.99994 6.30828C4.27857 6.30828 3.6916 5.7213 3.6916 4.99994C3.6916 4.27858 4.27857 3.6916 4.99994 3.6916C5.7213 3.6916 6.30827 4.27858 6.30827 4.99994C6.30827 5.7213 5.7213 6.30828 4.99994 6.30828Z"
              fill="#1B1B96"
              stroke="#1B1B96"
              strokeWidth="0.1"
            />
          </g>
          <defs>
            <clipPath id="clip0">
              <rect width="10" height="10" fill="white" />
            </clipPath>
          </defs>
        </>
      );
    case 'copy':
      return (
        <>
          <path
            d="M5.94 9.06L8.06 6.94C8.34138 6.65897 8.49965 6.27769 8.5 5.88V1.5C8.5 1.23478 8.39464 0.98043 8.20711 0.792893C8.01957 0.605357 7.76522 0.5 7.5 0.5H2C1.73478 0.5 1.48043 0.605357 1.29289 0.792893C1.10536 0.98043 1 1.23478 1 1.5V8.5C1 8.76522 1.10536 9.01957 1.29289 9.20711C1.48043 9.39464 1.73478 9.5 2 9.5H4.88C5.27769 9.49965 5.65896 9.34138 5.94 9.06ZM4.5 6V8.5H2V1.5H7.5V5.5H5C4.86739 5.5 4.74021 5.55268 4.64645 5.64645C4.55268 5.74021 4.5 5.86739 4.5 6ZM9.5 2.5V9.5C9.5 9.76522 9.39464 10.0196 9.20711 10.2071C9.01957 10.3946 8.76522 10.5 8.5 10.5H3C3 10.7652 3.10536 11.0196 3.29289 11.2071C3.48043 11.3946 3.73478 11.5 4 11.5H8.5C9.03043 11.5 9.53914 11.2893 9.91421 10.9142C10.2893 10.5391 10.5 10.0304 10.5 9.5V3.5C10.5 3.23478 10.3946 2.98043 10.2071 2.79289C10.0196 2.60536 9.76522 2.5 9.5 2.5Z"
            fill="currentColor"
          />
        </>
      );
    case 'plus':
      return (
        <>
          <rect width="14" height="14" rx="7" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.5 7C3.5 6.51675 3.89175 6.125 4.375 6.125H9.625C10.1082 6.125 10.5 6.51675 10.5 7C10.5 7.48325 10.1082 7.875 9.625 7.875H4.375C3.89175 7.875 3.5 7.48325 3.5 7Z"
            fill="white"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7 3.5C7.48325 3.5 7.875 3.89175 7.875 4.375L7.875 9.625C7.875 10.1082 7.48325 10.5 7 10.5C6.51675 10.5 6.125 10.1082 6.125 9.625L6.125 4.375C6.125 3.89175 6.51675 3.5 7 3.5Z"
            fill="white"
          />
        </>
      );
    case 'minus':
      return (
        <>
          <rect width="14" height="14" rx="7" fill="#FF5B51" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.5 7C3.5 6.51675 3.89175 6.125 4.375 6.125H9.625C10.1082 6.125 10.5 6.51675 10.5 7C10.5 7.48325 10.1082 7.875 9.625 7.875H4.375C3.89175 7.875 3.5 7.48325 3.5 7Z"
            fill="white"
          />
        </>
      );
    case 'addcircle':
      return (
        <>
          <rect width="14" height="14" rx="7" fill="#2C5CC5" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.5 7C3.5 6.51675 3.89175 6.125 4.375 6.125H9.625C10.1082 6.125 10.5 6.51675 10.5 7C10.5 7.48325 10.1082 7.875 9.625 7.875H4.375C3.89175 7.875 3.5 7.48325 3.5 7Z"
            fill="white"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7 3.5C7.48325 3.5 7.875 3.89175 7.875 4.375L7.875 9.625C7.875 10.1082 7.48325 10.5 7 10.5C6.51675 10.5 6.125 10.1082 6.125 9.625L6.125 4.375C6.125 3.89175 6.51675 3.5 7 3.5Z"
            fill="white"
          />
        </>
      );
    case 'editsmall':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.65141 1.90286L8.07998 1.33143C7.609 0.87935 6.86524 0.87935 6.39426 1.33143L1.39426 6.36572C1.3341 6.42872 1.29429 6.50836 1.27998 6.59429L0.999976 8.54286C0.981325 8.66711 1.0239 8.79271 1.11426 8.88C1.18833 8.95848 1.29208 9.00206 1.39998 9H1.45712L3.40569 8.72C3.49107 8.70764 3.57074 8.6698 3.63426 8.61143L7.92569 4.31429L8.64569 3.59429C8.87117 3.37098 8.99851 3.06711 8.99958 2.74976C9.00065 2.43241 8.87537 2.12769 8.65141 1.90286ZM3.16569 7.94286L1.87426 8.13143L2.05712 6.84L5.97141 2.92L7.07998 4.02857L3.16569 7.94286ZM8.08569 3.02286L7.64569 3.46286L6.53712 2.35429L6.97712 1.91429C7.05155 1.83602 7.15483 1.79172 7.26283 1.79172C7.37084 1.79172 7.47412 1.83602 7.54855 1.91429L8.11998 2.48572C8.25448 2.64498 8.23935 2.88199 8.08569 3.02286Z"
          fill="#2C5CC5"
        />
      );
    case 'freshping-logo':
      return (
        <>
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M15.4545 0H27.0909C29.2727 0 31 1.72727 31 3.90909V15.5455C31 24.0909 24.0909 31 15.5455 31H15.4545C6.90909 31 0 24.0909 0 15.5455C0 7 6.90909 0.0909091 15.4545 0Z"
            fill="#1F9999"
          />
          <path
            d="M14.3388 17.4309L12.9686 15.5771C12.9015 15.5192 12.8231 15.4756 12.7382 15.449C12.6533 15.4223 12.5638 15.4132 12.4751 15.4221H8.71875C8.71875 13.6443 9.4332 11.9393 10.7049 10.6821C11.9767 9.425 13.7015 8.71875 15.5 8.71875C17.2985 8.71875 19.0233 9.425 20.2951 10.6821C21.5668 11.9393 22.2812 13.6443 22.2812 15.4221H18.9313L16.9689 12.8338C16.9206 12.7556 16.8521 12.6916 16.7705 12.6482C16.6888 12.6048 16.597 12.5837 16.5044 12.587H16.3999C16.2879 12.6039 16.1827 12.6509 16.0957 12.7227C16.0087 12.7945 15.9432 12.8885 15.9064 12.9945L14.3388 17.4309Z"
            fill="white"
          />
          <path
            d="M18.409 16.4854C18.4951 16.5285 18.5908 16.5496 18.6874 16.5469H22.1999C21.967 18.1393 21.1615 19.5954 19.9311 20.6485C18.7006 21.7016 17.1275 22.2812 15.4999 22.2812C13.8724 22.2812 12.2993 21.7016 11.0688 20.6485C9.83833 19.5954 9.03287 18.1393 8.79997 16.5469H12.2312L14.1123 19.0492C14.1647 19.1286 14.2374 19.1927 14.3231 19.2351C14.4088 19.2774 14.5044 19.2965 14.6 19.2903H14.6813C14.7969 19.2836 14.9073 19.2404 14.9961 19.1671C15.085 19.0937 15.1476 18.994 15.1748 18.8828L16.725 14.366L18.1939 16.3002C18.2489 16.3786 18.3228 16.4423 18.409 16.4854Z"
            fill="white"
          />
        </>
      );
    case 'freshservice-logo':
      return (
        <>
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.956 0H26.217C28.3284 0 30 1.67155 30 3.78299V15.044C30 23.3138 23.3138 30 15.044 30H14.956C6.68622 30 0 23.3138 0 15.044C0 6.77419 6.68622 0.0879765 14.956 0Z"
            fill="#148FCC"
          />
          <path
            d="M14.7799 22.4337C14.9559 22.6096 15.2198 22.5217 15.3958 22.3457L20.1465 15.7475L21.2022 14.2519C21.3782 13.9879 21.2902 13.636 21.1142 13.4601C21.0262 13.3721 20.9383 13.3721 20.7623 13.3721H13.6362L15.3958 8.0935C15.4837 7.91755 15.3957 7.65362 15.2198 7.56564C15.0438 7.47767 14.7799 7.56564 14.6919 7.74159L9.9412 14.3398L8.79751 15.7475C8.62156 16.0114 8.70953 16.3633 8.88549 16.5392C8.97346 16.6272 9.06144 16.6272 9.23739 16.6272H16.3635L14.604 21.9058C14.516 22.0818 14.604 22.3457 14.7799 22.4337Z"
            fill="white"
          />
        </>
      );
    case 'search':
      return (
        <path
          fill="currentColor"
          d="M11.015 11.95c-2.641 2.17-6.557 2.013-9.033-.464-2.635-2.635-2.644-6.898-.019-9.523s6.888-2.616 9.523.019c2.477 2.476 2.633 6.392.464 9.033l3.855 3.855c.26.26.26.679.002.937a.662.662 0 01-.937-.002l-3.855-3.855zM2.902 2.902C.793 5.01.8 8.435 2.917 10.552c2.116 2.116 5.541 2.123 7.65.015 2.108-2.109 2.101-5.534-.015-7.65C8.435.8 5.01.793 2.902 2.902z"
        />
      );
    case 'gift-box':
      return (
        <path
          fill="currentColor"
          d="M1.64.14a.3.3 0 010 .26h.06a.3.3 0 01.3.28.32.32 0 01-.16.32v.72a.32.32 0 01-.08.2.28.28 0 01-.2.08H.44a.28.28 0 01-.2-.08.32.32 0 01-.08-.2V1A.33.33 0 010 .75V.7A.3.3 0 01.28.4h.11a.27.27 0 010-.26A.27.27 0 01.75 0 1 1 0 011 .4a1.11 1.11 0 01.27-.4.28.28 0 01.37.11zM.72 1h-.4v.69a.12.12 0 00.11.12h.29V1zm.56 0v.81h.28a.12.12 0 00.12-.12V1zM1.12.56H.88v1.28h.24V.56zm.16 0v.31h.42a.14.14 0 00.14-.12V.7a.13.13 0 00-.12-.14h-.44zm-.56 0H.3a.14.14 0 00-.14.12.14.14 0 00.12.19h.44V.56zm.64-.39a.39.39 0 00-.09.11l-.06.09h.13a.22.22 0 00.12 0 .11.11 0 000-.15.1.1 0 00-.14 0zm-.69 0a.1.1 0 00-.14 0 .11.11 0 000 .15.2.2 0 00.11 0h.18L.76.28A.39.39 0 00.67.17z"
        />
      );
    case 'visible':
      return (
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <g
            transform="translate(-244.000000, -806.000000)"
            fill="currentColor"
          >
            <g id="icon/visible" transform="translate(244.000000, 807.000000)">
              <g id="visible" transform="translate(-1.000000, -1.000000)">
                <path d="M9,11.15 C5.6626805,11.1542178 2.60412872,9.28896462 1.08,6.32 C0.969646383,6.12093767 0.969646383,5.87906233 1.08,5.68 C2.60812543,2.71500115 5.66437629,0.851874569 9,0.851874569 C12.3356237,0.851874569 15.3918746,2.71500115 16.92,5.68 C17.0303536,5.87906233 17.0303536,6.12093767 16.92,6.32 C15.3958713,9.28896462 12.3373195,11.1542178 9,11.15 Z M2.5,6 C3.84253547,8.31984715 6.3196843,9.74833813 9,9.74833813 C11.6803157,9.74833813 14.1574645,8.31984715 15.5,6 C14.1574645,3.68015285 11.6803157,2.25166187 9,2.25166187 C6.3196843,2.25166187 3.84253547,3.68015285 2.5,6 Z M9,8.8 C7.4536027,8.8 6.2,7.5463973 6.2,6 C6.2,4.4536027 7.4536027,3.2 9,3.2 C10.5463973,3.2 11.8,4.4536027 11.8,6 C11.7945146,7.54411947 10.5441195,8.79451462 9,8.8 Z M9,4.6 C8.22680135,4.6 7.6,5.22680135 7.6,6 C7.6,6.77319865 8.22680135,7.4 9,7.4 C9.77319865,7.4 10.4,6.77319865 10.4,6 C10.4,5.22680135 9.77319865,4.6 9,4.6 Z" />
              </g>
            </g>
          </g>
        </g>
      );
    case 'crown':
      return (
        <>
          <path
            d="M11.375 0.916829H11.1533C10.9213 0.917034 10.6989 1.00935 10.535 1.1735L8.75 2.9585L6.25333 0.461829C6.17137 0.379759 6.06016 0.333598 5.94417 0.333496H5.7225C5.60651 0.333598 5.4953 0.379759 5.41333 0.461829L2.91667 2.9585L1.13167 1.1735C0.96773 1.00935 0.74532 0.917034 0.513333 0.916829H0.291667C0.214312 0.916829 0.140125 0.947558 0.0854272 1.00226C0.0307291 1.05695 0 1.13114 0 1.2085V7.04183C0 7.11918 0.0307291 7.19337 0.0854272 7.24807C0.140125 7.30277 0.214312 7.3335 0.291667 7.3335H11.375C11.4524 7.3335 11.5265 7.30277 11.5812 7.24807C11.6359 7.19337 11.6667 7.11918 11.6667 7.04183V1.2085C11.6667 1.13114 11.6359 1.05695 11.5812 1.00226C11.5265 0.947558 11.4524 0.916829 11.375 0.916829ZM5.83333 5.5835C5.60259 5.5835 5.37703 5.51507 5.18517 5.38688C4.99331 5.25868 4.84378 5.07647 4.75547 4.86329C4.66717 4.65011 4.64407 4.41553 4.68908 4.18922C4.7341 3.96291 4.84521 3.75503 5.00838 3.59187C5.17154 3.42871 5.37942 3.3176 5.60573 3.27258C5.83204 3.22756 6.06662 3.25067 6.2798 3.33897C6.49298 3.42727 6.67519 3.57681 6.80338 3.76866C6.93158 3.96052 7 4.18608 7 4.41683C7 4.72625 6.87708 5.02299 6.65829 5.24179C6.4395 5.46058 6.14275 5.5835 5.83333 5.5835ZM11.375 8.50016H0.291667C0.214312 8.50016 0.140125 8.53089 0.0854272 8.58559C0.0307291 8.64029 0 8.71447 0 8.79183V9.37516C0 9.45252 0.0307291 9.5267 0.0854272 9.5814C0.140125 9.6361 0.214312 9.66683 0.291667 9.66683H11.375C11.4524 9.66683 11.5265 9.6361 11.5812 9.5814C11.6359 9.5267 11.6667 9.45252 11.6667 9.37516V8.79183C11.6667 8.71447 11.6359 8.64029 11.5812 8.58559C11.5265 8.53089 11.4524 8.50016 11.375 8.50016Z"
            fill="white"
          />
        </>
      );
    case 'info':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5 0C2.23858 0 0 2.23858 0 5C0 7.76142 2.23858 10 5 10C7.76142 10 10 7.76142 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0ZM5 9.125C2.72183 9.125 0.875 7.27817 0.875 5C0.875 2.72183 2.72183 0.875 5 0.875C7.27817 0.875 9.125 2.72183 9.125 5C9.12156 7.27675 7.27675 9.12156 5 9.125ZM5 3.75C5.34518 3.75 5.625 3.47018 5.625 3.125C5.625 2.77982 5.34518 2.5 5 2.5C4.65482 2.5 4.375 2.77982 4.375 3.125C4.375 3.47018 4.65482 3.75 5 3.75ZM5.4375 6.68125H5.625V6.7125C5.7813 6.7125 5.92573 6.79589 6.00389 6.93125C6.08204 7.06661 6.08204 7.23339 6.00389 7.36875C5.92573 7.50411 5.7813 7.5875 5.625 7.5875H4.375C4.13338 7.5875 3.9375 7.39162 3.9375 7.15C3.9375 6.90838 4.13338 6.7125 4.375 6.7125H4.5625V5.24375H4.375C4.13338 5.24375 3.9375 5.04787 3.9375 4.80625C3.9375 4.56463 4.13338 4.36875 4.375 4.36875H5C5.24162 4.36875 5.4375 4.56463 5.4375 4.80625V6.68125Z"
        />
      );
    case 'info-white':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5 0C2.23858 0 0 2.23858 0 5C0 7.76142 2.23858 10 5 10C7.76142 10 10 7.76142 10 5C10 3.67392 9.47322 2.40215 8.53553 1.46447C7.59785 0.526784 6.32608 0 5 0ZM5 9.125C2.72183 9.125 0.875 7.27817 0.875 5C0.875 2.72183 2.72183 0.875 5 0.875C7.27817 0.875 9.125 2.72183 9.125 5C9.12156 7.27675 7.27675 9.12156 5 9.125ZM5 3.75C5.34518 3.75 5.625 3.47018 5.625 3.125C5.625 2.77982 5.34518 2.5 5 2.5C4.65482 2.5 4.375 2.77982 4.375 3.125C4.375 3.47018 4.65482 3.75 5 3.75ZM5.4375 6.68125H5.625V6.7125C5.7813 6.7125 5.92573 6.79589 6.00389 6.93125C6.08204 7.06661 6.08204 7.23339 6.00389 7.36875C5.92573 7.50411 5.7813 7.5875 5.625 7.5875H4.375C4.13338 7.5875 3.9375 7.39162 3.9375 7.15C3.9375 6.90838 4.13338 6.7125 4.375 6.7125H4.5625V5.24375H4.375C4.13338 5.24375 3.9375 5.04787 3.9375 4.80625C3.9375 4.56463 4.13338 4.36875 4.375 4.36875H5C5.24162 4.36875 5.4375 4.56463 5.4375 4.80625V6.68125Z"
          fill="#fff"
        />
      );
    case 'upgradeinfo':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 8C0 3.58172 3.58172 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8ZM1.4 8C1.4 11.6451 4.35492 14.6 8 14.6C11.6428 14.5945 14.5945 11.6428 14.6 8C14.6 4.35492 11.6451 1.4 8 1.4C4.35492 1.4 1.4 4.35492 1.4 8ZM8 6C8.55229 6 9 5.55228 9 5C9 4.44772 8.55229 4 8 4C7.44772 4 7 4.44772 7 5C7 5.55228 7.44772 6 8 6ZM9.68998 11.385C9.68999 11.3833 9.69 11.3817 9.69 11.38V11.3899C9.69 11.3882 9.68999 11.3866 9.68998 11.385ZM8.99 12.08C9.37494 12.08 9.68731 11.7693 9.68998 11.385C9.68734 11.0045 9.38101 10.6954 9 10.69H8.7V7.64C8.7 7.2534 8.3866 6.94 8 6.94H7C6.6134 6.94 6.3 7.2534 6.3 7.64C6.3 8.0266 6.6134 8.34 7 8.34H7.3V10.68H7C6.6134 10.68 6.3 10.9934 6.3 11.38C6.3 11.7666 6.6134 12.08 7 12.08H8.99Z"
            fill="#475867"
          />
        </>
      );
    case 'optiondot':
      return (
        <path
          d="M3.37781 2.03998C3.37781 2.80095 2.76091 3.41785 1.99994 3.41785C1.23896 3.41785 0.62207 2.80095 0.62207 2.03998C0.62207 1.279 1.23896 0.662109 1.99994 0.662109C2.76091 0.662109 3.37781 1.279 3.37781 2.03998ZM3.37781 8.01074C3.37781 8.77172 2.76091 9.38861 1.99994 9.38861C1.23896 9.38861 0.62207 8.77172 0.62207 8.01074C0.62207 7.24977 1.23896 6.63287 1.99994 6.63287C2.76091 6.63287 3.37781 7.24977 3.37781 8.01074ZM3.37781 13.9815C3.37781 14.7425 2.76091 15.3594 1.99994 15.3594C1.23896 15.3594 0.62207 14.7425 0.62207 13.9815C0.62207 13.2205 1.23896 12.6036 1.99994 12.6036C2.76091 12.6036 3.37781 13.2205 3.37781 13.9815Z"
          fill="#3f3939"
        />
      );
    case 'settingsV2':
      return (
        <>
          <path
            d="M1.275 37.9687V31.7343H6.08438C6.2625 31.1999 6.44063 30.4874 6.79688 29.953L3.4125 26.5687L7.86563 22.1155L11.25 25.4999C11.7844 25.1437 12.3188 24.9655 13.0313 24.7874V19.978H19.2656V24.7874C19.8 24.9655 20.3344 25.1437 20.8687 25.4999L24.2531 22.1155L28.7063 26.5687L25.3219 29.953C25.6781 30.4874 25.8563 31.0218 26.0344 31.7343H30.8438V37.9687H26.0344C25.8563 38.503 25.6781 39.2155 25.3219 39.7499L28.7063 43.1343L24.2531 47.5874L20.8687 44.203C20.3344 44.5593 19.8 44.7374 19.2656 44.9155V49.7249H13.0313V44.9155C12.4969 44.7374 11.7844 44.5593 11.25 44.203L7.86563 47.5874L3.4125 43.1343L6.79688 39.7499C6.44063 39.2155 6.2625 38.6812 6.08438 37.9687H1.275ZM16.2375 39.0374C18.0187 39.0374 19.4438 37.9687 20.1563 36.3655C20.8688 34.7624 20.5125 32.9812 19.2656 31.7343C18.0188 30.4874 16.2375 30.1312 14.6344 30.8437C13.0312 31.5562 11.9625 32.9812 12.1406 34.7624C12.1406 35.8312 12.4969 36.8999 13.3875 37.7905C14.2781 38.6812 14.9906 39.0374 16.2375 39.0374Z"
            fill="white"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M29.4187 13.5655V9.29053H32.625C32.8031 8.93428 32.9812 8.57803 33.1594 8.22178L30.8437 5.90615L34.05 2.6999L36.1875 5.01553C36.5437 4.8374 36.9 4.65928 37.4344 4.65928V1.2749H41.7094V4.48115C42.0656 4.65928 42.4219 4.8374 42.7781 5.01553L45.0937 2.6999L48.3 5.90615L45.9844 8.22178C46.1625 8.57803 46.3406 8.93428 46.3406 9.29053H49.725V13.5655H46.5187C46.3406 14.0999 46.1625 14.4562 45.9844 14.8124L48.3 17.128L45.0937 20.1562L42.7781 17.8405C42.4219 18.0187 42.0656 18.1968 41.7094 18.3749V21.5812H37.4344V18.3749C37.0781 18.1968 36.7219 18.0187 36.3656 17.8405L34.05 20.1562L30.8437 16.9499L33.1594 14.8124C32.9812 14.4562 32.8031 14.0999 32.625 13.7437H29.4187V13.5655ZM39.5719 14.4562C40.8187 14.4562 41.8875 13.7437 42.2437 12.6749C42.6 11.428 42.4219 10.1812 41.5312 9.46865C40.6406 8.75615 39.3937 8.3999 38.325 8.75615C37.2562 9.29053 36.5437 10.1812 36.5437 11.428C36.5437 13.0312 37.9687 14.4562 39.5719 14.4562Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'cannedresponses':
      return (
        <svg
          width="41"
          height="36"
          viewBox="0 0 41 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.8345 30.0034L14.6919 29.875H14.5H5C2.51472 29.875 0.5 27.8603 0.5 25.375V5C0.5 2.51472 2.51472 0.5 5 0.5H36C38.4853 0.5 40.5 2.51472 40.5 5V25.375C40.5 27.8603 38.4853 29.875 36 29.875H27H26.8081L26.6655 30.0034L20.75 35.3273L14.8345 30.0034Z"
            fill="white"
            stroke="#363D42"
          />
          <path
            d="M20.5 8L22.4084 13.8734L28.584 13.8734L23.5878 17.5033L25.4962 23.3766L20.5 19.7467L15.5038 23.3766L17.4122 17.5033L12.416 13.8734L18.5916 13.8734L20.5 8Z"
            fill="#ffffff"
            stroke="#363D42"
          />
        </svg>
      );
    case 'incidentSources':
      return (
        <svg
          width="49"
          height="40"
          viewBox="0 0 49 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M39 12.0848C39 10.381 37.2503 9 35.0923 9H4.90789C2.74965 9 1 10.3812 1 12.0848V35.9152C1 37.619 2.74965 39 4.90772 39H35.0921C37.2503 39 38.9998 37.6188 38.9998 35.9152L39 12.0848Z"
            stroke="#363D42"
            strokeLinecap="round"
          />
          <path d="M23 19H17" stroke="#363D42" strokeLinecap="round" />
          <path d="M31 31H17" stroke="#363D42" strokeLinecap="round" />
          <path
            d="M10.0864 20.5547C11.4457 20.5547 12.5475 19.4529 12.5475 18.0936C12.5475 16.7344 11.4457 15.6326 10.0864 15.6326C8.72723 15.6326 7.62537 16.7344 7.62537 18.0936C7.62537 19.4529 8.72723 20.5547 10.0864 20.5547Z"
            stroke="#363D42"
            strokeLinecap="round"
          />
          <path
            d="M10.0864 32.4683C11.4457 32.4683 12.5475 31.3664 12.5475 30.0072C12.5475 28.648 11.4457 27.5461 10.0864 27.5461C8.72723 27.5461 7.62537 28.648 7.62537 30.0072C7.62537 31.3664 8.72723 32.4683 10.0864 32.4683Z"
            stroke="#363D42"
            strokeLinecap="round"
          />
          <path
            d="M36 25C42.6274 25 48 19.6274 48 13C48 6.37258 42.6274 1 36 1C29.3726 1 24 6.37258 24 13C24 19.6274 29.3726 25 36 25Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M31.5 15L34.5 18L43 9.5"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'incidentStatus':
      return (
        <svg
          width="42"
          height="33"
          viewBox="0 0 42 33"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.8624 5.71973H4.99299C2.92388 5.71973 1.245 7.4005 1.245 9.42352V28.625C1.245 30.6484 2.92388 32.1731 4.99299 32.1731H32.9369C35.006 32.1731 36.75 30.6482 36.75 28.625V21.9464"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.31702 25.5V21"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M28 25.5L28 22"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13 25.5V18"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M23 25.5V18"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 25.5733V14"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M33.5 17C37.9183 17 41.5 13.4183 41.5 9C41.5 4.58172 37.9183 1 33.5 1C29.0817 1 25.5 4.58172 25.5 9C25.5 13.4183 29.0817 17 33.5 17Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M30.6998 9.15634L33.3686 6.54639L36.0376 9.15634"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M33.3687 11.5064V6.54639"
            stroke="#363D42"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'accountSettingsV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M11.0531 15.8873V6.6123H14.2V15.8873"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4.2625 0.484375H1.28125V6.77812H4.2625C4.2625 5.95 5.25625 5.2875 6.58125 5.2875C7.90625 5.2875 8.9 5.95 8.9 6.77812H15.6906V5.12187C15.6906 5.12187 21.8187 5.12187 23.3094 8.26875C23.3094 1.3125 15.6906 1.3125 15.6906 1.3125V0.484375H8.9C8.9 1.3125 7.90625 1.975 6.58125 1.975C5.25625 1.975 4.2625 1.3125 4.2625 0.484375Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.6906 19.0346C15.6906 17.3783 14.3656 15.8877 12.7094 15.8877C11.0531 15.8877 9.56252 17.3783 9.56252 19.0346V39.2408L8.0719 40.7314V42.2221H15.6906V19.0346Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M23.3093 42.3564C23.3093 45.5033 27.9468 48.6502 27.9468 48.6502V42.3564"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M30.1 40.0686C31.425 40.0686 32.4187 41.0623 32.4187 42.3873V48.6811C33.2469 48.6811 37.7187 46.3623 37.7187 40.0686C37.7187 33.7748 32.4187 32.2842 32.4187 32.2842V9.75918C32.4187 8.10293 31.0937 6.6123 29.4375 6.6123C27.7812 6.6123 26.4562 7.9373 26.4562 9.75918V32.2842C26.4562 34.6029 23.475 35.4311 23.475 40.0686H30.1Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M28.6094 36.9219H31.5906"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M23.3093 42.3876V40.0688"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M32.4188 30.6279V9.59356C32.4188 7.93731 31.0938 6.6123 29.4375 6.6123C27.7813 6.6123 26.2906 7.93731 26.2906 9.59356V30.6279"
            fill="none"
            fillOpacity="0.3"
          />
          <path
            d="M32.4188 30.6279V9.59356C32.4188 7.93731 31.0938 6.6123 29.4375 6.6123C27.7813 6.6123 26.2906 7.93731 26.2906 9.59356V30.6279"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.7094 19.8623V21.3529V19.8623Z"
            fill="#2AB7CA"
            fillOpacity="0.3"
          />
          <path
            d="M12.7094 19.8623V21.3529"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.7094 24.6655V26.1562V24.6655Z"
            fill="#2AB7CA"
            fillOpacity="0.3"
          />
          <path
            d="M12.7094 24.6655V26.1562"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.7094 29.4688V30.9594V29.4688Z"
            fill="#2AB7CA"
            fillOpacity="0.3"
          />
          <path
            d="M12.7094 29.4688V30.9594"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M26.2906 30.6279H32.2532H26.2906Z" fill="#363D42" />
          <path
            d="M26.2906 30.6279H32.2532"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M29.4375 12.2437V10.5874V12.2437Z" fill="#363D42" />
          <path
            d="M29.4375 12.2437V10.5874"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M29.4375 16.8812V15.3906V16.8812Z" fill="#363D42" />
          <path
            d="M29.4375 16.8812V15.3906"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M29.4375 21.6845V20.1938V21.6845Z" fill="#363D42" />
          <path
            d="M29.4375 21.6845V20.1938"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'usersV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M28.975 13.8141C28.975 8.23594 24.5125 3.77344 18.9344 3.77344C13.3563 3.77344 8.8938 8.23594 8.8938 13.8141C8.8938 14.6109 9.05317 15.2484 9.21255 15.8859C9.85005 15.8859 10.6469 15.8859 11.2844 16.0453C11.2844 16.0453 10.9657 11.4234 12.0813 11.4234C13.1969 11.4234 16.7032 11.5828 16.7032 8.39531C16.7032 9.82969 21.325 13.0172 25.9469 13.0172C25.9469 14.1328 26.2657 15.0891 26.7438 16.0453L28.6563 15.8859C28.8157 15.2484 28.8157 14.4516 28.975 13.8141Z"
            fill="white"
          />
          <path
            d="M28.975 11.6625C28.975 5.76562 24.1938 0.984375 18.2969 0.984375C12.4 0.984375 7.61877 5.76562 7.61877 11.6625C7.61877 12.4594 7.77815 13.2563 7.93752 13.8938C7.77815 14.5313 7.61877 15.1687 7.61877 15.6469C7.61877 17.4 8.41565 18.9938 9.21252 18.9938C9.3719 18.9938 9.53127 18.9937 9.69065 18.8344C10.9656 23.775 14.3125 27.2812 18.2969 27.2812C22.2813 27.2812 25.6281 23.775 26.9031 18.8344C27.0625 18.9937 27.2219 18.9938 27.3813 18.9938C28.3375 18.9938 28.975 17.5594 28.975 15.6469C28.975 15.0094 28.8156 14.3719 28.6563 13.8938C28.8156 13.2563 28.975 12.4594 28.975 11.6625Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M28.975 11.6625C28.975 5.76562 24.1938 0.984375 18.2969 0.984375C12.4 0.984375 7.61877 5.76562 7.61877 11.6625C7.61877 12.4594 7.77815 13.2563 7.93752 13.8938C8.7344 13.8938 9.3719 13.8938 10.1688 14.0531C10.1688 14.0531 9.85002 9.1125 10.9656 9.1125C12.0813 9.1125 15.9063 9.27188 15.9063 5.76563C15.9063 7.2 20.8469 10.7063 25.7875 10.7063C25.9469 11.8219 26.1063 12.9375 26.5844 14.0531L28.8156 13.8938C28.8156 13.2563 28.8157 12.4594 28.975 11.6625Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M1.24377 44.6533V41.9439C1.24377 32.7002 8.7344 27.9189 18.1375 27.9189C27.5406 27.9189 35.0313 32.7002 35.0313 41.9439V44.6533"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'incidentTemplatesV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.3125 46.25H41.4063C44.0625 46.25 46.0938 44.2188 46.0938 41.5625V5.625"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.15625 3.75H40.1563C40.9375 3.75 41.5625 4.375 41.5625 5.15625V40.1563C41.5625 40.9375 40.9375 41.5625 40.1563 41.5625H5.15625C4.375 41.5625 3.75 40.9375 3.75 40.1563V5.15625C3.75 4.375 4.375 3.75 5.15625 3.75Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M24.5312 12.5H9.375V16.4062H24.5312V12.5Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M24.5312 20.625H9.375V24.5312H24.5312V20.625Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M34.375 28.75H9.375V32.6562H34.375V28.75Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'integrationsV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M22 11.8437V22.625L12.4688 17.1562L12.625 6.21875L22 11.8437Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M22 11.8437L31.5312 6.21875L31.375 17.1562L22 22.625V11.8437Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.625 6.21875L22 0.75L31.5312 6.21875L22 11.8438L12.625 6.21875Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.2812 32.3125V43.25L0.75 37.7812L0.90625 26.8438L10.2812 32.3125Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.2812 32.3125L19.8125 26.8438V37.7812L10.2812 43.25V32.3125Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M0.90625 26.8437L10.2813 21.375L19.8125 26.8437L10.2813 32.3125L0.90625 26.8437Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M33.7187 32.3125V43.25L24.1875 37.7812L24.3438 26.8438L33.7187 32.3125Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M33.7188 32.3125L43.25 26.8438L43.0937 37.7812L33.7188 43.25V32.3125Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M24.3438 26.8437L33.7188 21.375L43.25 26.8437L33.7188 32.3125L24.3438 26.8437Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'internalNotificationV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M27 29.8125H41.5313C42.4688 29.8125 43.25 29.0313 43.25 28.0938V2.46875C43.25 1.53125 42.4688 0.75 41.5313 0.75H2.46875C1.53125 0.75 0.75 1.53125 0.75 2.46875V28.0938C0.75 29.0313 1.53125 29.8125 2.46875 29.8125H16.6875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42.1562 1.6875L23.0938 19.1875C22.4688 19.8125 21.5313 19.8125 20.9063 19.1875L1.84375 1.6875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M42.1562 28.875L27.3125 15.2812"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16.6875 15.2812L1.84375 28.875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M22 26.8438C22.8629 26.8438 23.5625 26.1442 23.5625 25.2812C23.5625 24.4183 22.8629 23.7188 22 23.7188C21.1371 23.7188 20.4375 24.4183 20.4375 25.2812C20.4375 26.1442 21.1371 26.8438 22 26.8438Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M24.0312 40.75C24.0312 40.9063 24.0312 41.0625 24.0312 41.2187C23.875 42.1562 22.9375 42.9375 22 42.9375C20.9063 42.9375 20.125 42.1562 19.9688 41.2187C19.9688 41.0625 19.9688 40.9063 19.9688 40.75"
            fill="none"
            fillOpacity="0.3"
          />
          <path
            d="M24.0312 40.75C24.0312 40.9063 24.0312 41.0625 24.0312 41.2187C23.875 42.1562 22.9375 42.9375 22 42.9375C20.9063 42.9375 20.125 42.1562 19.9688 41.2187C19.9688 41.0625 19.9688 40.9063 19.9688 40.75"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19.9687 40.75H29.9688C27.4688 38.4063 27.4688 37.1563 27.4688 32C27.4688 30.5938 27 29.1875 25.9062 28.0938C24.8125 27 23.4062 26.5312 22 26.5312C20.5937 26.5312 19.1875 27.1562 18.0937 28.0938C17 29.0313 16.375 30.4375 16.375 32C16.375 37 16.375 38.4063 13.875 40.75H19.9687Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'statusPagePrivacyV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M35.75 24.9688V22.9375C35.75 21.8438 34.9688 21.0625 33.875 21.0625H2.625C1.53125 21.0625 0.75 21.8438 0.75 22.9375V41.375C0.75 42.4687 1.53125 43.25 2.625 43.25H33.875"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.25 0.75C11.6875 0.75 6.375 6.21875 6.375 12.7813V21.0625H30.2812V12.7813C30.2812 9.65625 29.0313 6.53125 26.8438 4.34375C24.6563 2.15625 21.5313 0.75 18.25 0.75Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.8437 21.0625V12.9375C11.6874 10.5937 12.9374 8.25 14.9687 7.15625C16.9999 6.0625 19.4999 5.90625 21.5312 7.15625C23.5624 8.40625 24.8124 10.5937 24.6562 12.9375V21.0625H11.8437V21.0625Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19.6562 31.6875C20.4375 31.0625 20.9063 29.9688 20.5938 29.0313C20.2813 28.0938 19.3438 27.3125 18.25 27.3125C17.1563 27.3125 16.2188 27.9375 15.9063 29.0313C15.5938 29.9688 15.9062 31.0625 16.8437 31.6875L15.75 36.8438H20.5938L19.6562 31.6875Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M34.0313 43.25C39.1226 43.25 43.25 39.1226 43.25 34.0313C43.25 28.9399 39.1226 24.8125 34.0313 24.8125C28.9399 24.8125 24.8125 28.9399 24.8125 34.0313C24.8125 39.1226 28.9399 43.25 34.0313 43.25Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M38.0937 31.2188L32.625 36.6875L29.8125 34.0313"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'manageSubscribersV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18.25 21.0625C23.8591 21.0625 28.4063 16.5154 28.4063 10.9062C28.4063 5.29711 23.8591 0.75 18.25 0.75C12.6409 0.75 8.09375 5.29711 8.09375 10.9062C8.09375 16.5154 12.6409 21.0625 18.25 21.0625Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.25 21.0625C21.2188 21.0625 24.1875 21.8437 26.6875 23.25"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.25 21.0625C13.5625 21.0625 9.1875 22.9375 5.90625 26.2188C2.625 29.5 0.75 34.0313 0.75 38.5625V43.25"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M43.25 35.9063V32H40.2813C40.125 31.6875 39.9687 31.2188 39.8125 30.9063L41.8438 28.7188L39.1875 26.0625L37 28.25C36.6875 28.0938 36.375 27.9375 35.9062 27.7813V24.8125H32V27.7813C31.6875 27.9375 31.2188 28.0938 30.9063 28.25L28.7187 26.2188L25.9062 29.0313L27.9375 31.0625C27.7812 31.375 27.625 31.6875 27.4687 32.1563H24.5V36.0625H27.4687C27.625 36.375 27.7812 36.8438 27.9375 37.1563L25.9062 39.3438L28.7187 42.1562L30.9063 40.125C31.2188 40.2813 31.5312 40.4375 32 40.5938V43.5625H35.9062V40.5938C36.2187 40.4375 36.6875 40.2813 37 40.125L39.0312 42.1562L41.8438 39.3438L39.8125 37.3125C39.9687 37 40.125 36.6875 40.2813 36.2188L43.25 35.9063ZM34.0313 36.6875C32.625 36.6875 31.375 35.4375 31.375 34.0313C31.375 32.625 32.625 31.375 34.0313 31.375C35.4375 31.375 36.6875 32.625 36.6875 34.0313C36.6875 35.4375 35.4375 36.6875 34.0313 36.6875Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'pricingAndPlansV2':
      return (
        <svg fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M20.3102 11.001C20.3102 11.001 25.9352 13.501 28.4352 15.8447C30.9352 18.1885 33.2789 26.3135 33.2789 41.001C33.2789 43.501 31.4039 45.0635 29.2164 45.0635H4.99768C2.81018 45.0635 0.935181 43.501 0.935181 41.001C0.935181 26.4697 3.43518 18.3447 5.77893 15.8447C8.12268 13.3447 13.9039 11.001 13.9039 11.001"
            fill="white"
          />
          <path
            d="M20.3102 11.001C20.3102 11.001 25.9352 13.501 28.4352 15.8447C30.9352 18.1885 33.2789 26.3135 33.2789 41.001C33.2789 43.501 31.4039 45.0635 29.2164 45.0635H4.99768C2.81018 45.0635 0.935181 43.501 0.935181 41.001C0.935181 26.4697 3.43518 18.3447 5.77893 15.8447C8.12268 13.3447 13.9039 11.001 13.9039 11.001"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.875 7.7832L10.5938 2.93945L13.875 1.2207L17.1562 2.93945L20.4375 1.2207L23.5625 2.93945L20.4375 7.7832"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 44.9707H34.9687V41.8457H26.8438V44.9707Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 44.9707H34.9687V41.8457H26.8438V44.9707Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 41.8457H34.9687V38.5645H26.8438V41.8457Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 41.8457H34.9687V38.5645H26.8438V41.8457Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 38.5645H34.9687V35.2832H26.8438V38.5645Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 38.5645H34.9687V35.2832H26.8438V38.5645Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 35.2832H34.9687V32.002H26.8438V35.2832Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.8438 35.2832H34.9687V32.002H26.8438V35.2832Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.7188 44.9707H26.8438V41.8457H18.7188V44.9707Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.7188 44.9707H26.8438V41.8457H18.7188V44.9707Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.7188 41.8457H26.8438V38.5645H18.7188V41.8457Z"
            fill="none"
            fillOpacity="0.3"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.7188 41.8457H26.8438V38.5645H18.7188V41.8457Z"
            fill="#ffffff"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14.6562 27.1582C14.6562 28.5645 15.75 29.6582 17.1562 29.6582C18.5625 29.6582 19.6563 28.5645 19.6563 27.1582C19.6563 25.752 18.25 24.9707 17.1562 24.6582C16.0625 24.3457 14.6562 23.5645 14.6562 22.1582C14.6562 20.752 15.75 19.9707 17.1562 19.9707C18.5625 19.9707 19.6563 21.0645 19.6563 22.4707"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.1562 19.9707V18.4082"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.1562 29.6582V31.2207"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M22 24.8145H23.5625"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.5938 24.8145H12.1562"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.875 11.0645H20.4375V7.7832H13.875V11.0645Z"
            stroke="#363D42"
            strokeWidth="0.875"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'operational':
      return (
        <>
          <rect width="12" height="12" rx="6" fill="#1F9C68" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.6422 3.86256L9.08067 3.31307C9.00364 3.23764 8.90998 3.19995 8.7999 3.19995C8.68977 3.19995 8.59612 3.23764 8.51909 3.31307L4.85255 6.76767L3.63862 5.57572C3.56153 5.50029 3.46793 5.4626 3.35785 5.4626C3.24773 5.4626 3.15412 5.50029 3.07704 5.57572L2.5155 6.1252C2.43842 6.20063 2.3999 6.29223 2.3999 6.39999C2.3999 6.50771 2.43842 6.59935 2.5155 6.67473L4.0102 8.13736L4.57179 8.68685C4.64882 8.76228 4.74243 8.79997 4.85255 8.79997C4.96263 8.79997 5.05623 8.76223 5.13332 8.68685L5.6949 8.13736L9.6422 4.41209C9.71923 4.33666 9.7578 4.24506 9.7578 4.1373C9.75785 4.02958 9.71923 3.93799 9.6422 3.86256Z"
            fill="white"
          />
        </>
      );

    case 'major':
      return (
        <>
          <rect width="12" height="12" rx="6" fill="#C02600" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.6534 5.93759L8.26424 4.32675L7.54833 3.61083L5.93748 5.22168L4.32665 3.61085L3.61074 4.32676L5.22157 5.93759L3.61081 7.54835L4.32673 8.26426L5.93749 6.6535L7.54826 8.26428L8.26417 7.54837L6.6534 5.93759Z"
            fill="white"
          />
        </>
      );

    case 'partial':
      return (
        <>
          <rect width="12" height="12" rx="6" fill="#E47D00" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5.8125 3C6.12316 3 6.375 3.21586 6.375 3.48214L6.375 7.01786C6.375 7.28414 6.12316 7.5 5.8125 7.5C5.50184 7.5 5.25 7.28414 5.25 7.01786L5.25 3.48214C5.25 3.21586 5.50184 3 5.8125 3Z"
            fill="white"
          />
          <path
            d="M6.375 8.8125C6.375 9.12316 6.12316 9.375 5.8125 9.375C5.50184 9.375 5.25 9.12316 5.25 8.8125C5.25 8.50184 5.50184 8.25 5.8125 8.25C6.12316 8.25 6.375 8.50184 6.375 8.8125Z"
            fill="white"
          />
        </>
      );

    case 'maintenance':
      return (
        <>
          <rect width="12" height="12" rx="6" fill="#2063E3" />
          <path
            d="M9.28142 4.21375V4.34375C9.28142 4.77473 9.11021 5.18805 8.80547 5.4928C8.50072 5.79755 8.08739 5.96875 7.65642 5.96875C7.43846 5.96958 7.22269 5.92532 7.02267 5.83875L4.38188 8.50375C4.30621 8.5788 4.20408 8.62111 4.0975 8.62156H4.01219C3.90561 8.62111 3.80348 8.5788 3.72781 8.50375L3.50844 8.28437C3.43339 8.20871 3.39107 8.10658 3.39062 8V7.92688C3.39107 7.8203 3.43339 7.71817 3.50844 7.6425L6.14923 4.9775C6.06683 4.77659 6.02672 4.56085 6.03141 4.34375C6.03141 3.91277 6.20262 3.49945 6.50737 3.1947C6.81211 2.88995 7.22544 2.71875 7.65642 2.71875H7.78642C7.84131 2.71888 7.89392 2.7408 7.93267 2.77969L8.00173 2.84875C8.02077 2.86763 8.03588 2.8901 8.04619 2.91485C8.0565 2.9396 8.06181 2.96615 8.06181 2.99297C8.06181 3.01978 8.0565 3.04633 8.04619 3.07109C8.03588 3.09584 8.02077 3.1183 8.00173 3.13719L7.36798 3.77094C7.33076 3.80891 7.30991 3.85996 7.30991 3.91313C7.30991 3.96629 7.33076 4.01734 7.36798 4.05531L7.94485 4.63219C7.98331 4.66847 8.03417 4.68868 8.08704 4.68868C8.13991 4.68868 8.19078 4.66847 8.22923 4.63219L8.87517 3.99844C8.89405 3.9794 8.91652 3.96429 8.94127 3.95398C8.96602 3.94366 8.99257 3.93835 9.01939 3.93835C9.0462 3.93835 9.07275 3.94366 9.0975 3.95398C9.12226 3.96429 9.14472 3.9794 9.1636 3.99844L9.23267 4.0675C9.26712 4.10816 9.28458 4.16054 9.28142 4.21375Z"
            fill="white"
          />
        </>
      );
    case 'degraded':
      return (
        <>
          <rect width="12" height="12" rx="6" fill="#ECB320" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3 6C3 5.58579 3.33579 5.25 3.75 5.25H8.25C8.66421 5.25 9 5.58579 9 6C9 6.41421 8.66421 6.75 8.25 6.75H3.75C3.33579 6.75 3 6.41421 3 6Z"
            fill="white"
          />
        </>
      );
    case 'arrow-left':
      return (
        <path
          d="M8.414 11.5H18a1 1 0 0 1 0 2H8.414l3.793 3.793a1 1 0 0 1-1.414 1.414l-5.5-5.5a1 1 0 0 1 0-1.414l5.5-5.5a1 1 0 0 1 1.414 1.414L8.414 11.5z"
          fill="currentColor"
          fillRule="evenodd"
        />
      );
    case 'logo':
      return (
        <g
          xmlns="http://www.w3.org/2000/svg"
          id="Freshdesk-logo"
          fill="none"
          fillRule="evenodd"
        >
          <path
            d="M29.9237537,56 C15.5894428,56 4,44.3764706 4,30 C4,15.6235294 15.5894428,4 29.9237537,4 L49.4428152,4 C53.1026393,4 56,6.90588235 56,10.5764706 L56,30 C56,44.3764706 44.4105572,56 29.9237537,56 Z M30.3812317,15.4705882 C23.6715543,15.4705882 18.4868035,20.8235294 18.4868035,27.4 L18.4868035,35.3529412 C18.4868035,37.4941176 20.3167155,39.3294118 22.4516129,39.3294118 L25.8064516,39.3294118 L25.8064516,30 L21.2316716,30 L21.2316716,27.4 C21.2316716,22.2 25.3489736,18.0705882 30.5337243,18.0705882 C35.7184751,18.0705882 39.8357771,22.2 39.8357771,27.4 L39.8357771,30 L35.2609971,30 L35.2609971,39.3294118 L38.3108504,39.3294118 L38.3108504,39.4823529 C38.3108504,41.4705882 36.6334311,43.1529412 34.6510264,43.1529412 L30.9912023,43.1529412 C30.686217,43.1529412 30.3812317,43.4588235 30.3812317,43.7647059 C30.3812317,44.0705882 30.686217,44.3764706 30.9912023,44.3764706 L34.6510264,44.3764706 C37.2434018,44.3764706 39.3782991,42.2352941 39.3782991,39.6352941 L39.3782991,39.3294118 C41.0557185,38.8705882 42.4281525,37.3411765 42.4281525,35.5058824 L42.4281525,27.5529412 C42.4281525,20.8235294 37.0909091,15.4705882 30.3812317,15.4705882 Z"
            id="Combined-Shape"
            fill="#FFF"
            fillRule="nonzero"
          />
        </g>
      );
    case 'refresh':
      return (
        <path d="M22.334 22.112A8.767 8.767 0 0 0 24.803 16a8.8 8.8 0 0 0-8.8-8.8H16A1.1 1.1 0 0 1 16 5h.001c6.075 0 11 4.925 11 11 0 2.933-1.148 5.599-3.02 7.571l1.919-.005a1.1 1.1 0 1 1 0 2.2h-4.666a1.1 1.1 0 0 1-1.1-1.1V20a1.1 1.1 0 1 1 2.2 0v2.112zM9.666 9.888A8.767 8.767 0 0 0 7.197 16a8.8 8.8 0 0 0 8.8 8.8H16a1.1 1.1 0 0 1 0 2.2h-.001c-6.075 0-11-4.925-11-11 0-2.933 1.148-5.599 3.02-7.571L6.1 8.434a1.1 1.1 0 0 1 0-2.2h4.666a1.1 1.1 0 0 1 1.1 1.1V12a1.1 1.1 0 0 1-2.2 0V9.888z" />
      );
    case 'planned':
      return (
        <g
          fill="none"
          fillRule="evenodd"
          stroke="#525252"
          transform="translate(1 1)"
        >
          <path d="M14.248.338h-12.4C.953.338.338.97.338 1.865v12.44c0 .896.615 1.557 1.51 1.557h12.4c.894 0 1.615-.661 1.615-1.557V1.866c0-.895-.72-1.529-1.615-1.529zM.338 5.063h15.524M4.5 5.03v10.832M8.1 5.03v10.832M11.7 5.03v10.832M.338 12.6h15.524M.338 9h15.524" />
          <circle cx="5.158" cy="2.406" r="1" />
          <circle cx="11.042" cy="2.406" r="1" />
          <circle cx="12.842" cy="12.306" r="5.062" fill="#FFF" />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12.66 9.308v3.187h2.347"
          />
        </g>
      );
    case 'public':
      return (
        <g fill="none" fillRule="evenodd" stroke="#525252">
          <path d="M7.237 16.13l-.36 2.222c0 .163.16.294.36.294h5.532c.199 0 .36-.131.36-.294l-.36-2.223M17.409 1.375H2.596c-.682 0-1.217.542-1.217 1.223v10.299c0 .681.535 1.228 1.217 1.228h14.813c.682 0 1.22-.547 1.22-1.228V2.598c0-.68-.538-1.223-1.22-1.223z" />
          <path d="M9.9 10.063a.406.406 0 1 0 0-.813.406.406 0 0 0 0 .813zM12.45 6.83a3.506 3.506 0 0 0-5.098 0M13.685 5.606a5.263 5.263 0 0 0-7.57 0M8.587 8.053a1.754 1.754 0 0 1 2.627 0M1.38 11.875h17.249" />
        </g>
      );
    case 'notes':
      return (
        <g fill="#d9822b" fillRule="evenodd">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15.4172 5.25714C15.4281 5.189 15.4281 5.11957 15.4172 5.05143V4.98286C15.3859 4.89296 15.3351 4.81104 15.2687 4.74286L10.6972 0.24C10.6317 0.172917 10.5542 0.118655 10.4687 0.08H10.3887C10.3217 0.0405878 10.2481 0.0134938 10.1715 0H3.65725C1.95305 0 0.571533 1.38152 0.571533 3.08571V15.2C0.571533 16.9042 1.95305 18.2857 3.65725 18.2857H12.343C14.0472 18.2857 15.4287 16.9042 15.4287 15.2V5.31429C15.4287 5.31429 15.4172 5.28 15.4172 5.25714ZM10.9258 2.73143L12.6972 4.57143H10.9258V2.73143ZM12.343 16.6857H3.65725C3.26321 16.6857 2.88531 16.5292 2.60669 16.2506C2.32806 15.9719 2.17153 15.594 2.17153 15.2V3.08571C2.17153 2.26518 2.83671 1.6 3.65725 1.6H9.37153V5.31428C9.37153 5.75611 9.7297 6.11428 10.1715 6.11428H13.8287V15.2571C13.798 16.0553 13.1417 16.6863 12.343 16.6857Z"
            fill="#597182"
          />

          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.2999 9.5C11.0455 9.5 11.6499 10.1044 11.6499 10.85V13.65C11.6499 14.008 11.5077 14.3514 11.2545 14.6046C11.0014 14.8578 10.658 15 10.2999 15H5.99994C5.6419 15 5.29852 14.8578 5.04534 14.6046C4.79217 14.3514 4.64994 14.008 4.64994 13.65V10.85C4.64988 10.4876 4.79554 10.1404 5.05414 9.88646C5.31275 9.63255 5.66258 9.49329 6.02494 9.5V9.125C6.02494 7.95139 6.97633 7 8.14994 7C9.32354 7 10.2749 7.95139 10.2749 9.125V9.5H10.2999ZM6.72494 9.125V9.5H9.57494V9.125C9.57494 8.33799 8.93694 7.7 8.14994 7.7C7.36293 7.7 6.72494 8.33799 6.72494 9.125ZM8.14994 11.265C8.50892 11.265 8.79994 11.556 8.79994 11.915C8.79671 12.1312 8.68366 12.3309 8.49994 12.445V12.85C8.49994 13.0433 8.34324 13.2 8.14994 13.2C7.95664 13.2 7.79994 13.0433 7.79994 12.85V12.445C7.61622 12.3309 7.50317 12.1312 7.49994 11.915C7.49994 11.556 7.79095 11.265 8.14994 11.265Z"
            fill="#597182"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.57148 4.8168H6.98291C7.42474 4.8168 7.78291 4.45862 7.78291 4.0168C7.78291 3.57497 7.42474 3.2168 6.98291 3.2168H4.57148C4.12966 3.2168 3.77148 3.57497 3.77148 4.0168C3.77148 4.45862 4.12966 4.8168 4.57148 4.8168Z"
            fill="#597182"
          />
        </g>
      );
    case 'edit':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.5643 2.12835L9.85002 1.41406C9.2613 0.848959 8.33159 0.848959 7.74287 1.41406L1.49287 7.70692C1.41768 7.78567 1.3679 7.88522 1.35002 7.99263L1.00002 10.4283C0.976703 10.5837 1.02992 10.7407 1.14287 10.8498C1.23546 10.9479 1.36515 11.0023 1.50002 10.9998H1.57144L4.00716 10.6498C4.11389 10.6343 4.21347 10.587 4.29287 10.5141L9.65716 5.14263L10.5572 4.24263C10.839 3.96349 10.9982 3.58365 10.9995 3.18697C11.0009 2.79029 10.8443 2.40938 10.5643 2.12835ZM3.70716 9.67835L2.09287 9.91406L2.32144 8.29977L7.2143 3.39977L8.60002 4.78549L3.70716 9.67835ZM9.85716 3.52835L9.30716 4.07835L7.92145 2.69263L8.47145 2.14263C8.56448 2.0448 8.69358 1.98942 8.82859 1.98942C8.9636 1.98942 9.09269 2.0448 9.18573 2.14263L9.90002 2.85692C10.0681 3.05599 10.0492 3.35226 9.85716 3.52835Z"
          fill="#131313"
        />
      );
    case 'check':
      return (
        <g xmlns="http://www.w3.org/2000/svg" fill="none" fillRule="evenodd">
          <mask id="b" fill="#fff">
            <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="#a" />
          </mask>
          <use
            fill="#1F9999"
            fillRule="nonzero"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xlinkHref="#a"
          />
          <g fill="#1F9999" mask="url(#b)">
            <path d="M0 0h24v24H0z" />
          </g>
        </g>
      );
    case 'settings':
      return (
        <path d="M23.493 24.692a4.186 4.186 0 0 1 .026-4.238 4.3 4.3 0 0 1 3.694-2.098h1.161v-4.361h-.851a4.56 4.56 0 0 1-3.959-2.295 4.584 4.584 0 0 1 .013-4.569l.427-.737-3.76-2.165-.483.835c-.787 1.36-2.242 2.197-3.817 2.196s-3.028-.839-3.815-2.199l-.486-.839-3.786 2.18.433.748a4.57 4.57 0 0 1 .015 4.555 4.54 4.54 0 0 1-3.946 2.29h-.734v4.361h1.028c1.521 0 2.929.799 3.707 2.103.78 1.308.79 2.934.027 4.252l-.518.895 3.76 2.165.483-.835c.787-1.36 2.242-2.197 3.817-2.196s3.028.839 3.815 2.199l.486.839 3.786-2.18-.524-.906zm2.37.053c.509.88.207 2.005-.675 2.512l-4.102 2.362a1.847 1.847 0 0 1-2.519-.673l-.577-.997a2.381 2.381 0 0 0-4.122-.003l-.574.992a1.846 1.846 0 0 1-2.519.673l-4.076-2.347a1.836 1.836 0 0 1-.675-2.512l.609-1.052a2.18 2.18 0 0 0-.014-2.208 2.288 2.288 0 0 0-1.965-1.115H3.443a1.842 1.842 0 0 1-1.844-1.839v-4.725c0-1.016.826-1.839 1.844-1.839h.916c.904 0 1.738-.484 2.184-1.268.45-.79.447-1.759-.009-2.546l-.524-.906a1.837 1.837 0 0 1 .675-2.513l4.102-2.362a1.846 1.846 0 0 1 2.519.673l.577.997a2.381 2.381 0 0 0 4.122.003l.574-.992a1.846 1.846 0 0 1 2.519-.673l4.076 2.347a1.837 1.837 0 0 1 .675 2.513l-.518.895a2.565 2.565 0 0 0-.008 2.558 2.53 2.53 0 0 0 2.198 1.274h1.034c1.018 0 1.844.823 1.844 1.839v4.725a1.842 1.842 0 0 1-1.844 1.839h-1.344a2.27 2.27 0 0 0-1.951 1.108 2.169 2.169 0 0 0-.013 2.196l.616 1.064zm-9.926-2.966c-3.206 0-5.807-2.587-5.807-5.779s2.6-5.779 5.807-5.779c3.206 0 5.807 2.587 5.807 5.779s-2.6 5.779-5.807 5.779zm0-2.021c2.088 0 3.78-1.683 3.78-3.758s-1.692-3.758-3.78-3.758-3.78 1.683-3.78 3.758 1.692 3.758 3.78 3.758z" />
      );
    case 'folder':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.92564 3.12915C9.84423 3.01648 9.71418 2.94913 9.57518 2.94766H8.47372V2.50332C8.47706 2.11277 8.32425 1.73706 8.04925 1.45971C7.77425 1.18236 7.39985 1.02635 7.00928 1.02637H4.21183L3.31689 0.131432C3.2349 0.0467531 3.12185 -0.000727112 3.00398 8.42032e-06H1.46444C0.655652 8.42032e-06 1.85177e-08 0.65566 1.85177e-08 1.46445V7.29717C-4.32842e-05 7.56718 0.0758604 7.83176 0.21904 8.06068C0.484535 8.49759 0.959452 8.76354 1.4707 8.76161H7.10316C7.85885 8.78447 8.5371 8.30074 8.7616 7.57879L10.0133 3.52968C10.0533 3.38994 10.0204 3.2394 9.92564 3.12915ZM1.46444 0.876169H2.82249L3.75497 1.7711C3.83195 1.83691 3.92911 1.87445 4.03034 1.87749H7.00928C7.33418 1.87749 7.59756 2.14088 7.59756 2.46577V2.93515H1.9651C1.77392 2.93651 1.60503 3.06002 1.5458 3.2418L0.87616 5.45724V1.46445C0.87616 1.13955 1.13954 0.876169 1.46444 0.876169ZM7.8917 7.31595C7.78512 7.65486 7.47095 7.88542 7.11567 7.88545H1.48321C1.34061 7.88663 1.20254 7.83541 1.0952 7.74151L2.34686 3.83634H8.98064L7.8917 7.31595Z"
          fill="#597182"
        />
      );
    case 'arrowdown':
      return (
        <path d="M8.626 9.744c-.875-.903-2.294-.903-3.17-.001s-.876 2.366-.001 3.268l8.959 9.244a2.195 2.195 0 0 0 3.171 0l8.959-9.244c.875-.903.874-2.366-.001-3.268s-2.295-.902-3.169.001L16 17.353 8.626 9.744z" />
      );
    case 'nextPageArrow':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.7508 0.372768C0.950537 0.37274 1.14188 0.44922 1.28169 0.584968L4.7856 3.90945C5.07147 4.19134 5.07147 4.63804 4.7856 4.91993L1.28169 8.24442C0.988489 8.52345 0.51311 8.52345 0.219905 8.24442C-0.0732998 7.96538 -0.0732998 7.51297 0.219905 7.23393L3.20353 4.41469L0.219906 1.58534C0.0134575 1.37943 -0.0455735 1.07685 0.0694487 0.814137C0.184471 0.551422 0.451797 0.378253 0.7508 0.372768Z"
          fill="#183247"
        />
      );
    case 'arrow':
      return (
        <g
          xmlns="http://www.w3.org/2000/svg"
          id="003_Add_new_check_1"
          transform="translate(-1380.000000, -321.000000)"
          stroke="#979797"
          strokeWidth="2"
        >
          <polyline
            id="dropdown"
            transform="translate(1385.574844, 322.569070) rotate(-135.000000) translate(-1385.574844, -322.569070) "
            points="1388.14969 320 1383 320 1383.01155 325.138141"
          />
        </g>
      );
    case 'status':
      return (
        <path d="M17 22c.926-.394 2.104-.5 3.682-.5h7.314l.006-16.496h-7.028a5.797 5.797 0 0 0-3.688 1.387L17 6.634V22zm-2-.024V6.66l-.36-.33a5.012 5.012 0 0 0-3.292-1.328H4V21.5h7.686c1.412 0 2.47.104 3.314.474zm13.832 3.166c.662 0 1.164.492 1.164 1.12 0 .63-.528 1.12-1.19 1.12h-8.098c-1.376 0-2.83.494-3.492 1.314a.867.867 0 0 1-.579.3h-.375a1.075 1.075 0 0 1-.897-.353c-.689-.821-2.145-1.313-3.495-1.313H3.164C2.53 27.33 2 26.838 2 26.236s.53-1.094 1.164-1.094h8.706c1.588 0 3.15.466 4.42 1.34a7.817 7.817 0 0 1 4.398-1.34h8.144zm.794-21.784a1.275 1.275 0 0 1 .37.988v18.228a1.035 1.035 0 0 1-.368.709.992.992 0 0 1-.828.218l-8.118.001c-1.35 0-2.99.08-3.652.93a1.167 1.167 0 0 1-.912.438h-.265c-.369 0-.698-.171-.912-.438-.638-.848-1.934-.93-3.256-.93H3.163a1.167 1.167 0 0 1-.797-.222.907.907 0 0 1-.367-.705V4.398c0-.356.132-.684.396-.96.373-.271.84-.434 1.345-.434h7.602a7.03 7.03 0 0 1 4.661 1.862 7.727 7.727 0 0 1 4.937-1.864h7.654a1.36 1.36 0 0 1 1.033.357z" />
      );
    case 'lock':
      return (
        <g
          id="Artboard"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <g id="lock" transform="translate(4 3)" stroke="currentColor">
            <path
              d="M2,7.52941176 L14,7.52941176 C15.1045695,7.52941176 16,8.42484227 16,9.52941176 L16,15.8823529 C16,16.9869224 15.1045695,17.8823529 14,17.8823529 L2,17.8823529 C0.8954305,17.8823529 1.3527075e-16,16.9869224 0,15.8823529 L0,9.52941176 C-1.3527075e-16,8.42484227 0.8954305,7.52941176 2,7.52941176 Z M6.82352941,14.8028654 L6.82352941,17.4705882 L9.17647059,17.4705882 L9.17647059,14.8028654 C9.87976632,14.3960308 10.3529412,13.6356263 10.3529412,12.7647059 C10.3529412,11.4652124 9.29949353,10.4117647 8,10.4117647 C6.70050647,10.4117647 5.64705882,11.4652124 5.64705882,12.7647059 C5.64705882,13.6356263 6.12023368,14.3960308 6.82352941,14.8028654 Z"
              id="Combined-Shape"
              strokeWidth="2"
              fill="currentColor"
            />
            <path
              d="M3.55555556,8.47058824 L3.55555556,4.70588235 C3.55555556,2.10689529 5.54540111,4.17966315e-16 8,0 C10.4545989,-8.3593263e-16 12.4444444,2.10689529 12.4444444,4.70588235 L12.4444444,8.47058824"
              id="Shape"
              strokeWidth="3.5"
            />
          </g>
        </g>
      );
    case 'help':
      return (
        <path d="M15.733 31.467C7.074 31.467 0 24.393 0 15.734S7.074.001 15.733.001s15.733 7.074 15.733 15.733-7.074 15.733-15.733 15.733zm0-2.452c7.305 0 13.281-5.977 13.281-13.281S23.037 2.453 15.733 2.453c-7.305 0-13.281 5.977-13.281 13.281s5.977 13.281 13.281 13.281zm-4.876-17.259c0-.66.212-1.328.635-2.005s1.042-1.238 1.854-1.682c.813-.444 1.76-.667 2.844-.667 1.007 0 1.896.186 2.667.557s1.366.877 1.786 1.516c.42.639.63 1.333.63 2.083 0 .59-.12 1.108-.359 1.552s-.524.828-.854 1.151c-.33.323-.922.866-1.776 1.63-.236.215-.425.404-.568.568s-.248.313-.318.448c-.069.135-.123.271-.161.406s-.096.373-.172.714c-.132.722-.545 1.083-1.24 1.083-.361 0-.665-.118-.911-.354s-.37-.587-.37-1.052c0-.583.09-1.089.271-1.516s.42-.802.719-1.125c.299-.323.701-.707 1.208-1.151.444-.389.766-.682.964-.88s.365-.418.5-.661c.135-.243.203-.507.203-.792 0-.556-.207-1.024-.62-1.406S16.843 9.6 16.19 9.6c-.764 0-1.326.193-1.688.578s-.667.953-.917 1.703c-.236.785-.684 1.177-1.344 1.177-.389 0-.717-.137-.984-.411s-.401-.571-.401-.891zm5.084 11.417c-.424 0-.793-.137-1.109-.411s-.474-.658-.474-1.151c0-.438.153-.806.458-1.104s.681-.448 1.125-.448c.437 0 .806.149 1.104.448s.448.667.448 1.104c0 .486-.156.868-.469 1.146s-.674.417-1.083.417z" />
      );
    case 'caution-old':
      return (
        <g
          id="caution"
          fill="none"
          fillRule="evenodd"
          width="24px"
          height="24px"
        >
          <mask id="bb" fill="#fff">
            <path
              id="aa"
              d="M19.262 16.543L13.731 6.99a2 2 0 0 0-3.462 0l-5.531 9.553a2 2 0 0 0 1.73 3.002h11.063a2 2 0 0 0 1.731-3.002zm-8.08.548v-1.636h1.636v1.636h-1.636zm0-3.273v-3.273h1.636v3.273h-1.636z"
            />
          </mask>
          <use fill="#f87878" />
          <g fill="#f87878" mask="url(#bb)">
            <path d="M0 0h24v24H0z" />
          </g>
        </g>
      );
    case 'manageuser':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <g
            id="049-copy"
            transform="translate(-73.000000, -151.000000)"
            fill="#FFFFFF"
            stroke="#2C3036"
          >
            <g id="Group-22" transform="translate(56.000000, 62.000000)">
              <g id="manage_user" transform="translate(18.000000, 90.000000)">
                <g id="Group">
                  <path
                    d="M11.9976,13.45672 L11.9976,12.65736 C11.9976,12.4332 11.86528,12.23208 11.66544,12.15264 C9.32032,11.21784 8.00736,10.05712 7.78856,9.33384 L7.78856,8.57504 C8.27504,8.06168 8.6588,7.34576 8.88584,6.5076 C9.42792,6.11504 9.56912,5.3004 9.11552,4.7248 L9.11552,3.02624 C9.11552,1.27272 8.16888,0.0724 6.00472,0.0724 C3.8964,0.0724 2.8928,1.27272 2.8928,3.02624 L2.8928,4.72544 C2.44008,5.30024 2.58128,6.11432 3.12248,6.50728 C3.34952,7.34576 3.73352,8.06168 4.22032,8.57504 L4.22032,9.33384 C4.00216,10.05624 2.68856,11.21728 0.34344,12.15264 C0.14352,12.23264 0.01128,12.4332 0.01128,12.65736 L0.01128,13.45672"
                    id="Shape"
                  />
                </g>
              </g>
            </g>
          </g>
        </g>
      );
    case 'privacy':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
        >
          <g
            id="049-copy"
            transform="translate(-73.000000, -364.000000)"
            stroke="#2C3036"
          >
            <g
              id="statuspage_privacy"
              transform="translate(74.000000, 365.000000)"
            >
              <polyline
                id="Path-4"
                strokeLinejoin="round"
                points="12.544166 3.8303087 12.544166 3.93730214e-12 0 1.49661665e-13 0 8.32910948 3.24296271 8.32910948"
              />
              <path
                d="M6.50662568,4 C7.40233302,4.2790653 8.06809574,4.41859795 8.50391385,4.41859795 C8.93973195,4.41859795 9.54698906,4.2790653 10.3256852,4 L12,5.87247624 C11.8966907,8.11332934 11.4276886,9.63587115 10.5929938,10.4401017 C9.75829906,11.2443322 9.03268625,11.7642983 8.41615542,12 C7.78236803,11.7863896 7.07650586,11.2864158 6.29856892,10.5000787 C5.52063198,9.7137415 5.08777567,8.17120736 5,5.87247624 L6.50662568,4 Z"
                id="Path-5"
                strokeLinejoin="round"
              />
              <path d="M8.6356871,8 L11.6222585,8" id="Line-2" />
            </g>
          </g>
        </g>
      );
    case 'manageteam':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
        >
          <g
            id="049-copy"
            transform="translate(-71.000000, -202.000000)"
            stroke="#2C3036"
          >
            <g id="Group-22" transform="translate(56.000000, 62.000000)">
              <g id="manage_team" transform="translate(16.000000, 140.000000)">
                <g id="Group" transform="translate(0.000000, 0.623333)">
                  <path
                    d="M7.107836,6.946812 L7.107836,6.386356 C7.470276,6.007188 7.756148,5.478352 7.925264,4.859212 C8.33204,4.567152 8.43268,3.96542 8.096352,3.542324 L8.096352,2.287656 C8.096352,0.992392 7.391124,0.10574 5.778844,0.10574 C4.20818,0.10574 3.460452,0.992392 3.460452,2.287656 L3.460452,3.5428 C3.124668,3.965488 3.22558,4.566744 3.63154,4.859008 C3.800724,5.478352 4.0868,6.007188 4.449376,6.386424 L4.449376,6.94688 C4.286856,7.480544 2.346884,8.33816 0.59976,9.029108 C0.45084,9.0882 0.352308,9.236372 0.352308,9.401952 L0.352308,10.803704 C0.352308,11.02382 0.524416,11.202728 0.736712,11.202728 L10.820432,11.202728 C11.03266,11.202728 11.204836,11.02382 11.204836,10.803704 L11.204836,9.401952 C11.204904,9.236304 11.106304,9.087724 10.957452,9.02904 C9.210328,8.338568 7.270832,7.481156 7.107836,6.946812 Z"
                    id="Shape"
                  />
                  <path
                    d="M13.9841476,11.20266 L15.685764,11.20266 C15.85216,11.20266 15.987072,11.065572 15.987072,10.896864 L15.987072,9.822532 C15.987072,9.695644 15.909824,9.581744 15.793136,9.536796 C14.423684,9.007552 12.903408,8.350468 12.775636,7.940904 L12.775636,7.511348 C13.05974,7.220716 13.2838,6.815436 13.4164,6.340864 C13.732668,6.118844 13.815764,5.657804 13.550496,5.33154 L13.550496,4.369952 C13.550496,3.37722 12.997724,2.697696 11.733944,2.697696 C10.502804,2.697696 9.916712,3.37722 9.916712,4.369952 L9.916712,5.331948 C9.652464,5.65726 9.734608,6.118096 10.050808,6.340728 C10.1164712,6.57580229 10.2046123,6.79386807 10.3113834,6.98940938"
                    id="Shape"
                  />
                </g>
              </g>
            </g>
          </g>
        </g>
      );
    case 'integrations':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <g
            id="049-copy"
            transform="translate(-73.000000, -309.000000)"
            stroke="#2C3036"
          >
            <g id="Group-22" transform="translate(56.000000, 62.000000)">
              <g id="Group-26" transform="translate(14.000000, 33.000000)">
                <g id="Group-23">
                  <g
                    id="integration"
                    transform="translate(3.000000, 214.000000)"
                  >
                    <g id="Group">
                      <path d="M3.18594,11.2272 L0.4065,14.00664" id="Shape" />
                      <path d="M10.08264,0.51912 L6.45396,4.1844" id="Shape" />
                      <path d="M13.86786,4.30476 L10.20162,7.932" id="Shape" />
                      <path
                        d="M3.41166,4.16124 C1.39806,6.17472 1.29696,9.33852 3.18594,11.2272 C5.07456,13.1163 8.23806,13.01472 10.25178,11.00112 L11.76102,9.492 L4.92102,2.65152 L3.41166,4.16124 L3.41166,4.16124 Z"
                        id="Shape"
                      />
                      <path
                        d="M4.33962,2.07012 L12.29082,10.02114"
                        id="Shape"
                      />
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      );
    case 'managesubscriber':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
        >
          <g
            id="049-copy"
            transform="translate(-72.000000, -257.000000)"
            stroke="#2C3036"
          >
            <g
              id="manage_subscribers"
              transform="translate(73.000000, 258.000000)"
            >
              <g
                id="Group"
                transform="translate(9.164956, 7.471165) rotate(-360.000000) translate(-9.164956, -7.471165) translate(4.164956, 1.471165)"
              >
                <path
                  d="M2.21185441,7.58988391 C1.56160526,5.80336213 0.261106953,2.23031857 0.261106953,2.23031857 C0.0722402866,1.71131857 0.339840287,1.13745191 0.85877362,0.948585242 C1.37777362,0.759718575 1.95164029,1.02731857 2.14050695,1.54625191 L3.6226,5.61826667"
                  id="Shape"
                />
                <path
                  d="M3.77906667,11.9228667 C3.44933333,11.7002 3.17806667,11.5926667 3.17806667,11.5926667 C1.14786667,10.4508 0.270133333,7.77153333 0.270133333,7.77153333 C-0.0857333333,6.72673333 0.756666667,6.23633333 1.216,6.57513333 L1.82413333,7.06793333 C1.97426667,7.1896 2.09,7.34846667 2.1598,7.52866667 C2.1598,7.52866667 2.22577793,7.69904455 2.25876689,7.78423349"
                  id="Shape"
                />
                <path
                  d="M9.64273333,9.7886 C9.5932,9.26826667 9.5762,8.62953333 9.6658,7.89793333 C9.85353333,6.3654 8.89798221,4.58884483 8.89798221,4.58884483 C7.66392018,2.98263732 5.65574674,3.69271124 3.64034146,4.62495122"
                  id="Shape"
                />
              </g>
              <polyline
                id="Path-4"
                strokeLinejoin="round"
                points="13.1994535 3.8303087 13.1994535 0 0 0 0 6.32910948 2.24296271 6.32910948"
              />
            </g>
          </g>
        </g>
      );
    case 'pricing':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <g
            id="049-copy"
            transform="translate(-72.000000, -415.000000)"
            stroke="#2C3036"
          >
            <g id="Group-22" transform="translate(56.000000, 62.000000)">
              <g id="Group-26" transform="translate(14.000000, 33.000000)">
                <g id="Group-23">
                  <g id="settings">
                    <g id="Group">
                      <g
                        id="pricing"
                        transform="translate(1.000000, 319.000000)"
                      >
                        <g id="Group">
                          <g
                            id="Group-3"
                            transform="translate(9.139241, 6.687777) rotate(-351.000000) translate(-9.139241, -6.687777) translate(1.639241, 1.187777)"
                          >
                            <polygon
                              id="Rectangle-path"
                              transform="translate(6.526564, 5.013759) rotate(-16.481370) translate(-6.526564, -5.013759) "
                              points="0.846625015 1.58890768 12.2065035 1.58890768 12.2065035 8.43861118 0.846625015 8.43861118"
                            />
                            <path
                              d="M1.52873684,8.14111579 C2.50528421,7.85216842 3.53115789,8.4096 3.82010526,9.38614737"
                              id="Shape"
                            />
                            <path
                              d="M11.0723368,7.19993684 C10.8036632,6.22338947 11.3798526,5.10922105 12.3975789,4.83562105"
                              id="Shape"
                            />
                            <path
                              d="M13.2386903,9.8500289 C12.9700166,8.87348153 13.546206,7.75931311 14.5639324,7.48571311"
                              id="Shape"
                              transform="translate(13.868410, 8.667871) rotate(17.000000) translate(-13.868410, -8.667871) "
                            />
                            <path
                              d="M11.3958316,1.87661053 C10.4877474,2.16555789 9.53381053,1.60812632 9.26513684,0.631578947"
                              id="Shape"
                            />
                            <path
                              d="M1.87661053,2.81778947 C2.16555789,3.79433684 1.60812632,4.82021053 0.631578947,5.10915789"
                              id="Shape"
                            />
                          </g>
                          <polyline
                            id="Shape"
                            points="15.3603368 8.70732632 15.8527789 8.70732632 15.8527789 12.0138947 4.49275789 12.0138947 4.49275789 10.8779368"
                          />
                          <path
                            d="M7.43288421,2.65010526 L7.55414737,3.04256842"
                            id="Shape"
                          />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      );
    case 'accountsettings':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <g id="049-copy" transform="translate(-72.000000, -97.000000)">
            <g id="Group-22" transform="translate(56.000000, 62.000000)">
              <g
                id="account_settings"
                transform="translate(17.000000, 36.000000)"
              >
                <g id="Group">
                  <path
                    d="M2,0 L6.1025641,0 C9.47291719,-6.19123818e-16 12.2051282,2.73221101 12.2051282,6.1025641 L12.2051282,6.1025641 C12.2051282,9.47291719 9.47291719,12.2051282 6.1025641,12.2051282 L6.1025641,12.2051282 C2.73221101,12.2051282 -4.75429207e-16,9.47291719 -8.8817842e-16,6.1025641 L0,2 C-1.3527075e-16,0.8954305 0.8954305,-9.07316899e-16 2,-1.11022302e-15 Z"
                    id="Rectangle"
                    stroke="#2C3036"
                  />
                  <path
                    d="M2.51282051,2.87179487 L7.17948718,2.87179487"
                    id="Line"
                    stroke="#2C3036"
                    strokeLinecap="round"
                  />
                  <polygon
                    id="Rectangle"
                    fill="#FFFFFF"
                    points="5.38461538 6.1025641 12.9230769 6.1025641 12.9230769 13.0917183 5.38461538 13.0917183"
                  />
                  <g
                    id="Group-2"
                    transform="translate(5.743590, 5.743590)"
                    stroke="#2C3036"
                  >
                    <path
                      d="M3.3664897,5.56362575 C2.33626623,5.56362575 1.50110509,4.72846461 1.50110509,3.69824113 C1.50110509,2.66801766 2.33626623,1.83285652 3.3664897,1.83285652 C4.39671318,1.83285652 5.23187432,2.66801766 5.23187432,3.69824113 C5.23187432,4.72846461 4.39671318,5.56362575 3.3664897,5.56362575 Z"
                      id="Oval"
                      strokeWidth="0.5"
                    />
                    <path
                      d="M6.10775248,5.09256989 C6.16525429,4.97468137 6.2142415,4.8548306 6.25590091,4.72980744 L5.4897828,4.29155849 L5.52098962,4.0879699 C5.5554267,3.86330756 5.55417605,3.63609347 5.51805584,3.4126013 L5.48519797,3.20929467 L6.24800566,2.76456714 C6.20541218,2.64098655 6.15497219,2.52023032 6.09701117,2.40307434 L5.2459834,2.6360908 L5.12397797,2.46915248 C4.99050984,2.28652993 4.82977612,2.12615231 4.64567797,1.99298326 L4.47893425,1.8723677 L4.7044199,1.01769342 C4.5869022,0.96006759 4.46775202,0.911124743 4.3437771,0.869525564 L3.90663858,1.63696495 L3.70239095,1.60536801 C3.47804435,1.57066178 3.25097693,1.57187611 3.02757334,1.60816243 L2.82372761,1.64127206 L2.3801231,0.877225236 C2.25812076,0.919652124 2.13754821,0.970188942 2.02074722,1.028089 L2.25324231,1.88075147 L2.08710482,2.00269209 C1.90442163,2.13677683 1.74403296,2.29827653 1.61095231,2.48325291 L1.49031119,2.65093888 L0.638121489,2.42523839 C0.581537074,2.54214793 0.531954809,2.66337709 0.490180348,2.78750725 L1.25580074,3.22544776 L1.22455766,3.42906942 C1.18997504,3.65445602 1.19131405,3.88236757 1.22774259,4.10654324 L1.26080513,4.31000508 L0.498326254,4.754559 C0.540815867,4.8772252 0.591418322,4.99835371 0.649335296,5.1155644 L1.5003082,4.88255299 L1.62234478,5.04924664 C1.75632547,5.23225512 1.91740789,5.39275438 2.10166691,5.52566759 L2.26880369,5.64622986 L2.04339941,6.50068229 C2.16087917,6.5581891 2.28004977,6.60705681 2.40412692,6.6485759 L2.84142113,5.88087734 L3.04545538,5.91220493 C3.2696553,5.94662877 3.49637727,5.94523753 3.71938463,5.90886435 L3.92335562,5.87559607 L4.36696365,6.63967315 C4.48991982,6.59699296 4.61008146,6.54647955 4.72666177,6.48846496 L4.49407135,5.63543405 L4.65997625,5.51346686 C4.84193599,5.37969675 5.00178318,5.21865102 5.13447306,5.03431245 L5.25513345,4.86668579 L6.10775247,5.0925699 Z"
                      id="Shape"
                      strokeWidth="0.6"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
      );
    case 'report_incident':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
        >
          <g
            id="components_report-copy-3"
            transform="translate(-73.000000, -102.000000)"
            stroke="#2C3036"
          >
            <g
              id="reports_incidents"
              transform="translate(81.000000, 109.615385) scale(-1, 1) rotate(-90.000000) translate(-81.000000, -109.615385) translate(73.000000, 101.000000)"
            >
              <g id="Group">
                <path
                  d="M12.3373066,3.13970226 C12.3305254,2.03608519 11.4196867,1.1414268 10.326879,1.1414268 L4.15689935,1.1414268 L2.80236377,1.13598107 C1.41201111,1.13598107 0.283869098,2.29093491 0.283869098,3.68106035 L0.283869098,7.29647022 L0.283869098,11.1949633 C0.283869098,12.585316 1.41201111,13.6330225 2.80236377,13.6330225 L9.82456805,13.6330225 C11.2149207,13.6330225 12.3868024,12.5852024 12.3868024,11.1949633 L12.3373066,3.13970226 Z"
                  id="Shape"
                  transform="translate(6.335336, 7.384502) scale(-1, 1) rotate(-360.000000) translate(-6.335336, -7.384502) "
                />
                <g
                  id="Group-2"
                  transform="translate(5.545936, 7.050114) rotate(-270.000000) translate(-5.545936, -7.050114) translate(4.315167, 3.357807)"
                >
                  <path
                    d="M1.70728211,3.26684786 L1.70728211,0.062046746"
                    id="Line"
                  />
                  <path
                    d="M1.71132601,5.83908928 L1.71132601,5.79081172"
                    id="Line-Copy"
                  />
                </g>
                <path
                  d="M3.743479,16.0945609 L11.8483408,16.0945609 C13.5051951,16.0945609 14.8483408,14.7514152 14.8483408,13.0945609 L14.8483408,4.91182959"
                  id="Path-4"
                />
              </g>
            </g>
          </g>
        </g>
      );
    case 'report_components':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <g
            id="components_report-copy-3"
            transform="translate(-73.000000, -203.000000)"
            stroke="#2C3036"
          >
            <g
              id="reports_components"
              transform="translate(73.000000, 204.000000)"
            >
              <polyline
                id="Path-20"
                transform="translate(8.467179, 10.073805) rotate(-180.000000) translate(-8.467179, -10.073805) "
                points="4.48892059 12.3410314 -3.01092484e-12 10.7599952 8.64914881 7.80657915 16.934358 10.7599952 12.2207598 12.236989"
              />
              <polyline
                id="Path-20"
                transform="translate(8.467179, 6.891222) rotate(-180.000000) translate(-8.467179, -6.891222) "
                points="4.8669887 9.28059517 1.15360166e-10 7.8011016 8.64914881 4.50184937 16.934358 7.8011016 11.856855 9.14646674"
              />
              <polygon
                id="Path-20"
                transform="translate(8.467179, 2.842959) rotate(-180.000000) translate(-8.467179, -2.842959) "
                points="-3.63797881e-12 2.95341604 8.64914881 0 16.934358 2.95341604 8.94714829 5.68591757"
              />
            </g>
          </g>
        </g>
      );
    case 'report_maintenance':
      return (
        <g
          id="Page-1"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <g
            id="components_report-copy-3"
            transform="translate(-71.000000, -152.000000)"
            stroke="#2C3036"
          >
            <g
              id="reports_mainenance"
              transform="translate(72.000000, 153.000000)"
            >
              <g id="Group-3" transform="translate(7.268144, 3.948184)">
                <circle
                  id="Oval"
                  cx="5.30664062"
                  cy="5.30664062"
                  r="5.30664062"
                />
                <polyline
                  id="Path-19"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points="3.75375504 5.11056381 5.2878969 6.11921495 7.74745856 3.27572274"
                />
              </g>
              <polyline
                id="Path-18"
                strokeLinecap="round"
                strokeLinejoin="round"
                points="5.39512538 9.75936231 -1.23545618e-12 9.75936231 8.72429152e-12 0.798165356 9.80935392 0.798165356 9.80935392 2.6003947"
              />
              <path
                d="M1.96150352,3.94818424 L7.26814415,3.94818424"
                id="Line-3"
                strokeLinecap="round"
              />
              <path
                d="M1.96150352,6.84271549 L4.5884753,6.84271549"
                id="Line-3"
                strokeLinecap="round"
              />
              <path
                d="M2.4439254,3.89688282e-13 L2.4439254,1.14885061"
                id="Line-2"
                strokeLinecap="round"
              />
              <path
                d="M7.26814415,3.89688282e-13 L7.26814415,1.14885061"
                id="Line-2"
                strokeLinecap="round"
              />
            </g>
          </g>
        </g>
      );
    case 'calendar':
      return (
        <>
          <mask
            id="mask0"
            mask-type="alpha"
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="14"
            height="15"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.5335 0.6C3.5335 0.268629 3.80212 0 4.13349 0C4.46485 0 4.73348 0.268629 4.73348 0.6V2.6C4.73348 2.93137 4.46485 3.2 4.13349 3.2C3.80212 3.2 3.5335 2.93137 3.5335 2.6V0.6ZM11.1335 1.18246V2.39389H11.9868C12.0984 2.39389 12.1887 2.48429 12.1887 2.59579V5.44913H1.54493V2.59579C1.54493 2.48429 1.63532 2.39389 1.74683 2.39389H2.7335V1.18246H1.74683C0.966268 1.18246 0.333496 1.81523 0.333496 2.59579V12.8358C0.333496 13.6164 0.966268 14.2491 1.74683 14.2491H11.9868C12.7674 14.2491 13.4002 13.6164 13.4002 12.8358V2.59579C13.4002 1.81523 12.7674 1.18246 11.9868 1.18246H11.1335ZM5.53351 2.39389H8.3335V1.18246H5.53351V2.39389ZM8.33405 6.64913H5.2674V9.18246H8.33405V6.64913ZM9.53406 9.18246V6.64913H12.1887V9.18246H9.53406ZM8.33405 13.0377V10.3825H5.2674V13.0377H8.33405ZM11.9868 13.0377H9.53406V10.3825H12.1887V12.8358C12.1887 12.9473 12.0983 13.0377 11.9868 13.0377ZM4.06738 6.64913H1.54492V9.18246H4.06738V6.64913ZM4.06738 10.3825V13.0377H1.74683C1.63532 13.0377 1.54492 12.9473 1.54492 12.8358V10.3825H4.06738ZM9.7335 0C9.40213 0 9.1335 0.268629 9.1335 0.6V2.6C9.1335 2.93137 9.40213 3.2 9.7335 3.2C10.0649 3.2 10.3335 2.93137 10.3335 2.6V0.6C10.3335 0.268629 10.0649 0 9.7335 0Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask0)">
            <rect
              x="-0.999756"
              y="-0.68457"
              width="16"
              height="16"
              fill="#12344D"
            />
          </g>
        </>
      );
    case 'tick':
      return (
        <path
          d="M14.1462 15.4005L19.5361 20.3984C19.9757 20.806 20.6625 20.7801 21.0701 20.3405C21.4777 19.901 21.4518 19.2142 21.0122 18.8066L15.6223 13.8086L20.6785 8.35584C21.0897 7.91241 21.0635 7.2196 20.6201 6.80842C20.1767 6.39723 19.4839 6.42337 19.0727 6.86681L14.0164 12.3196L8.61254 7.30866C8.17297 6.90105 7.48619 6.92697 7.07858 7.36654C6.67097 7.80612 6.69688 8.4929 7.13646 8.90051L12.5404 13.9114L7.38054 19.4759C6.96935 19.9193 6.9955 20.6121 7.43893 21.0233C7.88236 21.4345 8.57516 21.4084 8.98635 20.9649L14.1462 15.4005Z"
          fill="#E65555"
        />
      );
    case 'cancel':
      return (
        <path
          d="M1 8.5L7 14.5L20.5 1"
          stroke="#2F2FBE"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );

    case 'canned':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.35714 17.73C8.52644 17.9027 8.75814 18 9 18C9.25956 18.0145 9.51271 17.916 9.69429 17.73L11.5714 15.8143H14.9143C16.6206 15.8072 18 14.422 18 12.7157V3.08571C17.993 1.38445 16.6156 0.00704467 14.9143 0H3.08571C1.38445 0.00704467 0.00704467 1.38445 1.17306e-10 3.08571V12.7157C-1.46887e-05 14.422 1.37944 15.8072 3.08571 15.8143H6.49286L8.35714 17.73ZM1.8 3.08571C1.8 2.37563 2.37563 1.8 3.08571 1.8H14.9143C15.6244 1.8 16.2 2.37563 16.2 3.08571V12.7286C16.2 13.4387 15.6244 14.0143 14.9143 14.0143H11.2371C11.0016 14.0143 10.7754 14.1066 10.6071 14.2714L9 15.8271L7.49571 14.2586C7.32746 14.0937 7.10127 14.0014 6.86571 14.0014H3.08571C2.37563 14.0014 1.8 13.4258 1.8 12.7157V3.08571ZM11.0186 12.4457C11.1437 12.5171 11.286 12.5527 11.43 12.5486C11.623 12.5505 11.8118 12.4921 11.97 12.3814C12.2434 12.1804 12.3782 11.841 12.3171 11.5071L11.9314 9.25714L13.5643 7.71429C13.8094 7.4738 13.8988 7.11611 13.7957 6.78857C13.6883 6.46786 13.4102 6.23443 13.0757 6.18429L10.8129 5.85L9.81 3.80571C9.63678 3.5246 9.3302 3.3534 9 3.3534C8.6698 3.3534 8.36322 3.5246 8.19 3.80571L7.18714 5.85L4.92429 6.18429C4.58981 6.23443 4.31168 6.46786 4.20429 6.78857C4.10118 7.11611 4.1906 7.4738 4.43571 7.71429L6.06857 9.32143L5.68286 11.5714C5.62182 11.9053 5.75658 12.2447 6.03 12.4457C6.32033 12.6302 6.6911 12.6302 6.98143 12.4457L9 11.3914L11.0186 12.4457ZM9.42429 9.57857C9.29404 9.50842 9.14791 9.473 9 9.47571C8.83484 9.46356 8.66967 9.49927 8.52429 9.57857L7.71429 10.0029L7.92 9.15428C7.96627 8.86277 7.87075 8.56667 7.66286 8.35714L6.99429 7.71428L7.90714 7.52143C8.2027 7.47599 8.45727 7.28866 8.58857 7.02L9 6.19714L9.41143 7.08429C9.54273 7.35295 9.7973 7.54027 10.0929 7.58571L11.0057 7.71428L10.2857 8.29286C10.1143 8.52064 10.0574 8.8147 10.1314 9.09L10.2857 10.0029L9.42429 9.57857Z"
          fill="#597182"
        />
      );
    case 'reorder-bold':
      return (
        <g opacity="0.65">
          <path
            d="M9.16683 5.00004C9.16683 5.32968 9.06908 5.65191 8.88595 5.92599C8.70281 6.20007 8.44251 6.4137 8.13797 6.53984C7.83343 6.66599 7.49832 6.69899 7.17501 6.63468C6.85171 6.57038 6.55474 6.41164 6.32165 6.17855C6.08856 5.94547 5.92983 5.64849 5.86552 5.32519C5.80121 5.00189 5.83422 4.66678 5.96036 4.36224C6.08651 4.05769 6.30013 3.79739 6.57421 3.61426C6.8483 3.43112 7.17053 3.33337 7.50016 3.33337C7.94219 3.33337 8.36611 3.50897 8.67868 3.82153C8.99124 4.13409 9.16683 4.55801 9.16683 5.00004ZM7.50016 8.33337C7.17053 8.33337 6.8483 8.43112 6.57421 8.61426C6.30013 8.79739 6.08651 9.05769 5.96036 9.36224C5.83422 9.66678 5.80121 10.0019 5.86552 10.3252C5.92983 10.6485 6.08856 10.9455 6.32165 11.1786C6.55474 11.4116 6.85171 11.5704 7.17501 11.6347C7.49832 11.699 7.83343 11.666 8.13797 11.5398C8.44251 11.4137 8.70281 11.2001 8.88595 10.926C9.06908 10.6519 9.16683 10.3297 9.16683 10C9.16683 9.55801 8.99124 9.13409 8.67868 8.82153C8.36611 8.50897 7.94219 8.33337 7.50016 8.33337ZM7.50016 13.3334C7.17053 13.3334 6.8483 13.4311 6.57421 13.6143C6.30013 13.7974 6.08651 14.0577 5.96036 14.3622C5.83422 14.6668 5.80121 15.0019 5.86552 15.3252C5.92983 15.6485 6.08856 15.9455 6.32165 16.1786C6.55474 16.4116 6.85171 16.5704 7.17501 16.6347C7.49832 16.699 7.83343 16.666 8.13797 16.5398C8.44251 16.4137 8.70281 16.2001 8.88595 15.926C9.06908 15.6519 9.16683 15.3297 9.16683 15C9.16683 14.558 8.99124 14.1341 8.67868 13.8215C8.36611 13.509 7.94219 13.3334 7.50016 13.3334ZM12.5002 6.66671C12.8298 6.66671 13.152 6.56896 13.4261 6.38582C13.7002 6.20269 13.9138 5.94239 14.04 5.63785C14.1661 5.3333 14.1991 4.99819 14.1348 4.67489C14.0705 4.35159 13.9118 4.05462 13.6787 3.82153C13.4456 3.58844 13.1486 3.42971 12.8253 3.3654C12.502 3.30109 12.1669 3.3341 11.8624 3.46024C11.5578 3.58639 11.2975 3.80001 11.1144 4.07409C10.9312 4.34817 10.8335 4.67041 10.8335 5.00004C10.8335 5.44207 11.0091 5.86599 11.3217 6.17855C11.6342 6.49111 12.0581 6.66671 12.5002 6.66671ZM12.5002 8.33337C12.1705 8.33337 11.8483 8.43112 11.5742 8.61426C11.3001 8.79739 11.0865 9.05769 10.9604 9.36224C10.8342 9.66678 10.8012 10.0019 10.8655 10.3252C10.9298 10.6485 11.0886 10.9455 11.3217 11.1786C11.5547 11.4116 11.8517 11.5704 12.175 11.6347C12.4983 11.699 12.8334 11.666 13.138 11.5398C13.4425 11.4137 13.7028 11.2001 13.8859 10.926C14.0691 10.6519 14.1668 10.3297 14.1668 10C14.1668 9.55801 13.9912 9.13409 13.6787 8.82153C13.3661 8.50897 12.9422 8.33337 12.5002 8.33337ZM12.5002 13.3334C12.1705 13.3334 11.8483 13.4311 11.5742 13.6143C11.3001 13.7974 11.0865 14.0577 10.9604 14.3622C10.8342 14.6668 10.8012 15.0019 10.8655 15.3252C10.9298 15.6485 11.0886 15.9455 11.3217 16.1786C11.5547 16.4116 11.8517 16.5704 12.175 16.6347C12.4983 16.699 12.8334 16.666 13.138 16.5398C13.4425 16.4137 13.7028 16.2001 13.8859 15.926C14.0691 15.6519 14.1668 15.3297 14.1668 15C14.1668 14.558 13.9912 14.1341 13.6787 13.8215C13.3661 13.509 12.9422 13.3334 12.5002 13.3334Z"
            fill="black"
          />
        </g>
      );
    case 'reorder':
      return (
        <path
          fill="none"
          stroke="#3f3939"
          strokeWidth="2"
          d="M15,5 L17,5 L17,3 L15,3 L15,5 Z M7,5 L9,5 L9,3 L7,3 L7,5 Z M15,13 L17,13 L17,11 L15,11 L15,13 Z M7,13 L9,13 L9,11 L7,11 L7,13 Z M15,21 L17,21 L17,19 L15,19 L15,21 Z M7,21 L9,21 L9,19 L7,19 L7,21 Z"
        />
      );
    case 'public-incident':
      return (
        <path
          xmlns="http://www.w3.org/2000/svg"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.5715 9.37125C14.1313 7.85201 14.1313 6.18299 13.5715 4.66375C12.5851 1.87788 9.95549 0.0115681 7.0002 0C4.05074 0.00258538 1.41933 1.85367 0.420203 4.62875C-0.128349 6.14998 -0.128349 7.81502 0.420203 9.33625C1.4076 12.1251 4.0417 13.9921 7.0002 14C9.94646 13.9937 12.5734 12.1433 13.5715 9.37125ZM5.3202 9.73875H8.6802C8.31151 10.7691 7.74028 11.7152 7.0002 12.5213C6.26012 11.7152 5.6889 10.7691 5.3202 9.73875ZM4.9877 8.51375C4.89233 8.01486 4.84544 7.50792 4.8477 7C4.84544 6.49208 4.89233 5.98514 4.9877 5.48625H9.0127C9.10808 5.98514 9.15496 6.49208 9.1527 7C9.15496 7.50792 9.10808 8.01486 9.0127 8.51375H4.9877ZM1.2252 7C1.22752 6.48832 1.29815 5.97924 1.4352 5.48625H3.75395C3.67005 5.98654 3.62616 6.49273 3.6227 7C3.62616 7.50727 3.67005 8.01346 3.75395 8.51375H1.4352C1.29815 8.02076 1.22752 7.51168 1.2252 7ZM7.0002 1.47875C7.74028 2.28484 8.31151 3.23093 8.6802 4.26125H5.3202C5.6889 3.23093 6.26012 2.28484 7.0002 1.47875ZM10.2465 5.48625H12.5652C12.845 6.47603 12.845 7.52397 12.5652 8.51375H10.2465C10.3304 8.01346 10.3742 7.50727 10.3777 7C10.3742 6.49273 10.3304 5.98654 10.2465 5.48625ZM12.0752 4.26125H9.9752C9.6607 3.24726 9.17539 2.29441 8.5402 1.44375C10.0529 1.86264 11.3295 2.8801 12.0752 4.26125ZM5.4602 1.44375C4.82501 2.29441 4.33971 3.24726 4.0252 4.26125H1.91645C2.66579 2.87957 3.94518 1.86238 5.4602 1.44375ZM1.91645 9.73875H4.0252C4.33971 10.7527 4.82501 11.7056 5.4602 12.5563C3.94518 12.1376 2.66579 11.1204 1.91645 9.73875ZM8.5402 12.5563C9.17539 11.7056 9.6607 10.7527 9.9752 9.73875H12.0752C11.3295 11.1199 10.0529 12.1374 8.5402 12.5563Z"
          fill="#466174"
        />
      );
    case 'private-incident':
      return (
        <g style={{ transform: 'scale(0.7)', opacity: 0.8 }}>
          <path d="M19 10h1a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V11a1 1 0 0 1 1-1h1V9a7 7 0 1 1 14 0v1zM5 12v8h14v-8H5zm6 2h2v4h-2v-4zm6-4V9A5 5 0 0 0 7 9v1h10z" />
        </g>
      );

    case 'sendmail':
      return (
        <>
          <path
            d="M1.40002 5.2503C1.40002 5.09315 1.52742 4.96576 1.68456 4.96576H4.23559C4.39274 4.96576 4.52013 5.09315 4.52013 5.2503C4.52013 5.40744 4.39274 5.53483 4.23559 5.53483H1.68456C1.52742 5.53483 1.40002 5.40744 1.40002 5.2503Z"
            fill="#979797"
          />
          <path
            d="M1.83137 6.60356C1.83137 6.44641 1.95876 6.31902 2.11591 6.31902H3.70539C3.86254 6.31902 3.98993 6.44641 3.98993 6.60356C3.98993 6.7607 3.86254 6.88809 3.70539 6.88809H2.11591C1.95876 6.88809 1.83137 6.7607 1.83137 6.60356Z"
            fill="#979797"
          />
          <path
            d="M2.16574 7.91862C2.16574 7.76147 2.29313 7.63408 2.45028 7.63408H3.19596C3.35311 7.63408 3.4805 7.76147 3.4805 7.91862C3.4805 8.07577 3.35311 8.20316 3.19596 8.20316H2.45028C2.29313 8.20316 2.16574 8.07577 2.16574 7.91862Z"
            fill="#979797"
          />
          <path
            d="M11.2163 4.90039H5.74635C5.56501 4.90039 5.3911 4.97243 5.26287 5.10065C5.18471 5.17881 5.18471 5.29111 5.06261 5.58413L3.74396 9.68658C3.74396 9.86792 3.816 10.0418 3.94423 10.1701C4.07245 10.2983 4.24637 10.3703 4.4277 10.3703H9.89763C10.079 10.3703 10.2529 10.2983 10.3811 10.1701C10.4593 9.97962 10.4593 9.97962 10.5814 9.68658L11.9 5.58413C11.9 5.40279 11.828 5.22888 11.6998 5.10065C11.5715 4.97243 11.3976 4.90039 11.2163 4.90039ZM10.8256 7.26908L8.31282 8.2776C8.14743 8.3932 7.95052 8.4552 7.74873 8.4552C7.54695 8.4552 7.35003 8.3932 7.18465 8.2776L5.11145 7.19582L5.3068 6.47789L7.52652 7.79898C7.59177 7.84427 7.6693 7.86853 7.74873 7.86853C7.82816 7.86853 7.9057 7.84427 7.97095 7.79898L11.0454 6.47789L10.8256 7.26908Z"
            fill="#979797"
          />
        </>
      );
    case 'sendtweet':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.2564 6.84649C11.0482 7.12405 10.7966 7.36617 10.5112 7.56349C10.5112 7.63599 10.5112 7.7085 10.5112 7.78503C10.5123 8.43686 10.3838 9.0824 10.133 9.68406C9.88226 10.2857 9.51431 10.8315 9.0506 11.2896C8.58688 11.7477 8.03667 12.1089 7.43199 12.3523C6.82731 12.5957 6.18025 12.7164 5.52849 12.7073C4.76736 12.7099 4.01598 12.5361 3.3332 12.1998C3.31518 12.1919 3.29987 12.179 3.28913 12.1625C3.27839 12.146 3.27271 12.1268 3.27278 12.1071V12.0628C3.27278 12.0351 3.28381 12.0084 3.30345 11.9888C3.32309 11.9691 3.34973 11.9581 3.37751 11.9581C4.12568 11.9335 4.84716 11.6742 5.43987 11.2169C5.10804 11.2103 4.78486 11.1099 4.50762 10.9275C4.23037 10.745 4.01036 10.4879 3.87296 10.1858C3.86599 10.1692 3.86341 10.1511 3.86544 10.1333C3.86748 10.1154 3.87408 10.0984 3.88459 10.0838C3.89511 10.0693 3.9092 10.0577 3.9255 10.0501C3.94181 10.0426 3.95978 10.0393 3.97769 10.0407C4.1835 10.0614 4.39138 10.0423 4.58995 9.98435C4.2256 9.90876 3.89468 9.71939 3.64492 9.44356C3.39515 9.16773 3.23945 8.8197 3.20027 8.44966C3.19887 8.43175 3.20209 8.41378 3.20964 8.39748C3.21719 8.38118 3.2288 8.36709 3.24336 8.35657C3.25793 8.34605 3.27495 8.33946 3.2928 8.33742C3.31065 8.33538 3.32872 8.33797 3.34528 8.34493C3.5459 8.43341 3.76246 8.48002 3.98172 8.48189C3.66319 8.27292 3.42284 7.96433 3.29823 7.60433C3.17361 7.24433 3.17174 6.8532 3.29292 6.49203C3.30556 6.45655 3.3272 6.42496 3.35572 6.40036C3.38424 6.37575 3.41865 6.35898 3.4556 6.35167C3.49255 6.34436 3.53076 6.34677 3.5665 6.35866C3.60224 6.37056 3.63427 6.39153 3.65947 6.41952C4.54342 7.35986 5.75798 7.92021 7.04707 7.98241C7.01405 7.85071 6.99781 7.71537 6.99873 7.5796C7.00463 7.23152 7.11384 6.89303 7.3125 6.60715C7.51117 6.32126 7.79032 6.10087 8.1145 5.97396C8.43868 5.84705 8.79326 5.81935 9.13321 5.89438C9.47317 5.96941 9.78316 6.14378 10.0238 6.39535C10.3593 6.33147 10.6836 6.21883 10.9865 6.06102C10.9974 6.05424 11.0099 6.05064 11.0228 6.05064C11.0356 6.05064 11.0481 6.05424 11.059 6.06102C11.0658 6.0719 11.0694 6.08446 11.0694 6.09728C11.0694 6.11009 11.0658 6.12265 11.059 6.13353C10.9123 6.46931 10.6645 6.75094 10.3501 6.93914C10.6254 6.90723 10.8959 6.84231 11.1557 6.74579C11.1664 6.73852 11.179 6.73463 11.1919 6.73463C11.2049 6.73463 11.2175 6.73852 11.2282 6.74579C11.2372 6.74993 11.2453 6.756 11.2518 6.76356C11.2582 6.77113 11.263 6.78002 11.2657 6.7896C11.2683 6.79919 11.2689 6.80925 11.2673 6.81907C11.2657 6.8289 11.262 6.83826 11.2564 6.84649Z"
            fill="#979797"
          />
          <path
            d="M11.4854 4.01924C11.6248 4.02944 11.7296 4.15073 11.7194 4.29015L11.6557 5.16129C11.6455 5.30071 11.5242 5.40547 11.3847 5.39527C11.2453 5.38507 11.1406 5.26378 11.1508 5.12436L11.2145 4.25322C11.2247 4.1138 11.346 4.00904 11.4854 4.01924Z"
            fill="#979797"
          />
          <path
            d="M13.0589 8.06536C12.9669 8.17338 12.8047 8.18632 12.6967 8.09426L12.0217 7.51907C11.9137 7.42701 11.9008 7.26482 11.9928 7.1568C12.0849 7.04878 12.2471 7.03584 12.3551 7.12789L13.03 7.70309C13.138 7.79515 13.151 7.95734 13.0589 8.06536Z"
            fill="#979797"
          />
          <path
            d="M10.0554 4.44114C10.1764 4.37118 10.3312 4.41257 10.4012 4.53359L10.8384 5.28979C10.9083 5.41082 10.8669 5.56564 10.7459 5.63561C10.6249 5.70557 10.47 5.66418 10.4001 5.54316L9.96292 4.78696C9.89296 4.66594 9.93435 4.51111 10.0554 4.44114Z"
            fill="#979797"
          />
          <path
            d="M13.5722 6.63949C13.5568 6.78058 13.43 6.88248 13.2889 6.8671L12.4073 6.77098C12.2662 6.75559 12.1643 6.62875 12.1797 6.48766C12.1951 6.34657 12.3219 6.24467 12.463 6.26005L13.3446 6.35617C13.4857 6.37155 13.5876 6.4984 13.5722 6.63949Z"
            fill="#979797"
          />
        </>
      );
    case 'allup':
      return (
        <>
          <circle cx="6" cy="6" r="6" fill="#29B15C" />
          <path
            d="M4.17663 5.69253C3.97587 5.50593 3.65941 5.51483 3.46981 5.7124C3.2802 5.90997 3.28924 6.2214 3.49 6.408L4.99 7.80218C5.19076 7.98877 5.50721 7.97988 5.69682 7.7823L8.53015 4.82993C8.71976 4.63235 8.71072 4.32092 8.50996 4.13433C8.3092 3.94773 7.99274 3.95663 7.80314 4.1542L5.31312 6.74884L4.17663 5.69253Z"
            fill="white"
            stroke="white"
            strokeWidth="0.5"
          />
        </>
      );
    case 'circle-plus':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0ZM6 3C6.41421 3 6.75 3.33579 6.75 3.75V5.25H8.25C8.66421 5.25 9 5.58579 9 6C9 6.41421 8.66421 6.75 8.25 6.75H6.75V8.25C6.75 8.66421 6.41421 9 6 9C5.58579 9 5.25 8.66421 5.25 8.25V6.75H3.75C3.33579 6.75 3 6.41421 3 6C3 5.58579 3.33579 5.25 3.75 5.25H5.25V3.75C5.25 3.33579 5.58579 3 6 3Z"
          fill="#2C5CC5"
        />
      );
    case 'history':
      return (
        <path
          d="M12.8334 7C12.8334 8.39239 12.2802 9.72774 11.2957 10.7123C10.3111 11.6969 8.97576 12.25 7.58337 12.25C6.50561 12.2535 5.45352 11.9212 4.57337 11.2992C4.53862 11.2741 4.50968 11.2419 4.48854 11.2046C4.4674 11.1674 4.45455 11.126 4.45087 11.0833C4.44743 11.0418 4.45294 11 4.46703 10.9607C4.48111 10.9215 4.50345 10.8857 4.53254 10.8558L4.95254 10.43C5.00036 10.3827 5.06307 10.3533 5.13004 10.347C5.19702 10.3406 5.26414 10.3576 5.32004 10.395C5.98865 10.8457 6.77706 11.0854 7.58337 11.0833C8.39098 11.0833 9.18045 10.8439 9.85195 10.3952C10.5235 9.94649 11.0468 9.30876 11.3559 8.56262C11.6649 7.81649 11.7458 6.99547 11.5882 6.20338C11.4307 5.41129 11.0418 4.68371 10.4707 4.11265C9.89966 3.54158 9.17208 3.15268 8.37999 2.99513C7.5879 2.83757 6.76688 2.91843 6.02075 3.22749C5.27461 3.53655 4.63688 4.05992 4.1882 4.73142C3.73952 5.40292 3.50004 6.19239 3.50004 7H4.8767C4.91583 6.99943 4.95466 7.00691 4.99078 7.02196C5.02691 7.03701 5.05955 7.05932 5.0867 7.0875L5.20337 7.20417C5.23071 7.23128 5.25241 7.26354 5.26721 7.29908C5.28202 7.33462 5.28964 7.37275 5.28964 7.41125C5.28964 7.44975 5.28202 7.48788 5.26721 7.52342C5.25241 7.55896 5.23071 7.59122 5.20337 7.61833L3.1442 9.68333C3.0887 9.73715 3.01443 9.76724 2.93712 9.76724C2.85981 9.76724 2.78554 9.73715 2.73004 9.68333L0.670869 7.61833C0.643532 7.59122 0.621833 7.55896 0.607026 7.52342C0.592218 7.48788 0.584595 7.44975 0.584595 7.41125C0.584595 7.37275 0.592218 7.33462 0.607026 7.29908C0.621833 7.26354 0.643532 7.23128 0.670869 7.20417L0.787536 7.0875C0.814685 7.05932 0.847333 7.03701 0.883456 7.02196C0.919578 7.00691 0.958407 6.99943 0.997536 7H2.33337C2.33337 5.60761 2.88649 4.27226 3.87106 3.28769C4.85563 2.30312 6.19098 1.75 7.58337 1.75C8.97576 1.75 10.3111 2.30312 11.2957 3.28769C12.2802 4.27226 12.8334 5.60761 12.8334 7ZM7.80504 4.08333H7.3617C7.28435 4.08333 7.21016 4.11406 7.15546 4.16876C7.10077 4.22346 7.07004 4.29765 7.07004 4.375V7.09333C7.0729 7.16988 7.10409 7.24263 7.15754 7.2975L9.0767 9.21667C9.10382 9.244 9.13608 9.2657 9.17162 9.28051C9.20716 9.29532 9.24528 9.30294 9.28379 9.30294C9.32229 9.30294 9.36041 9.29532 9.39596 9.28051C9.4315 9.2657 9.46376 9.244 9.49087 9.21667L9.80004 8.9075C9.82737 8.88039 9.84907 8.84813 9.86388 8.81259C9.87869 8.77704 9.88631 8.73892 9.88631 8.70042C9.88631 8.66191 9.87869 8.62379 9.86388 8.58825C9.84907 8.55271 9.82737 8.52045 9.80004 8.49333L8.0967 6.79V4.375C8.0967 4.3367 8.08916 4.29877 8.0745 4.26338C8.05984 4.228 8.03836 4.19584 8.01128 4.16876C7.98419 4.14168 7.95204 4.12019 7.91665 4.10554C7.88127 4.09088 7.84334 4.08333 7.80504 4.08333Z"
          fill="#2C5CC5"
        />
      );
    case 'statusPagePreviewTwitter':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14.5833 2.56865C14.2172 3.05673 13.7747 3.48249 13.2728 3.82948C13.2728 3.95698 13.2728 4.08448 13.2728 4.21907C13.2748 5.3653 13.0488 6.50048 12.6078 7.55851C12.1669 8.61653 11.5198 9.57626 10.7044 10.3818C9.88893 11.1874 8.92138 11.8226 7.85805 12.2507C6.79473 12.6787 5.65687 12.8909 4.51075 12.8749C3.1723 12.8794 1.85099 12.5739 0.65033 11.9824C0.618653 11.9686 0.591715 11.9458 0.572835 11.9168C0.553955 11.8879 0.543959 11.854 0.54408 11.8195V11.7416C0.54408 11.6927 0.563483 11.6459 0.598021 11.6113C0.632559 11.5768 0.679402 11.5574 0.728247 11.5574C2.04391 11.5141 3.31263 11.0581 4.35491 10.2541C3.77139 10.2423 3.20308 10.0658 2.71555 9.745C2.22801 9.42415 1.84111 8.97201 1.5995 8.44073C1.58725 8.41161 1.5827 8.37983 1.58628 8.34844C1.58987 8.31706 1.60146 8.28712 1.61996 8.26151C1.63846 8.2359 1.66323 8.21548 1.6919 8.20221C1.72057 8.18893 1.75217 8.18326 1.78366 8.18573C2.14559 8.2221 2.51113 8.18843 2.86033 8.08657C2.21963 7.95363 1.6377 7.62063 1.19849 7.13558C0.759278 6.65054 0.485478 6.03853 0.41658 5.38782C0.414109 5.35632 0.419782 5.32472 0.433052 5.29605C0.446322 5.26738 0.466744 5.24261 0.492354 5.22411C0.517965 5.20562 0.547903 5.19402 0.57929 5.19044C0.610677 5.18685 0.642459 5.1914 0.67158 5.20365C1.02436 5.35924 1.40519 5.44119 1.79075 5.44448C1.23061 5.07701 0.807967 4.53437 0.588831 3.90131C0.369696 3.26825 0.366413 2.58044 0.579496 1.94532C0.601731 1.88293 0.639785 1.82738 0.689933 1.78412C0.740081 1.74085 0.800603 1.71135 0.865577 1.6985C0.93055 1.68564 0.997747 1.68988 1.06059 1.7108C1.12343 1.73172 1.17977 1.76859 1.22408 1.81782C2.77851 3.4714 4.91431 4.45677 7.18117 4.56615C7.1231 4.33456 7.09454 4.09657 7.09617 3.85782C7.10653 3.24571 7.29859 2.65049 7.64793 2.14776C7.99728 1.64503 8.48817 1.25746 9.05824 1.03429C9.62831 0.811123 10.2518 0.762415 10.8497 0.894356C11.4475 1.0263 11.9926 1.33293 12.4158 1.77532C13.0057 1.66298 13.5761 1.46491 14.1087 1.1874C14.1278 1.17547 14.1499 1.16914 14.1724 1.16914C14.195 1.16914 14.217 1.17547 14.2362 1.1874C14.2481 1.20652 14.2544 1.22861 14.2544 1.25115C14.2544 1.27369 14.2481 1.29578 14.2362 1.3149C13.9782 1.90537 13.5424 2.40061 12.9895 2.73157C13.4736 2.67545 13.9493 2.56129 14.4062 2.39157C14.425 2.37878 14.4472 2.37194 14.4699 2.37194C14.4927 2.37194 14.5149 2.37878 14.5337 2.39157C14.5496 2.39885 14.5637 2.40951 14.5751 2.42282C14.5865 2.43612 14.5948 2.45175 14.5995 2.46861C14.6043 2.48546 14.6052 2.50315 14.6024 2.52042C14.5996 2.5377 14.5931 2.55416 14.5833 2.56865Z"
          fill="#454B60"
        />
      );
    case 'statusPagePreviewLinkedin':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M11.4583 0.125H1.54167C1.16594 0.125 0.805609 0.274255 0.539932 0.539932C0.274255 0.805609 0.125 1.16594 0.125 1.54167V11.4583C0.125 11.8341 0.274255 12.1944 0.539932 12.4601C0.805609 12.7257 1.16594 12.875 1.54167 12.875H11.4583C11.8341 12.875 12.1944 12.7257 12.4601 12.4601C12.7257 12.1944 12.875 11.8341 12.875 11.4583V1.54167C12.875 1.16594 12.7257 0.805609 12.4601 0.539932C12.1944 0.274255 11.8341 0.125 11.4583 0.125ZM4.02083 10.3958C4.02083 10.4898 3.98352 10.5798 3.9171 10.6463C3.85068 10.7127 3.7606 10.75 3.66667 10.75H2.60417C2.51024 10.75 2.42015 10.7127 2.35373 10.6463C2.28731 10.5798 2.25 10.4898 2.25 10.3958V5.4375C2.25 5.34357 2.28731 5.25349 2.35373 5.18707C2.42015 5.12065 2.51024 5.08333 2.60417 5.08333H3.66667C3.7606 5.08333 3.85068 5.12065 3.9171 5.18707C3.98352 5.25349 4.02083 5.34357 4.02083 5.4375V10.3958ZM3.13542 4.375C2.92527 4.375 2.71985 4.31269 2.54512 4.19594C2.3704 4.07919 2.23421 3.91325 2.15379 3.7191C2.07338 3.52495 2.05234 3.31132 2.09333 3.10522C2.13433 2.89911 2.23552 2.70979 2.38412 2.5612C2.53271 2.41261 2.72203 2.31141 2.92813 2.27042C3.13424 2.22942 3.34787 2.25046 3.54202 2.33088C3.73616 2.4113 3.9021 2.54748 4.01885 2.72221C4.1356 2.89693 4.19792 3.10236 4.19792 3.3125C4.19792 3.59429 4.08597 3.86454 3.88672 4.0638C3.68746 4.26306 3.41721 4.375 3.13542 4.375ZM10.75 10.3958C10.75 10.4898 10.7127 10.5798 10.6463 10.6463C10.5798 10.7127 10.4898 10.75 10.3958 10.75H9.33333C9.2394 10.75 9.14932 10.7127 9.0829 10.6463C9.01648 10.5798 8.97917 10.4898 8.97917 10.3958V7.73958C8.97917 7.45779 8.86722 7.18754 8.66797 6.98828C8.46871 6.78902 8.19846 6.67708 7.91667 6.67708C7.63487 6.67708 7.36462 6.78902 7.16537 6.98828C6.96611 7.18754 6.85417 7.45779 6.85417 7.73958V10.3958C6.85417 10.4898 6.81685 10.5798 6.75043 10.6463C6.68401 10.7127 6.59393 10.75 6.5 10.75H5.4375C5.34357 10.75 5.25349 10.7127 5.18707 10.6463C5.12065 10.5798 5.08333 10.4898 5.08333 10.3958V5.4375C5.08333 5.34357 5.12065 5.25349 5.18707 5.18707C5.25349 5.12065 5.34357 5.08333 5.4375 5.08333H6.5C6.59393 5.08333 6.68401 5.12065 6.75043 5.18707C6.81685 5.25349 6.85417 5.34357 6.85417 5.4375V5.8625C7.06561 5.54074 7.36051 5.28247 7.70734 5.11529C8.05417 4.94812 8.43991 4.87831 8.82333 4.91333C9.36281 4.97985 9.85844 5.2441 10.2143 5.65495C10.5702 6.0658 10.7611 6.59405 10.75 7.1375V10.3958Z"
          fill="#454B60"
        />
      );
    case 'expandArrow':
      return (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.72857 0.0357123C9.85112 0.0871304 9.94858 0.184596 10 0.307141C10.0125 0.370649 10.0149 0.435738 10.0071 0.499998V2.93571C10.0071 3.21185 9.78329 3.43571 9.50714 3.43571C9.231 3.43571 9.00714 3.21185 9.00714 2.93571V1.71428L3.29286 7.42857C3.09514 7.62468 2.77628 7.62468 2.57857 7.42857C2.38246 7.23086 2.38246 6.912 2.57857 6.71428L8.29286 0.999998H7.06429C6.78814 0.999998 6.56429 0.776141 6.56429 0.499998C6.56429 0.223856 6.78814 -1.96326e-06 7.06429 -1.96326e-06H9.5C9.57777 -0.0026811 9.65533 0.00943855 9.72857 0.0357123ZM9 5.36428C9 5.08814 9.22386 4.86428 9.5 4.86428C9.77614 4.86428 10 5.08814 10 5.36428V8.28571C9.99609 9.23086 9.23086 9.99608 8.28572 10H1.71429C0.769138 9.99608 0.00391371 9.23086 0 8.28571V1.71428C0.00391371 0.769136 0.769138 0.00391174 1.71429 -1.96332e-06H4.63571C4.81435 -1.96596e-06 4.97941 0.0952975 5.06873 0.249998C5.15804 0.404699 5.15804 0.595297 5.06873 0.749998C4.97941 0.904699 4.81435 0.999998 4.63571 0.999998H1.71429C1.3198 0.999998 1 1.31979 1 1.71428V8.28571C1 8.6802 1.3198 9 1.71429 9H8.28572C8.6802 9 9 8.6802 9 8.28571V5.36428Z"
          fill="#12344D"
        />
      );
    case 'incidentSymbol':
      return (
        <>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.26879 5.2875V9.34875C8.26879 9.78367 8.62136 10.1363 9.05629 10.1363C9.49121 10.1363 9.84379 9.78367 9.84379 9.34875V5.2875C9.84379 5.00615 9.69369 4.74618 9.45004 4.60551C9.20638 4.46483 8.90619 4.46483 8.66254 4.60551C8.41888 4.74618 8.26879 5.00615 8.26879 5.2875ZM7.875 12.5003C7.875 11.8479 8.40386 11.319 9.05625 11.319C9.37427 11.3027 9.68431 11.4219 9.90948 11.647C10.1346 11.8722 10.2538 12.1823 10.2375 12.5003C10.2375 13.1527 9.70864 13.6815 9.05625 13.6815C8.40386 13.6815 7.875 13.1527 7.875 12.5003Z"
            fill="white"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 9C0 4.02944 4.02944 0 9 0C11.3869 0 13.6761 0.948212 15.364 2.63604C17.0518 4.32387 18 6.61305 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9ZM1.57496 8.99963C1.57496 13.1003 4.89925 16.4246 8.99996 16.4246C13.0981 16.4184 16.4188 13.0978 16.425 8.99963C16.425 4.89892 13.1007 1.57463 8.99996 1.57463C4.89925 1.57463 1.57496 4.89892 1.57496 8.99963Z"
            fill="white"
          />
        </>
      );
    default:
      return null;
  }
};

class Icons extends React.Component<Props> {
  render() {
    const {
      size = 32,
      style = {},
      tipText,
      tipLocation,
      count,
      onClick,
      glyph,
      dataCy,
      color,
      fill,
      viewBox
    } = this.props;

    return (
      <SvgWrapper
        data-cy={dataCy}
        size={size}
        tipText={tipText}
        tipLocation={tipLocation}
        count={count}
        className="icon"
        onClick={onClick}
        style={style}
      >
        <InlineSvg
          fillRule="evenodd"
          fill={fill}
          clipRule="evenodd"
          strokeLinejoin="round"
          strokeMiterlimit="1.414"
          xmlns="http://www.w3.org/2000/svg"
          aria-labelledby="title"
          viewBox={viewBox || '0 0 32 32'}
          color={color}
          preserveAspectRatio="xMidYMid meet"
          fit
          id={glyph}
        >
          {/* <title id="title">{glyph}</title> */}
          <Glyph glyph={glyph} />
        </InlineSvg>
      </SvgWrapper>
    );
  }
}

export default Icons;

export const CustomIcons = type => {
  switch (type) {
    case 'selected':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 100 100"
          fill="none"
        >
          <circle cx="50" cy="50" r="50" fill="#2F2FBE" />
          <line
            x1="28.589"
            y1="48.9578"
            x2="47.4321"
            y2="61.4006"
            stroke="white"
            strokeWidth="9"
          />
          <line
            x1="46.437"
            y1="65.6088"
            x2="71.132"
            y2="29.1047"
            stroke="white"
            strokeWidth="9"
          />
        </svg>
      );
    case 'operational':
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="14" cy="14" r="13.5" fill="white" stroke="#2A9E55" />
          <path
            d="M9.89745 13.8082C9.44575 13.3883 8.73373 13.4084 8.30711 13.8529C7.8805 14.2974 7.90084 14.9982 8.35255 15.418L11.7275 18.5549C12.1793 18.9747 12.8913 18.9547 13.3179 18.5102L19.6929 11.8673C20.1195 11.4228 20.0992 10.7221 19.6474 10.3022C19.1957 9.8824 18.4837 9.90242 18.0571 10.347L12.4546 16.1849L9.89745 13.8082Z"
            fill="#29B15C"
          />
        </svg>
      );
    case 'dashboard':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="14"
          viewBox="0 0 20 14"
        >
          <g fill="#FFF" fillRule="evenodd" transform="translate(0 -1.262)">
            <rect width="9.143" height="1.169" y="3.508" rx=".585" />
            <rect width="9.143" height="1.169" y="8.185" rx=".585" />
            <path d="M15.4 13.75l2.885-2.887a.5.5 0 0 1 .708 0l.12.12a.5.5 0 0 1 0 .707l-3.334 3.334-.007.007a.5.5 0 0 1-.707 0l-3.428-3.427a.5.5 0 0 1 0-.708l.101-.1a.5.5 0 0 1 .708 0l2.953 2.953zm.045-12.456c.062.024.12.061.17.111l3.426 3.427a.5.5 0 0 1 0 .707l-.1.101a.5.5 0 0 1-.708 0l-2.958-2.958-2.881 2.88a.5.5 0 0 1-.707 0l-.12-.12a.5.5 0 0 1 0-.706L14.9 1.402a.5.5 0 0 1 .545-.108z" />
            <rect width="5.714" height="1.169" y="12.863" rx=".585" />
          </g>
        </svg>
      );
    case 'alerts':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="20"
          viewBox="0 0 18 20"
        >
          <g id="Page-1" fill="none" fillRule="evenodd" strokeLinecap="round">
            <g
              id="Alerts_card_on_select"
              transform="translate(-20 -77)"
              stroke="#FFF"
            >
              <g id="side_menu">
                <g id="bell" transform="translate(21 77)">
                  <g id="Group" transform="translate(.4)">
                    <g id="Shape">
                      <path d="M16.0844,13.3176 C15.1624,12.33568 13.16336,9.71704 13.3268,5.59672 C13.42352,3.16024 12.10424,1.86864 10.76088,1.18608 C9.06512,0.32456 7.03464,0.32456 5.33888,1.18608 C3.99544,1.86856 2.67616,3.16016 2.77288,5.59664 C2.9364,9.71704 0.93736,12.33568 0.01536,13.31752" />
                      <path d="M16.0844,13.3176 C11.70288,16.88704 4.3912,16.88248 0.01536,13.3176" />
                    </g>
                    <path
                      d="M6.24088,17.22976 C6.24088,18.01768 7.0524,18.65872 8.0496,18.65872 C9.0472,18.65872 9.8588,18.01768 9.8588,17.22976"
                      id="Shape"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      );
    case 'check':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <defs>
            <path
              id="a"
              d="M9.59 15.58l-3.465-3.465a1 1 0 0 0-1.412-.003l-.003.003a.997.997 0 0 0-.002 1.413L9.59 18.41l8.925-9.024a1 1 0 0 0-.004-1.41l-.001-.002a.991.991 0 0 0-1.406.004L9.59 15.58z"
            />
          </defs>
          <g fill="none" fillRule="evenodd">
            <mask id="b" fill="#fff">
              <use xlinkHref="#a" />
            </mask>
            <use fill="#1F9999" fillRule="nonzero" xlinkHref="#a" />
            <g fill="#149999" mask="url(#b)">
              <path d="M0 0h24v24H0z" />
            </g>
          </g>
        </svg>
      );
    case 'incident':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18px"
          height="15px"
          viewBox="0 0 18 15"
        >
          <g
            id="Symbols"
            stroke="none"
            strokeWidth="1"
            fill="none"
            fillRule="evenodd"
          >
            <g
              id="side_menu"
              transform="translate(-18.000000, -140.000000)"
              stroke="#FFFFFF"
            >
              <g id="ic_incident_maintenance">
                <g transform="translate(27.153846, 147.653846) scale(-1, 1) rotate(-90.000000) translate(-27.153846, -147.653846) translate(19.653846, 138.653846)">
                  <g id="Group" transform="translate(-0.000000, 0.000000)">
                    <path
                      d="M6.11249231,6.92740927 L5.01193215,6.92298462 C3.88227061,6.92298462 2.96565522,7.86138462 2.96565522,8.99086154 L2.96565522,11.9283821 L2.96565522,15.0959077 C2.96565522,16.2255692 3.88227061,17.0768308 5.01193215,17.0768308 L11.9824615,17.0768308 C13.1121231,17.0768308 14.0642769,16.2254769 14.0642769,15.0959077 L14.0642769,10.8460615"
                      id="Shape"
                      strokeLinecap="round"
                      transform="translate(8.514966, 11.999908) scale(-1, 1) rotate(-360.000000) translate(-8.514966, -11.999908) "
                    />
                    <path
                      d="M4.9802265,8.89252945 C7.21060722,8.89252945 9.01868803,7.08444863 9.01868803,4.85406791 C9.01868803,2.62368719 7.21060722,0.815606373 4.9802265,0.815606373 C2.74984578,0.815606373 0.941764957,2.62368719 0.941764957,4.85406791 C0.941764957,7.08444863 2.74984578,8.89252945 4.9802265,8.89252945 Z"
                      id="Oval"
                    />
                    <g
                      id="Group-2"
                      transform="translate(8.006073, 11.728218) rotate(-270.000000) translate(-8.006073, -11.728218) translate(7.006073, 8.228218)"
                      strokeLinecap="round"
                    >
                      <path
                        d="M1.38716671,3.1574173 L1.38716671,0.0504129811"
                        id="Line"
                      />
                      <path
                        d="M1.39045238,5.73836612 L1.39045238,5.69914061"
                        id="Line-Copy"
                      />
                    </g>
                    <polyline
                      id="Path-3"
                      strokeLinecap="round"
                      points="5.78557561 5.32096557 4.76489746 4.81762939 4.55406892 2.73768821"
                    />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      );

    case 'reports':
      return (
        <svg
          id="icon-report"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            fillRule: 'evenodd',
            clipRule: 'evenodd',
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            strokeMiterlimit: 1.5,
            width: '21px'
          }}
        >
          <path
            d="M19.25 17.875H2.75V4.125"
            stroke="#F3F2FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            className="circle"
            d="M19.2502 8.25L13.7502 13.0625L8.25018 8.9375L2.75018 13.75"
            stroke="#F3F2FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case 'settings':
      return (
        <svg
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          id="icon-settings"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.30435 1.28127C8.72404 1.16509 9.16754 1.36023 9.36544 1.74814C9.6698 2.34471 10.2884 2.75 11 2.75C11.7117 2.75 12.3302 2.34471 12.6346 1.74814C12.8325 1.36023 13.276 1.16509 13.6957 1.28127C15.276 1.71873 16.6993 2.53059 17.8646 3.61411C18.1612 3.88987 18.2417 4.32756 18.0626 4.69078C17.9428 4.93375 17.875 5.20766 17.875 5.5C17.875 6.51252 18.6958 7.33333 19.7084 7.33333L19.7339 7.33315C20.1391 7.32736 20.5 7.58837 20.6215 7.97503C20.9218 8.9311 21.0834 9.94754 21.0834 11C21.0834 11.6946 21.013 12.3737 20.8787 13.0303C20.7846 13.4903 20.3582 13.8048 19.891 13.7589C19.8312 13.753 19.7704 13.75 19.7084 13.75C18.6958 13.75 17.875 14.5708 17.875 15.5833C17.875 16.0343 18.0366 16.4451 18.3062 16.7645C18.6089 17.1234 18.5916 17.653 18.2661 17.9913C17.0762 19.2277 15.5725 20.1619 13.8815 20.6654C13.4032 20.8078 12.8988 20.5413 12.7468 20.0659C12.5102 19.3258 11.8163 18.7917 11 18.7917C10.1837 18.7917 9.48984 19.3258 9.25323 20.0659C9.10126 20.5413 8.59688 20.8078 8.11853 20.6654C6.42752 20.1619 4.92383 19.2277 3.73397 17.9913C3.4084 17.653 3.39108 17.1234 3.69386 16.7645C3.9634 16.4451 4.12502 16.0343 4.12502 15.5833C4.12502 14.5708 3.30421 13.75 2.29169 13.75C2.22971 13.75 2.1688 13.753 2.10907 13.7589C1.64184 13.8048 1.21545 13.4903 1.12136 13.0303C0.987057 12.3737 0.916687 11.6946 0.916687 11C0.916687 9.94755 1.07824 8.9311 1.37855 7.97502C1.5 7.58837 1.86094 7.32736 2.26618 7.33315L2.29169 7.33333C3.30421 7.33333 4.12502 6.51252 4.12502 5.5C4.12502 5.20766 4.05726 4.93376 3.93746 4.69078C3.75838 4.32756 3.83886 3.88987 4.13543 3.61411C5.3007 2.53059 6.72404 1.71873 8.30435 1.28127ZM5.83766 4.56433C5.91645 4.86353 5.95835 5.17728 5.95835 5.5C5.95835 7.29376 4.6703 8.78668 2.96865 9.1043C2.82576 9.71227 2.75002 10.3468 2.75002 11C2.75002 11.3223 2.76846 11.64 2.80429 11.9522C4.58667 12.2015 5.95835 13.7323 5.95835 15.5833C5.95835 16.1693 5.82039 16.7241 5.57545 17.216C6.26296 17.8166 7.05049 18.3045 7.9093 18.6518C8.56039 17.6339 9.70055 16.9583 11 16.9583C12.2995 16.9583 13.4396 17.6339 14.0907 18.6518C14.9495 18.3045 15.7371 17.8165 16.4246 17.216C16.1796 16.7241 16.0417 16.1693 16.0417 15.5833C16.0417 13.7323 17.4134 12.2015 19.1957 11.9522C19.2316 11.64 19.25 11.3223 19.25 11C19.25 10.3468 19.1743 9.71227 19.0314 9.1043C17.3297 8.78668 16.0417 7.29375 16.0417 5.5C16.0417 5.17728 16.0836 4.86353 16.1624 4.56433C15.4678 4.00635 14.6815 3.55886 13.8301 3.24795C13.1583 4.06254 12.1405 4.58333 11 4.58333C9.85958 4.58333 8.84176 4.06254 8.16996 3.24795C7.31856 3.55886 6.53224 4.00635 5.83766 4.56433ZM6.87502 11C6.87502 8.72182 8.72184 6.875 11 6.875C13.2782 6.875 15.125 8.72182 15.125 11C15.125 13.2782 13.2782 15.125 11 15.125C8.72184 15.125 6.87502 13.2782 6.87502 11ZM11 8.70833C9.73436 8.70833 8.70835 9.73434 8.70835 11C8.70835 12.2657 9.73436 13.2917 11 13.2917C12.2657 13.2917 13.2917 12.2657 13.2917 11C13.2917 9.73434 12.2657 8.70833 11 8.70833Z"
            fill="#F3F2FF"
          />
        </svg>
      );
    case 'close':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="20"
          height="20"
          viewBox="0 0 24 16"
        >
          <defs>
            <path
              id="a"
              d="M18.295 5.705a.997.997 0 0 0-1.41 0L12 10.59 7.115 5.705a.997.997 0 1 0-1.41 1.41L10.59 12l-4.885 4.885a.997.997 0 0 0 1.41 1.41L12 13.41l4.885 4.885a.997.997 0 0 0 1.41-1.41L13.41 12l4.885-4.885a.997.997 0 0 0 0-1.41z"
            />
          </defs>
          <g fill="none" fillRule="evenodd">
            <mask id="b" fill="#fff">
              <use xlinkHref="#a" />
            </mask>
            <use fill="#597182" xlinkHref="#a" />
            <g fill="#597182" mask="url(#b)">
              <path d="M0 0h24v24H0z" />
            </g>
          </g>
        </svg>
      );
    case 'uparrow':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <defs>
            <path
              id="a"
              d="M11.295 4.705a.998.998 0 0 0 0 1.41L16.17 11H5a1 1 0 0 0 0 2h11.17l-4.876 4.884a.997.997 0 1 0 1.41 1.41l7.296-7.294-7.295-7.295a.997.997 0 0 0-1.41 0z"
            />
          </defs>
          <g fill="none" fillRule="evenodd">
            <mask id="b" fill="#fff">
              <use xlinkHref="#a" />
            </mask>
            <g fill="#28bf43" mask="url(#b)" transform="rotate(-90 12 12)">
              <path d="M0 0h24v24H0z" />
            </g>
          </g>
        </svg>
      );
    case 'downarrow':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="24"
          height="24"
          viewBox="0 0 24 24"
        >
          <defs>
            <path
              id="a"
              d="M11.295 4.705a.998.998 0 0 0 0 1.41L16.17 11H5a1 1 0 0 0 0 2h11.17l-4.876 4.884a.997.997 0 1 0 1.41 1.41l7.296-7.294-7.295-7.295a.997.997 0 0 0-1.41 0z"
            />
          </defs>
          <g fill="none" fillRule="evenodd">
            <mask id="b" fill="#fff">
              <use xlinkHref="#a" />
            </mask>
            <g fill="#ff6060" mask="url(#b)" transform="rotate(90 12 12)">
              <path d="M0 0h24v24H0z" />
            </g>
          </g>
        </svg>
      );
    case 'customize':
      return (
        <svg
          width="19"
          height="19"
          viewBox="0 0 19 19"
          fill="none"
          id="icon-magicwand"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.31148 4.75409C2.31148 3.95735 2.95736 3.31147 3.7541 3.31147H7.84426C8.20642 3.31147 8.5 3.01789 8.5 2.65573C8.5 2.29358 8.20642 2 7.84426 2H3.7541C2.23305 2 1 3.23305 1 4.75409V15.2459C1 16.7669 2.23305 18 3.7541 18H14.2459C15.7669 18 17 16.7669 17 15.2459V11C17 10.6378 16.7064 10.3443 16.3443 10.3443C15.9821 10.3443 15.6885 10.6378 15.6885 11V15.2459C15.6885 16.0426 15.0426 16.6885 14.2459 16.6885H3.7541C2.95736 16.6885 2.31148 16.0426 2.31148 15.2459V6.97297C2.35852 6.99049 2.40901 7 2.46154 7H7.53846C7.79336 7 8 6.77614 8 6.5C8 6.22385 7.79336 6 7.53846 6H2.46154C2.40901 6 2.35852 6.0095 2.31148 6.02702V4.75409Z"
            fill="#F3F2FF"
          />
          <path
            d="M2.31148 6.97297L2.35509 6.85583L2.18648 6.79306V6.97297H2.31148ZM2.31148 6.02702H2.18648V6.20693L2.35509 6.14416L2.31148 6.02702ZM2.43648 4.75409C2.43648 4.02639 3.0264 3.43647 3.7541 3.43647V3.18647C2.88832 3.18647 2.18648 3.88832 2.18648 4.75409H2.43648ZM3.7541 3.43647H7.84426V3.18647H3.7541V3.43647ZM7.84426 3.43647C8.27545 3.43647 8.625 3.08692 8.625 2.65573H8.375C8.375 2.94885 8.13738 3.18647 7.84426 3.18647V3.43647ZM8.625 2.65573C8.625 2.22454 8.27545 1.875 7.84426 1.875V2.125C8.13738 2.125 8.375 2.36261 8.375 2.65573H8.625ZM7.84426 1.875H3.7541V2.125H7.84426V1.875ZM3.7541 1.875C2.16402 1.875 0.875 3.16401 0.875 4.75409H1.125C1.125 3.30208 2.30209 2.125 3.7541 2.125V1.875ZM0.875 4.75409V15.2459H1.125V4.75409H0.875ZM0.875 15.2459C0.875 16.836 2.16402 18.125 3.7541 18.125V17.875C2.30209 17.875 1.125 16.6979 1.125 15.2459H0.875ZM3.7541 18.125H14.2459V17.875H3.7541V18.125ZM14.2459 18.125C15.836 18.125 17.125 16.836 17.125 15.2459H16.875C16.875 16.6979 15.6979 17.875 14.2459 17.875V18.125ZM17.125 15.2459V11H16.875V15.2459H17.125ZM17.125 11C17.125 10.5688 16.7755 10.2193 16.3443 10.2193V10.4693C16.6374 10.4693 16.875 10.7069 16.875 11H17.125ZM16.3443 10.2193C15.9131 10.2193 15.5635 10.5688 15.5635 11H15.8135C15.8135 10.7069 16.0511 10.4693 16.3443 10.4693V10.2193ZM15.5635 11V15.2459H15.8135V11H15.5635ZM15.5635 15.2459C15.5635 15.9736 14.9736 16.5635 14.2459 16.5635V16.8135C15.1117 16.8135 15.8135 16.1117 15.8135 15.2459H15.5635ZM14.2459 16.5635H3.7541V16.8135H14.2459V16.5635ZM3.7541 16.5635C3.0264 16.5635 2.43648 15.9736 2.43648 15.2459H2.18648C2.18648 16.1117 2.88832 16.8135 3.7541 16.8135V16.5635ZM2.43648 15.2459V6.97297H2.18648V15.2459H2.43648ZM2.26787 7.09012C2.32855 7.11271 2.39378 7.125 2.46154 7.125V6.875C2.42423 6.875 2.3885 6.86827 2.35509 6.85583L2.26787 7.09012ZM2.46154 7.125H7.53846V6.875H2.46154V7.125ZM7.53846 7.125C7.87163 7.125 8.125 6.83556 8.125 6.5H7.875C7.875 6.71671 7.71509 6.875 7.53846 6.875V7.125ZM8.125 6.5C8.125 6.16443 7.87163 5.875 7.53846 5.875V6.125C7.71509 6.125 7.875 6.28328 7.875 6.5H8.125ZM7.53846 5.875H2.46154V6.125H7.53846V5.875ZM2.46154 5.875C2.39378 5.875 2.32855 5.88728 2.26787 5.90987L2.35509 6.14416C2.3885 6.13173 2.42423 6.125 2.46154 6.125V5.875ZM2.43648 6.02702V4.75409H2.18648V6.02702H2.43648Z"
            fill="#F3F2FF"
          />
          <path
            id="wand"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.5168 4.04488L17.8293 5.62857C17.9509 5.77012 17.9868 5.96618 17.9233 6.14164C17.8399 6.3222 17.6651 6.44328 17.4667 6.45788L15.4155 6.56353L14.3191 8.30131C14.2231 8.45021 14.0583 8.54055 13.8811 8.54154L13.7988 8.54578C13.5986 8.52428 13.4284 8.39015 13.3607 8.20056L12.8922 6.82859L7.20991 13.0884C7.11651 13.1941 6.98453 13.2577 6.84371 13.2649C6.70858 13.2784 6.57375 13.2366 6.46995 13.1491C6.3624 13.0568 6.29738 12.9245 6.2901 12.783C6.28281 12.6415 6.33388 12.5032 6.43139 12.4005L12.0743 6.10512L10.6596 5.74264C10.4631 5.69643 10.3114 5.54012 10.2712 5.34232C10.2324 5.14685 10.306 4.94609 10.462 4.82209L12.0492 3.57693L11.9436 1.52576C11.9326 1.32575 12.0364 1.13693 12.2112 1.0391C12.3823 0.939124 12.5954 0.945637 12.76 1.05587L14.4702 2.19876L16.3751 1.44013C16.5607 1.36282 16.7744 1.40179 16.9207 1.53963C17.0714 1.6687 17.132 1.87441 17.0753 2.06458L16.5168 4.04488ZM14.6541 5.76959C14.7388 5.61652 14.9023 5.52397 15.0771 5.53013L16.358 5.47917L15.557 4.49963C15.4443 4.36884 15.4067 4.18937 15.4574 4.02438L15.8 2.80579L14.623 3.27173C14.4637 3.3363 14.2827 3.3175 14.1401 3.22155L13.0834 2.52538L13.1486 3.79052C13.1646 3.96259 13.0911 4.13082 12.9538 4.23589L11.9593 5.0377L13.1839 5.34992C13.3416 5.3869 13.4694 5.50215 13.5223 5.65524L13.9587 6.84121L14.6541 5.76959Z"
            fill="#F3F2FF"
          />
        </svg>
      );
    case 'explore':
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          id="icon-explore"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.08316 13.8614C5.11089 16.7782 1.2218 16.7782 1.2218 16.7782C1.2218 16.7782 1.2218 12.8891 4.13862 11.9168M9 12.8891L14.8336 7.05546C17.0426 4.84651 17.021 2.63756 16.8805 1.69551C16.8588 1.55046 16.7912 1.41619 16.6875 1.31249C16.5838 1.20879 16.4495 1.14115 16.3045 1.11953C15.3624 0.97897 13.1535 0.95743 10.9445 3.16638L5.1109 9M9 12.8891L5.1109 9M9 12.8891L9.7203 16.4908C9.7447 16.6128 9.8018 16.7259 9.8854 16.8181C9.9691 16.9102 10.0762 16.9779 10.1953 17.014C10.3144 17.05 10.441 17.0531 10.5617 17.0228C10.6824 16.9926 10.7926 16.9301 10.8806 16.8421L13.66 14.0627C13.7238 13.9989 13.7745 13.9231 13.809 13.8397C13.8436 13.7563 13.8614 13.6669 13.8614 13.5766V8.0277M5.1109 9L1.50921 8.2797C1.38719 8.2553 1.27408 8.1982 1.18195 8.1146C1.08981 8.0309 1.0221 7.92384 0.986042 7.80474C0.949982 7.68564 0.946922 7.55899 0.977192 7.43829C1.00746 7.31759 1.06992 7.20737 1.15791 7.11938L3.93728 4.34C4.00112 4.27616 4.07691 4.22552 4.16032 4.19097C4.24373 4.15642 4.33313 4.13864 4.42341 4.13864H9.9723"
            stroke="#F3F2FF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'component':
      return (
        <svg
          width="18"
          height="20"
          viewBox="0 0 18 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.2057 5.3088V5.2288C17.1442 5.14236 17.0666 5.06863 16.9772 5.01166L9.36572 1.09166C9.13822 0.965541 8.86179 0.965541 8.63429 1.09166L1.12572 4.97737C1.03625 5.03435 0.958635 5.10808 0.897152 5.19452L0.840009 5.27452C0.776741 5.39047 0.741514 5.51964 0.737152 5.65166V14.5659C0.740795 14.864 0.907529 15.136 1.17144 15.2745L8.68001 19.1602C8.89622 19.2665 9.14951 19.2665 9.36572 19.1602L16.8743 15.2745C17.1382 15.136 17.3049 14.864 17.3086 14.5659V5.68594C17.3042 5.55392 17.269 5.42476 17.2057 5.3088ZM9.00001 2.70309L14.7714 5.68594L9.00001 8.6688L3.28572 5.68594L9.00001 2.70309ZM2.29144 7.00023L8.20001 10.0517V17.1717L2.29144 14.1202V7.00023ZM9.80001 17.1717V10.0517L15.7086 7.00023V14.1202L9.80001 17.1717Z"
            fill="#E4E3FE"
          />
        </svg>
      );
    case 'email':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="10"
          viewBox="0 0 12 10"
        >
          <g fill="#8C8C8C" fillRule="nonzero" opacity=".56">
            <path d="M1.15 2.923l1.373.954c.763.529 1.347.936 1.753 1.221l.284.204c.145.105.266.19.362.254.095.065.211.138.348.218.136.08.264.14.384.18.12.04.232.06.335.06H6.002c.103 0 .215-.02.335-.06.12-.04.249-.1.385-.18a7.066 7.066 0 0 0 .709-.472l.284-.204 3.132-2.175c.326-.227.598-.502.816-.823.219-.32.328-.658.328-1.01 0-.295-.106-.547-.318-.756a1.034 1.034 0 0 0-.752-.315H1.07C.727.02.463.135.278.367.093.6 0 .89 0 1.237c0 .281.123.586.368.913.245.328.506.586.783.773z" />
            <path d="M11.322 3.64A139.07 139.07 0 0 0 7.99 5.947c-.255.187-.46.333-.62.438a3.82 3.82 0 0 1-.632.321c-.263.11-.508.164-.736.164H5.99c-.228 0-.473-.054-.736-.164a3.822 3.822 0 0 1-.632-.32 15.847 15.847 0 0 1-.62-.44C3.4 5.508 2.292 4.738.677 3.64A3.544 3.544 0 0 1 0 3.057V8.37c0 .295.105.547.314.756.21.21.462.315.757.315h9.85c.294 0 .546-.105.756-.315.21-.21.314-.461.314-.756V3.057a3.69 3.69 0 0 1-.669.582z" />
          </g>
        </svg>
      );
    case 'phoneNumber':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
        >
          <path
            fill="#8C8C8C"
            fillRule="nonzero"
            d="M11.976 9.47a.58.58 0 0 1-.178.508l-1.687 1.677a.95.95 0 0 1-.298.216 1.23 1.23 0 0 1-.361.114l-.076.007a1.724 1.724 0 0 1-.165.006c-.161 0-.421-.027-.78-.082-.36-.055-.8-.191-1.32-.407-.52-.216-1.11-.54-1.769-.972-.66-.432-1.361-1.025-2.105-1.779a13.916 13.916 0 0 1-1.472-1.677 11.953 11.953 0 0 1-.938-1.48 7.98 7.98 0 0 1-.533-1.233 6.548 6.548 0 0 1-.241-.953 3.152 3.152 0 0 1-.05-.629l.012-.254c.017-.118.055-.239.114-.362a.952.952 0 0 1 .216-.298L2.032.182a.558.558 0 0 1 .405-.178c.11 0 .208.032.292.095a.883.883 0 0 1 .216.235l1.357 2.58a.62.62 0 0 1 .063.444.792.792 0 0 1-.215.407l-.622.622a.212.212 0 0 0-.044.083.314.314 0 0 0-.02.095c.035.178.11.381.23.61a5.6 5.6 0 0 0 .468.743c.212.293.512.63.901 1.01.38.39.719.693 1.015.91.296.215.543.374.742.476.198.101.35.163.456.184l.159.032a.302.302 0 0 0 .082-.02.212.212 0 0 0 .083-.044l.723-.737a.776.776 0 0 1 .533-.203c.143 0 .257.025.342.076h.013l2.448 1.448c.177.11.283.25.317.42z"
            opacity=".56"
          />
        </svg>
      );
    case 'orgSize':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="11"
          viewBox="0 0 14 11"
        >
          <path
            fill="#8C8C8C"
            fillRule="nonzero"
            d="M6.985 5.284c1.225 0 2.177-1.27 2.177-2.585v-.412C9.162.972 8.21.039 6.985.039S4.808.972 4.808 2.287v.412c0 1.314.952 2.585 2.177 2.585zm-4.362.425c.35 0 1.309-.455 1.309-1.397v-.699c0-.942-.692-1.397-1.309-1.397-.596 0-1.308.455-1.308 1.397v.699c0 .942.977 1.397 1.308 1.397zm7.84 1.746l-.004-.02a1.683 1.683 0 0 0-.048-.241v-.002a1.494 1.494 0 0 0-.239-.488c-.188-.262-.474-.529-.937-.79-.245-.137-.352-.2-.504-.2-.24 0-.45.188-.805.513l-.322.296c-.012.01-.359.5-.619.5s-.607-.49-.619-.5l-.322-.296c-.354-.325-.564-.513-.805-.513-.151 0-.287.063-.504.2-.45.284-.748.527-.937.789l-.002.002a1.494 1.494 0 0 0-.285.728c0 .008-.003.015-.003.023a2.42 2.42 0 0 0-.015.266v1.921c0 .62.234.873.873.873h5.238c.645 0 .873-.239.873-.873v-1.92c0-.092-.005-.18-.015-.268zm.891-1.746c.35 0 1.309-.455 1.309-1.397v-.699c0-.942-.691-1.397-1.309-1.397-.596 0-1.308.455-1.308 1.397v.699c0 .942.977 1.397 1.308 1.397zm-10.36.945A1.889 1.889 0 0 0 0 8.334v.873c0 .633.213.873.873.873H2.62V7.46l-.437-.436c-.462-.407-.851-.562-1.19-.37zm1.652.824c.022.01 0-.247 0 0zm11.324.856c0-.48-.181-1.217-.993-1.68-.339-.192-.728-.037-1.19.37l-.436.436v2.62h1.746c.66 0 .873-.24.873-.873v-.873zm-2.587-.856c0-.247-.022.01 0 0z"
            opacity=".56"
          />
        </svg>
      );
    case 'employeeRole':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="13"
          height="14"
          viewBox="0 0 13 14"
        >
          <g fill="#8C8C8C" fillRule="nonzero" opacity=".56">
            <path d="M11.143 9.286C9.685 8.73 8.609 8.264 8.609 8.264L7.482 11.82l-.154.48-.502-1.425c1.158-1.612-.086-1.692-.305-1.692h-.01-.006c-.219 0-1.468.08-.31 1.692l-.501 1.426-.155-.48-1.126-3.557s-.876.572-2.334 1.127a2.32 2.32 0 0 0-.667.358c-.322.31-.616 1.652-.88 4.025h11.932c-.703-2.925-1.143-4.42-1.321-4.488z" />
            <path d="M3.489 5.093c.197.705.384.897.523.913.128.838.774 1.9 1.831 2.27.433.154.913.154 1.346-.006C8.229 7.896 8.897 6.834 9.025 6c.14-.01.326-.207.523-.912.273-.961-.016-1.105-.261-1.079.048-.133.08-.266.107-.395.416-2.488-.812-2.573-.812-2.573S8.38.651 7.845.352C7.488.14 6.985-.027 6.33.032a2.2 2.2 0 0 0-.603.117 2.59 2.59 0 0 0-.662.342 3.44 3.44 0 0 0-.684.566 2.705 2.705 0 0 0-.736 1.297c-.107.4-.08.817.005 1.266.021.133.059.261.107.395-.251-.022-.54.117-.267 1.078z" />
          </g>
        </svg>
      );
    case 'agentMessagePointer':
      return (
        <svg
          width="12px"
          height="17px"
          viewBox="0 0 14 18"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>Path</title>
          <desc>Created with Sketch.</desc>
          <g
            id="Page-1"
            stroke="none"
            strokeWidth="1"
            fill="none"
            fillRule="evenodd"
          >
            <g
              id="2"
              transform="translate(-233.000000, -168.000000)"
              fill="#FFFFFF"
              stroke="#EDEDED"
            >
              <path
                d="M246,180 L235.718324,185.140838 C235.224345,185.387827 234.623672,185.187603 234.376683,184.693624 C234.208655,184.357569 234.243177,183.955688 234.466054,183.653212 L246,168 L246,168"
                id="Path"
              />
            </g>
          </g>
        </svg>
      );
    case 'empty-maintenance-list':
      return (
        <svg
          width="252"
          height="198"
          viewBox="0 0 252 198"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M105.279 114.411C105.279 114.411 121.824 120.43 126.208 119.55C130.591 118.669 132.046 117.796 136.731 111.95C141.416 106.104 150.172 95.0011 150.172 95.0011C150.172 95.0011 151.048 91.4979 161.864 91.2013C161.864 91.2013 164.216 91.0308 164.216 90.5006C164.216 89.9705 162.485 84.1738 164.893 83.1952C167.301 82.2166 173.779 83.4287 177.016 82.4432C180.253 81.4576 185.074 79.2061 186.052 79.5822C187.031 79.9582 186.882 85.4559 186.503 87.1866C186.503 87.1866 192.001 87.9386 194.635 89.5221C194.635 89.5221 183.642 91.255 178.899 94.492C174.155 97.729 171.161 100.209 169.055 104.32C165.986 110.306 169.732 128.791 170.183 132.031C170.634 135.27 174.324 157.707 174.324 157.707C174.324 157.707 163.933 149.122 157.382 148.218C157.382 148.218 155.123 129.244 151.433 120.425C151.433 120.425 146.013 124.716 143.602 126.75C141.192 128.784 131.598 135.459 128.961 136.363C126.325 137.267 123.202 135.798 117.328 133.093C111.454 130.389 98.9175 123.38 98.9175 123.38L105.279 114.411Z"
            fill="white"
          />
          <path
            d="M149 107.101H70.1115L64.0112 56.1874C63.759 54.0855 65.2537 52.2357 67.1875 52.2357H137.738C139.329 52.2357 140.679 53.5086 140.91 55.2275L145.735 91.2946"
            fill="white"
          />
          <path
            d="M86.9832 107.335L70.1115 107.101L64.0112 56.1874C63.759 54.0855 65.2537 52.2357 67.1875 52.2357H137.738C139.329 52.2357 140.679 53.5086 140.91 55.2275L146.5 97.5"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M159.164 48.2047C159.164 48.2047 154.846 47.3079 152.814 45.0378C150.782 42.7677 155.448 43.5898 157.604 44.4025C157.604 44.4025 152.503 41.9129 152.517 39.6217C152.531 37.3306 159.316 40.0468 159.316 40.0468C159.316 40.0468 154.39 36.5155 153.748 33.3976C153.106 30.2798 161.345 34.0703 165.778 34.1287C170.211 34.187 179.119 32.2439 181.37 33.5565C185.436 35.9293 185.849 38.433 185.849 38.433"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M163.412 47.114C163.412 47.114 162.055 49.8232 164.428 55.2462C166.801 60.6693 165.783 49.4846 170.188 49.4846C174.592 49.4846 170.673 66.8 176.961 69.8338C189.614 75.9341 188.708 45.6637 188.708 45.6637"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M157.793 50.2366C157.793 50.2366 156.929 59.623 157.515 66.2301C158.101 72.8372 157.812 76.957 160.983 77.312C163.452 77.5876 165.012 77.3354 167.177 76.6861C168.681 76.2354 169.585 74.4277 169.585 74.4277"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M167.177 77.1392C167.177 77.1392 167.817 78.1925 167.478 81.92"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M155.278 118.41C155.278 118.41 133.59 138.283 127.268 137.829C120.946 137.376 97.9016 124.284 97.9016 124.284L105.13 114.192C105.13 114.192 120.04 119.764 125.916 119.764C131.792 119.764 134.725 113.666 134.725 113.666C134.725 113.666 144.889 101.692 148.052 96.9489C151.214 92.2055 155.168 91.5772 158.561 91.5772C161.955 91.5772 163.419 91.2386 163.249 90.5613C162.478 87.4761 162.364 85.1196 164.379 83.3329C165.703 82.1652 169.893 83.4637 173.955 83.013C178.016 82.5622 178.626 82.2586 183.593 80.0002C188.561 77.7418 186.907 87.2286 186.907 87.2286C186.907 87.2286 193.232 87.3804 195.341 89.0362"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M151.212 122.469C151.212 122.469 155.731 136.926 155.731 147.769"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M97.8175 124.417C97.8175 124.417 85.6566 119.36 84.9349 116.59C84.2133 113.821 92.5206 116.952 92.5206 116.952L88.7838 111.413C88.3206 112.384 87.563 113.185 86.6188 113.701C85.0517 114.545 85.0517 112.618 86.0163 110.691C86.9808 108.764 88.3027 105.873 90.1104 107.559C91.918 109.245 94.2045 111.653 94.2045 111.653"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M89.9329 106.816C89.9329 106.816 90.6335 103.456 93.2329 106.053C95.19 108.012 97.9038 110.271 97.9038 110.271"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M104.903 114.563C104.903 114.563 96.7712 105.978 95.7552 104.962C94.7393 103.946 93.2703 104.962 93.2703 104.962"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M140.973 102.666C140.973 102.666 135.475 102.589 135.4 104.17C135.326 105.751 136.907 103.418 139.917 106.204H97.8269"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M173.803 154.094C173.803 154.094 168.198 122.674 167.929 110.271C167.478 89.0363 217.04 81.1587 222.145 99.88C227.751 120.407 218.53 155.902 218.53 155.902"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M181.634 70.9641C181.634 70.9641 181.181 75.3316 183.29 79.3977"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M113.262 114.787H133.744"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M172.446 156.805C172.446 156.805 152.569 136.926 136.304 156.355"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M162.847 91.2947C162.847 91.2947 168.718 91.9719 174.707 90.5053"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M157.412 63.5069C157.126 63.6433 156.816 63.7227 156.499 63.7404C154.283 63.8782 152.295 60.8608 152.062 57.0049C151.828 53.149 153.43 49.912 155.647 49.7648C156.214 49.7383 156.775 49.8974 157.244 50.2179"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M152.193 54.5105L163.863 53.7212"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M186.151 40.6937C186.151 40.6937 187.657 40.243 188.11 37.6833"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M186.001 42.5761C186.001 42.5761 188.71 42.7279 189.614 40.0071"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M167.252 51.381C167.252 51.381 169.51 45.2083 173.878 49.1973C176.477 51.5725 175.083 63.1285 169.737 61.6969"
            fill="white"
          />
          <path
            d="M167.252 51.381C167.252 51.381 169.51 45.2083 173.878 49.1973C176.477 51.5725 175.083 63.1285 169.737 61.6969"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M172.238 52.2334C172.238 52.2334 169.557 53.7305 169.34 55.3513C169.34 55.3513 170.811 56.8203 170.258 57.0375C169.704 57.2547 168.623 57.0796 168.623 57.0796"
            fill="white"
          />
          <path
            d="M172.238 52.2334C172.238 52.2334 169.557 53.7305 169.34 55.3513C169.34 55.3513 170.811 56.8203 170.258 57.0375C169.704 57.2547 168.623 57.0796 168.623 57.0796"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M64.469 56.0543H140.821"
            stroke="#12344D"
            strokeMiterlimit="10"
          />
          <path
            d="M157.646 51.4814C157.646 51.4814 157.985 50.9723 159.904 50.7457"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M167.1 83.3891C167.1 83.3891 166.649 86.1006 166.866 87.9059"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M181.8 80.9484C181.8 80.9484 182.538 84.2181 182.236 86.1005"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M171.327 82.9897C171.327 82.9897 171.544 86.4765 171.764 88.1394"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M176.722 82.4969C176.722 82.4969 176.94 85.9838 177.159 87.6489"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M109.911 116.369L103.096 126.988"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M100.837 120.549L105.298 123.09"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M103.423 116.983L107.883 119.524"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M154.376 119.757C154.376 119.757 153.472 123.027 154.61 128.228C155.747 133.429 154.843 136.926 154.843 136.926L151.34 122.13L154.376 119.757Z"
            fill="#0F1112"
          />
          <path
            d="M113.171 109.593L116.366 112.181H134.926L131.722 109.565C131.722 109.565 116.824 109.593 113.171 109.593Z"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M140.83 158.613C140.83 158.613 153.482 144.6 171.554 160.421"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M70.2656 107.183L84.1221 116.219"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M47.5974 133.99H113.036"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M75.5369 107.335H37.6365"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M75.99 139.109H154.15"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M135.512 100.735H79.1335C78.6245 100.734 78.1332 100.548 77.7524 100.21C77.3716 99.8722 77.1277 99.4067 77.0666 98.9014L72.5101 60.9028C72.4744 60.6102 72.5012 60.3134 72.5887 60.0319C72.6763 59.7504 72.8226 59.4907 73.018 59.2699C73.2133 59.0492 73.4533 58.8724 73.7221 58.7513C73.9908 58.6302 74.2822 58.5675 74.577 58.5673H130.662C131.17 58.5672 131.662 58.7538 132.042 59.0917C132.423 59.4296 132.666 59.8954 132.726 60.4007L136.86 94.457"
            fill="white"
          />
          <path
            d="M135.512 100.735H79.1335C78.6245 100.734 78.1332 100.548 77.7524 100.21C77.3716 99.8722 77.1277 99.4067 77.0666 98.9014L72.5101 60.9028C72.4744 60.6102 72.5012 60.3134 72.5887 60.0319C72.6763 59.7504 72.8226 59.4907 73.018 59.2699C73.2133 59.0492 73.4533 58.8724 73.7221 58.7513C73.9908 58.6302 74.2822 58.5675 74.577 58.5673H130.662C131.17 58.5672 131.662 58.7538 132.042 59.0917C132.423 59.4296 132.666 59.8954 132.726 60.4007L136.86 94.457"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M112.289 69H95.2088C94.5412 69 94 69.5412 94 70.2088V70.2117C94 70.8793 94.5412 71.4205 95.2088 71.4205H112.289C112.956 71.4205 113.497 70.8793 113.497 70.2117V70.2088C113.497 69.5412 112.956 69 112.289 69Z"
            fill="#12344D"
          />
          <path
            d="M96.7816 87.6351H111.062C111.062 83.7837 108.517 80.1722 105.24 79.3397C108.431 78.5427 110.819 75.3608 110.819 71.5508H96.5416C96.5416 75.4023 98.9918 78.6345 102.248 79.3752C99.0777 80.261 96.7816 83.8222 96.7816 87.6351Z"
            fill="#98CFF8"
          />
          <path
            d="M102.338 79.1263C102.539 79.2524 102.698 79.4361 102.794 79.6537C102.879 79.9196 102.917 80.1983 102.907 80.4773C102.936 81.3661 103.046 82.2549 103.158 83.1289C103.173 83.3435 103.231 83.553 103.327 83.7451C103.377 83.8399 103.454 83.9174 103.549 83.9676C103.644 84.0178 103.751 84.0383 103.858 84.0266C104.172 83.9644 104.284 83.5822 104.332 83.2652C104.512 82.1246 104.693 80.981 104.773 79.8285C104.812 79.2359 104.966 79.0197 105.505 78.833C106.032 78.6442 106.53 78.384 106.986 78.0598C107.915 77.3861 108.717 76.5536 109.356 75.6007C109.518 75.4045 109.621 75.166 109.653 74.9134C107.753 75.7548 105.28 75.6926 103.256 75.5059C102.23 75.4018 101.211 75.2365 100.205 75.0112C99.5291 74.863 98.2789 74.235 97.6834 74.8216C97.6676 74.8336 97.6545 74.8488 97.6448 74.8661C97.6351 74.8834 97.6291 74.9025 97.6271 74.9223C97.629 74.9504 97.6371 74.9777 97.6508 75.0023C98.1507 75.9867 98.8583 76.851 99.7246 77.5354C100.119 77.88 100.541 78.1912 100.987 78.4657C101.448 78.6625 101.899 78.883 102.338 79.1263V79.1263Z"
            fill="#3A6A8C"
          />
          <path
            d="M110.424 87.6352C110.424 86.4679 108.765 85.5288 107.9 84.9688C106.968 84.3168 105.938 83.8168 104.849 83.4875C104.058 83.2624 103.212 83.3428 102.479 83.7127C101.48 84.1945 100.516 84.7469 99.5958 85.3658C98.9144 85.8487 97.6227 86.702 97.6227 87.6175"
            fill="#3A6A8C"
          />
          <path
            d="M112.517 87.7567H95.437C94.7695 87.7567 94.2283 88.2979 94.2283 88.9655V88.9685C94.2283 89.6361 94.7695 90.1772 95.437 90.1772H112.517C113.184 90.1772 113.726 89.6361 113.726 88.9685V88.9655C113.726 88.2979 113.184 87.7567 112.517 87.7567Z"
            fill="#12344D"
          />
          <path
            d="M103.775 75.9C106.897 75.8891 109.527 75.2082 109.525 74.75C109.523 74.2919 106.883 73.8619 103.761 73.8728C100.639 73.8837 98.1093 74.2639 98.1109 74.722C98.1125 75.1802 100.653 75.9109 103.775 75.9Z"
            fill="#12344D"
          />
          <path
            d="M103.735 79.233C103.808 79.533 103.815 79.8455 103.756 80.1485"
            stroke="#98CFF8"
            strokeWidth="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M103.56 82.2284L103.649 82.9305"
            stroke="#98CFF8"
            strokeWidth="0.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M73.2061 63.7194H133.144"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M132.806 63.4555L73.101 63.4952L72.7764 60.9682C72.5638 59.5039 73.5541 58.7775 75.0348 58.7799L130.505 58.8149C131.673 58.8149 132.285 59.3941 132.488 60.5548L132.806 63.4555Z"
            fill="#12344D"
          />
          <path
            d="M75.9176 61.1597H101.367"
            stroke="#F6F7F9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M75.0861 139.411L74.1823 165.615"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M78.2484 154.771L121.621 154.636"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M135.795 97.9602C135.795 97.9602 134.508 99.4129 135.795 100.445C136.925 101.347 138.054 99.936 140.482 101.685C142.832 103.381 142.629 103.192 142.629 103.192"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M142.855 102.995C142.64 102.093 142.209 101.257 141.599 100.558C140.988 99.8596 140.217 99.3199 139.352 98.9855C136.339 97.7804 135.571 98.0887 134.945 96.9513C133.703 94.6929 137.43 94.5808 140.669 95.2207C142.783 95.6591 144.767 96.5798 146.466 97.9112"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'empty-incidents-list':
      return (
        <svg
          width="252"
          height="198"
          viewBox="0 0 252 198"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M105.279 114.411C105.279 114.411 121.824 120.43 126.208 119.55C130.592 118.669 132.047 117.796 136.732 111.95C141.417 106.104 150.172 95.0011 150.172 95.0011C150.172 95.0011 151.048 91.4979 161.864 91.2013C161.864 91.2013 164.216 91.0308 164.216 90.5006C164.216 89.9705 162.485 84.1738 164.893 83.1952C167.301 82.2166 173.779 83.4287 177.016 82.4432C180.253 81.4576 185.074 79.2061 186.053 79.5822C187.031 79.9582 186.882 85.4559 186.503 87.1866C186.503 87.1866 192.001 87.9386 194.635 89.5221C194.635 89.5221 183.642 91.255 178.899 94.492C174.156 97.729 171.161 100.209 169.055 104.32C165.986 110.306 169.732 128.791 170.183 132.031C170.634 135.27 174.324 157.707 174.324 157.707C174.324 157.707 163.933 149.122 157.382 148.218C157.382 148.218 155.124 129.244 151.433 120.425C151.433 120.425 146.013 124.716 143.603 126.75C141.192 128.784 131.598 135.459 128.961 136.363C126.325 137.267 123.202 135.798 117.328 133.093C111.454 130.389 98.9176 123.38 98.9176 123.38L105.279 114.411Z"
            fill="white"
          />
          <path
            d="M149 107.101H70.1116L64.0113 56.1874C63.759 54.0855 65.2537 52.2357 67.1875 52.2357H137.738C139.329 52.2357 140.679 53.5086 140.91 55.2275L145.735 91.2946"
            fill="white"
          />
          <path
            d="M86.9832 107.335L70.1116 107.101L64.0113 56.1874C63.759 54.0855 65.2537 52.2357 67.1875 52.2357H137.738C139.329 52.2357 140.679 53.5086 140.91 55.2275L146.5 97.5"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M159.164 48.2047C159.164 48.2047 154.846 47.3079 152.814 45.0378C150.782 42.7677 155.448 43.5898 157.604 44.4025C157.604 44.4025 152.503 41.9129 152.517 39.6217C152.531 37.3306 159.316 40.0468 159.316 40.0468C159.316 40.0468 154.39 36.5155 153.748 33.3976C153.106 30.2798 161.345 34.0703 165.778 34.1287C170.211 34.187 179.119 32.2439 181.37 33.5565C185.436 35.9293 185.849 38.433 185.849 38.433"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M163.412 47.114C163.412 47.114 162.055 49.8232 164.428 55.2462C166.801 60.6693 165.783 49.4846 170.188 49.4846C174.592 49.4846 170.673 66.8 176.96 69.8338C189.614 75.9341 188.708 45.6637 188.708 45.6637"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M157.793 50.2366C157.793 50.2366 156.929 59.623 157.515 66.2301C158.101 72.8372 157.812 76.957 160.983 77.312C163.452 77.5876 165.012 77.3354 167.177 76.6861C168.681 76.2354 169.585 74.4277 169.585 74.4277"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M167.177 77.1392C167.177 77.1392 167.817 78.1925 167.478 81.92"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M155.278 118.41C155.278 118.41 133.59 138.283 127.268 137.829C120.946 137.376 97.9017 124.284 97.9017 124.284L105.13 114.192C105.13 114.192 120.04 119.764 125.916 119.764C131.792 119.764 134.725 113.666 134.725 113.666C134.725 113.666 144.889 101.692 148.052 96.9489C151.214 92.2055 155.168 91.5772 158.561 91.5772C161.955 91.5772 163.419 91.2386 163.249 90.5613C162.478 87.4761 162.364 85.1196 164.379 83.3329C165.703 82.1652 169.893 83.4637 173.955 83.013C178.016 82.5622 178.626 82.2586 183.593 80.0002C188.561 77.7418 186.907 87.2286 186.907 87.2286C186.907 87.2286 193.232 87.3804 195.341 89.0362"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M151.212 122.469C151.212 122.469 155.731 136.926 155.731 147.769"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M97.8176 124.417C97.8176 124.417 85.6567 119.36 84.935 116.59C84.2133 113.821 92.5207 116.952 92.5207 116.952L88.7839 111.413C88.3206 112.384 87.5631 113.185 86.6189 113.701C85.0518 114.545 85.0518 112.618 86.0163 110.691C86.9809 108.764 88.3028 105.873 90.1104 107.559C91.9181 109.245 94.2046 111.653 94.2046 111.653"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M89.9329 106.816C89.9329 106.816 90.6336 103.456 93.233 106.053C95.1901 108.012 97.9039 110.271 97.9039 110.271"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M104.903 114.563C104.903 114.563 96.7712 105.978 95.7553 104.962C94.7394 103.946 93.2703 104.962 93.2703 104.962"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M140.973 102.666C140.973 102.666 135.475 102.589 135.4 104.17C135.326 105.751 136.907 103.418 139.917 106.204H97.8269"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M173.803 154.094C173.803 154.094 168.198 122.674 167.929 110.271C167.478 89.0363 217.04 81.1587 222.145 99.88C227.75 120.407 218.53 155.902 218.53 155.902"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M181.634 70.9641C181.634 70.9641 181.181 75.3316 183.29 79.3977"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M113.262 114.787H133.744"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M172.446 156.805C172.446 156.805 152.569 136.926 136.304 156.355"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M162.847 91.2947C162.847 91.2947 168.719 91.9719 174.707 90.5053"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M157.412 63.5069C157.126 63.6433 156.816 63.7227 156.499 63.7404C154.283 63.8782 152.295 60.8608 152.062 57.0049C151.828 53.149 153.43 49.912 155.647 49.7648C156.214 49.7383 156.775 49.8974 157.244 50.2179"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M152.193 54.5105L163.863 53.7212"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M186.151 40.6937C186.151 40.6937 187.657 40.243 188.11 37.6833"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M186.001 42.5761C186.001 42.5761 188.71 42.7279 189.614 40.0071"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M167.252 51.381C167.252 51.381 169.51 45.2083 173.878 49.1973C176.477 51.5725 175.083 63.1285 169.737 61.6969"
            fill="white"
          />
          <path
            d="M167.252 51.381C167.252 51.381 169.51 45.2083 173.878 49.1973C176.477 51.5725 175.083 63.1285 169.737 61.6969"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M172.238 52.2334C172.238 52.2334 169.557 53.7305 169.34 55.3513C169.34 55.3513 170.811 56.8203 170.258 57.0375C169.704 57.2547 168.623 57.0796 168.623 57.0796"
            fill="white"
          />
          <path
            d="M172.238 52.2334C172.238 52.2334 169.557 53.7305 169.34 55.3513C169.34 55.3513 170.811 56.8203 170.258 57.0375C169.704 57.2547 168.623 57.0796 168.623 57.0796"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M64.469 56.0543H140.821"
            stroke="#12344D"
            strokeMiterlimit="10"
          />
          <path
            d="M157.646 51.4814C157.646 51.4814 157.985 50.9723 159.904 50.7457"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M167.1 83.3891C167.1 83.3891 166.649 86.1006 166.866 87.9059"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M181.8 80.9484C181.8 80.9484 182.538 84.2181 182.236 86.1005"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M171.327 82.9897C171.327 82.9897 171.545 86.4765 171.764 88.1394"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M176.722 82.4969C176.722 82.4969 176.939 85.9838 177.159 87.6489"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M109.911 116.369L103.096 126.988"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M100.837 120.549L105.298 123.09"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M103.423 116.983L107.884 119.524"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M154.376 119.757C154.376 119.757 153.472 123.027 154.61 128.228C155.747 133.429 154.843 136.926 154.843 136.926L151.34 122.13L154.376 119.757Z"
            fill="#0F1112"
          />
          <path
            d="M113.171 109.593L116.366 112.181H134.926L131.722 109.565C131.722 109.565 116.824 109.593 113.171 109.593Z"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M140.83 158.613C140.83 158.613 153.482 144.6 171.554 160.421"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M70.2657 107.183L84.1222 116.219"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M47.5974 133.99H113.036"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M75.5369 107.335H37.6365"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M75.99 139.109H154.15"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M135.512 100.735H79.1336C78.6246 100.734 78.1333 100.548 77.7525 100.21C77.3717 99.8722 77.1277 99.4067 77.0667 98.9014L72.5101 60.9028C72.4744 60.6102 72.5012 60.3134 72.5888 60.0319C72.6763 59.7504 72.8226 59.4907 73.018 59.2699C73.2134 59.0492 73.4533 58.8724 73.7221 58.7513C73.9908 58.6302 74.2822 58.5675 74.577 58.5673H130.662C131.171 58.5672 131.662 58.7538 132.042 59.0917C132.423 59.4296 132.666 59.8954 132.726 60.4007L136.86 94.457"
            fill="white"
          />
          <path
            d="M135.512 100.735H79.1336C78.6246 100.734 78.1333 100.548 77.7525 100.21C77.3717 99.8722 77.1277 99.4067 77.0667 98.9014L72.5101 60.9028C72.4744 60.6102 72.5012 60.3134 72.5888 60.0319C72.6763 59.7504 72.8226 59.4907 73.018 59.2699C73.2134 59.0492 73.4533 58.8724 73.7221 58.7513C73.9908 58.6302 74.2822 58.5675 74.577 58.5673H130.662C131.171 58.5672 131.662 58.7538 132.042 59.0917C132.423 59.4296 132.666 59.8954 132.726 60.4007L136.86 94.457"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M73.2061 63.7194H133.144"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M132.806 63.4555L73.101 63.4952L72.7764 60.9682C72.5638 59.5039 73.5541 58.7775 75.0348 58.7799L130.505 58.8149C131.673 58.8149 132.285 59.3941 132.488 60.5548L132.806 63.4555Z"
            fill="#12344D"
          />
          <path
            d="M75.9176 61.1597H101.368"
            stroke="#F6F7F9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M75.0862 139.411L74.1823 165.615"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M78.2484 154.771L121.621 154.636"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M135.795 97.9602C135.795 97.9602 134.508 99.4129 135.795 100.445C136.926 101.347 138.054 99.936 140.482 101.685C142.832 103.381 142.629 103.192 142.629 103.192"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M142.855 102.995C142.64 102.093 142.209 101.257 141.599 100.558C140.988 99.8596 140.217 99.3199 139.352 98.9855C136.339 97.7804 135.571 98.0887 134.945 96.9513C133.703 94.6929 137.43 94.5808 140.669 95.2207C142.783 95.6591 144.767 96.5798 146.466 97.9112"
            stroke="#12344D"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <ellipse
            cx="105.488"
            cy="80.093"
            rx="7.81396"
            ry="7.81395"
            fill="#29B15C"
          />
          <path
            d="M103.485 79.9993C103.264 79.7943 102.916 79.8041 102.708 80.0212C102.5 80.2383 102.51 80.5805 102.73 80.7855L104.379 82.3175C104.599 82.5226 104.947 82.5128 105.155 82.2957L108.269 79.0515C108.477 78.8344 108.467 78.4922 108.246 78.2871C108.026 78.0821 107.678 78.0919 107.47 78.309L104.734 81.1601L103.485 79.9993Z"
            fill="white"
          />
        </svg>
      );
    case 'searchEmptyResponse':
      return (
        <svg
          width="134"
          height="122"
          viewBox="0 0 114 72"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M95.4009 70.9626H3.06005C2.51888 70.9669 1.99832 70.7653 1.61406 70.4025C1.22979 70.0398 1.01365 69.546 1.01367 69.0309V2.8943C1.02296 1.82488 1.93644 0.96261 3.06005 0.962646H111.221C111.759 0.962646 112.275 1.16616 112.656 1.52841C113.036 1.89067 113.25 2.38199 113.25 2.8943V69.0181C113.25 70.0849 112.341 70.9497 111.221 70.9497"
            fill="#fff"
          />
          <path
            d="M95.4009 70.9626H3.06005C2.51888 70.9669 1.99832 70.7653 1.61406 70.4025C1.22979 70.0398 1.01365 69.546 1.01367 69.0309V2.8943C1.02296 1.82488 1.93644 0.96261 3.06005 0.962646H111.221C111.759 0.962646 112.275 1.16616 112.656 1.52841C113.036 1.89067 113.25 2.38199 113.25 2.8943V69.0181C113.25 70.0849 112.341 70.9497 111.221 70.9497"
            stroke="#6683A3"
            strokeWidth="0.5"
          />
          <path
            d="M6.66688 6.74002C7.18621 6.74002 7.6072 6.31902 7.6072 5.7997C7.6072 5.28037 7.18621 4.85938 6.66688 4.85938C6.14756 4.85938 5.72656 5.28037 5.72656 5.7997C5.72656 6.31902 6.14756 6.74002 6.66688 6.74002Z"
            fill="#4790D6"
          />
          <path
            d="M9.76161 6.74002C10.2809 6.74002 10.7019 6.31902 10.7019 5.7997C10.7019 5.28037 10.2809 4.85938 9.76161 4.85938C9.24228 4.85938 8.82129 5.28037 8.82129 5.7997C8.82129 6.31902 9.24228 6.74002 9.76161 6.74002Z"
            fill="#4790D6"
          />
          <path
            d="M12.8534 6.74002C13.3727 6.74002 13.7937 6.31902 13.7937 5.7997C13.7937 5.28037 13.3727 4.85938 12.8534 4.85938C12.3341 4.85938 11.9131 5.28037 11.9131 5.7997C11.9131 6.31902 12.3341 6.74002 12.8534 6.74002Z"
            fill="#4790D6"
          />
          <rect
            x="23.2178"
            y="15.9602"
            width="52.6924"
            height="7.63658"
            rx="3.81829"
            fill="#DDEAF8"
          />
          <rect
            x="20.9268"
            y="46.5066"
            width="52.6924"
            height="7.63658"
            rx="3.81829"
            fill="#DDEAF8"
          />
          <rect
            x="32.3818"
            y="31.2334"
            width="52.6924"
            height="7.63658"
            rx="3.81829"
            fill="#DDEAF8"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M44.6004 27.4149C44.6004 29.5237 42.8909 31.2332 40.7821 31.2332L32.355 31.2332C32.3638 31.2332 32.3727 31.2333 32.3815 31.2333H64.4555C62.3467 31.2333 60.6372 29.5238 60.6372 27.415C60.6372 25.3062 62.3467 23.5967 64.4555 23.5967H40.8086C42.9052 23.6109 44.6004 25.3149 44.6004 27.4149Z"
            fill="#DDEAF8"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M45.3641 42.6881C45.3641 44.7969 43.6546 46.5064 41.5458 46.5064L33.1187 46.5064C33.1275 46.5064 33.1363 46.5065 33.1452 46.5065H65.2192C63.1104 46.5065 61.4009 44.7969 61.4009 42.6882C61.4009 40.5794 63.1104 38.8699 65.2192 38.8699H41.5722C43.6688 38.8841 45.3641 40.5881 45.3641 42.6881Z"
            fill="#DDEAF8"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M73.3134 46.1589C77.0046 40.4057 76.3345 32.6646 71.3031 27.6332C65.4986 21.8287 56.0878 21.8287 50.2833 27.6332C44.4789 33.4376 44.4789 42.8485 50.2833 48.6529C55.3148 53.6844 63.056 54.3544 68.8092 50.6632L78.8974 60.7514C79.6785 61.5325 80.9448 61.5325 81.7259 60.7514L83.4017 59.0756C84.1827 58.2946 84.1827 57.0282 83.4017 56.2472L73.3134 46.1589ZM66.9502 31.9865C70.3504 35.3867 70.3504 40.8995 66.9502 44.2997C63.55 47.6999 58.0372 47.6999 54.6371 44.2997C51.2369 40.8995 51.2369 35.3867 54.6371 31.9865C58.0372 28.5863 63.55 28.5863 66.9502 31.9865Z"
            fill="#BBCCE2"
          />
          <mask id="path-12-inside-1" fill="white">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M73.3133 44.0358C77.0046 38.2826 76.3345 30.5413 71.3031 25.5099C65.4986 19.7054 56.0878 19.7054 50.2833 25.5099C44.4789 31.3143 44.4789 40.7252 50.2833 46.5297C55.3147 51.561 63.0558 52.2311 68.809 48.54L78.8974 58.6284C79.6785 59.4095 80.9448 59.4095 81.7259 58.6284L83.4017 56.9526C84.1827 56.1716 84.1827 54.9052 83.4017 54.1242L73.3133 44.0358ZM66.9502 29.8633C70.3504 33.2635 70.3504 38.7763 66.9502 42.1765C63.55 45.5767 58.0373 45.5767 54.6371 42.1765C51.2369 38.7763 51.2369 33.2635 54.6371 29.8633C58.0373 26.4631 63.55 26.4631 66.9502 29.8633Z"
            />
          </mask>
          <path
            d="M73.3133 44.0358L72.4716 43.4958L72.0368 44.1735L72.6062 44.7429L73.3133 44.0358ZM68.809 48.54L69.5161 47.8329L68.9467 47.2635L68.269 47.6983L68.809 48.54ZM81.7259 58.6284L82.433 59.3355L81.7259 58.6284ZM83.4017 56.9526L82.6946 56.2455L83.4017 56.9526ZM74.1549 44.5758C78.0947 38.4353 77.381 30.1736 72.0102 24.8028L70.596 26.217C75.288 30.9091 75.9144 38.1299 72.4716 43.4958L74.1549 44.5758ZM72.0102 24.8028C65.8152 18.6078 55.7712 18.6078 49.5762 24.8028L50.9904 26.217C56.4044 20.8031 65.1821 20.8031 70.596 26.217L72.0102 24.8028ZM49.5762 24.8028C43.3812 30.9978 43.3812 41.0418 49.5762 47.2368L50.9904 45.8226C45.5765 40.4086 45.5765 31.6309 50.9904 26.217L49.5762 24.8028ZM49.5762 47.2368C54.947 52.6075 63.2085 53.3213 69.349 49.3816L68.269 47.6983C62.9031 51.141 55.6824 50.5146 50.9904 45.8226L49.5762 47.2368ZM79.6045 57.9213L69.5161 47.8329L68.1019 49.2471L78.1903 59.3355L79.6045 57.9213ZM81.0188 57.9213C80.6282 58.3118 79.9951 58.3118 79.6045 57.9213L78.1903 59.3355C79.3619 60.5071 81.2614 60.5071 82.433 59.3355L81.0188 57.9213ZM82.6946 56.2455L81.0188 57.9213L82.433 59.3355L84.1088 57.6597L82.6946 56.2455ZM82.6946 54.8313C83.0851 55.2218 83.0851 55.855 82.6946 56.2455L84.1088 57.6597C85.2804 56.4881 85.2804 54.5886 84.1088 53.4171L82.6946 54.8313ZM72.6062 44.7429L82.6946 54.8313L84.1088 53.4171L74.0204 43.3287L72.6062 44.7429ZM67.6573 42.8836C71.4481 39.0929 71.4481 32.9469 67.6573 29.1562L66.2431 30.5704C69.2528 33.5801 69.2528 38.4597 66.2431 41.4694L67.6573 42.8836ZM53.93 42.8836C57.7207 46.6743 63.8666 46.6743 67.6573 42.8836L66.2431 41.4694C63.2335 44.479 58.3538 44.479 55.3442 41.4694L53.93 42.8836ZM53.93 29.1562C50.1392 32.9469 50.1392 39.0929 53.93 42.8836L55.3442 41.4694C52.3345 38.4597 52.3345 33.5801 55.3442 30.5704L53.93 29.1562ZM67.6573 29.1562C63.8666 25.3655 57.7207 25.3655 53.93 29.1562L55.3442 30.5704C58.3538 27.5607 63.2335 27.5607 66.2431 30.5704L67.6573 29.1562Z"
            fill="#4790D6"
            mask="url(#path-12-inside-1)"
          />
        </svg>
      );
    case 'emptyCannedResponse':
    case 'emptySubscribers':
      return (
        <svg
          width="134"
          height="122"
          viewBox="0 0 114 72"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M95.1884 70.9999H3.04207C2.50204 71.0042 1.98258 70.8026 1.59912 70.4399C1.21566 70.0772 0.999981 69.5834 1 69.0683V2.93165C1.00927 1.86223 1.92082 0.999963 3.04207 1H110.975C111.512 1 112.027 1.20351 112.407 1.56577C112.787 1.92802 113 2.41935 113 2.93165V69.0554C113 70.1222 112.093 70.9871 110.975 70.9871"
            fill="#f6f6f6"
          />
          <path
            d="M95.1884 70.9999H3.04207C2.50204 71.0042 1.98258 70.8026 1.59912 70.4399C1.21566 70.0772 0.999981 69.5834 1 69.0683V2.93165C1.00927 1.86223 1.92082 0.999963 3.04207 1H110.975C111.512 1 112.027 1.20351 112.407 1.56577C112.787 1.92802 113 2.41935 113 2.93165V69.0554C113 70.1222 112.093 70.9871 110.975 70.9871"
            stroke="#6683A3"
            strokeWidth="0.5"
          />
          <rect
            x="12.1162"
            y="28.0317"
            width="89.5471"
            height="32.731"
            rx="4.27"
            stroke="#4790D6"
            strokeWidth="0.65"
          />
          <path
            d="M14.2406 17.533H98.9224C99.4807 17.5288 100.018 17.7113 100.416 18.0405C100.815 18.3697 101.041 18.8185 101.046 19.2883V23.8091C101.041 24.2786 100.815 24.7272 100.416 25.0558C100.018 25.3845 99.4803 25.5663 98.9224 25.5612H14.2406C13.6827 25.5663 13.1453 25.3845 12.7468 25.0558C12.3484 24.7272 12.1217 24.2786 12.1167 23.8091V19.2883C12.1217 18.8185 12.3483 18.3697 12.7466 18.0405C13.1449 17.7113 13.6823 17.5288 14.2406 17.533V17.533Z"
            fill="#DDEAF8"
          />
          <rect
            x="16.7114"
            y="33.8647"
            width="4.289"
            height="4.289"
            rx="1"
            fill="white"
            stroke="#C0DEFC"
            strokeWidth="0.54"
          />
          <rect
            x="16.7114"
            y="42.6279"
            width="4.289"
            height="4.289"
            rx="1"
            fill="white"
            stroke="#C0DEFC"
            strokeWidth="0.54"
          />
          <rect
            x="16.7114"
            y="51.3911"
            width="4.289"
            height="4.289"
            rx="1"
            fill="white"
            stroke="#C0DEFC"
            strokeWidth="0.54"
          />
          <rect x="25" y="33" width="49" height="6" rx="3" fill="#DDEAF8" />
          <rect x="25" y="42" width="67" height="6" rx="3" fill="#DDEAF8" />
          <rect x="25" y="51" width="57" height="6" rx="3" fill="#DDEAF8" />
          <path
            d="M6.66636 7.56489C7.18566 7.56489 7.60664 7.14391 7.60664 6.62461C7.60664 6.10531 7.18566 5.68433 6.66636 5.68433C6.14705 5.68433 5.72607 6.10531 5.72607 6.62461C5.72607 7.14391 6.14705 7.56489 6.66636 7.56489Z"
            fill="#4790D6"
          />
          <path
            d="M9.76108 7.56489C10.2804 7.56489 10.7014 7.14391 10.7014 6.62461C10.7014 6.10531 10.2804 5.68433 9.76108 5.68433C9.24178 5.68433 8.8208 6.10531 8.8208 6.62461C8.8208 7.14391 9.24178 7.56489 9.76108 7.56489Z"
            fill="#4790D6"
          />
          <path
            d="M12.8524 7.56489C13.3717 7.56489 13.7927 7.14391 13.7927 6.62461C13.7927 6.10531 13.3717 5.68433 12.8524 5.68433C12.3331 5.68433 11.9121 6.10531 11.9121 6.62461C11.9121 7.14391 12.3331 7.56489 12.8524 7.56489Z"
            fill="#4790D6"
          />
        </svg>
      );
    case 'tick':
      return (
        <svg
          width="28"
          height="28"
          viewBox="-2 -4 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 8.5L7 14.5L20.5 1"
            stroke="#2F2FBE"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'cancel':
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.1462 15.4005L19.5361 20.3984C19.9757 20.806 20.6625 20.7801 21.0701 20.3405C21.4777 19.901 21.4518 19.2142 21.0122 18.8066L15.6223 13.8086L20.6785 8.35584C21.0897 7.91241 21.0635 7.2196 20.6201 6.80842C20.1767 6.39723 19.4839 6.42337 19.0727 6.86681L14.0164 12.3196L8.61254 7.30866C8.17297 6.90105 7.48619 6.92697 7.07858 7.36654C6.67097 7.80612 6.69688 8.4929 7.13646 8.90051L12.5404 13.9114L7.38054 19.4759C6.96935 19.9193 6.9955 20.6121 7.43893 21.0233C7.88236 21.4345 8.57516 21.4084 8.98635 20.9649L14.1462 15.4005Z"
            fill="#E65555"
          />
        </svg>
      );
    case 'status-embed':
      return (
        <svg
          id="icon-magicwand"
          width="20"
          height="23"
          viewBox="0 0 20 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.1867 18.605L16.5636 13.3583C17.4222 12.092 17.9244 10.5652 17.9244 8.92325C17.9244 4.55436 14.37 1 10.0011 1C5.63224 1 2.07788 4.55436 2.07788 8.92325C2.07788 10.5652 2.58007 12.0921 3.43883 13.3586L0.8154 18.605C0.719354 18.7973 0.729685 19.0254 0.842519 19.2081C0.955514 19.3908 1.15503 19.5022 1.36989 19.5022H4.3796L6.18543 21.91C6.30327 22.0669 6.48729 22.1579 6.68132 22.1579C6.93847 22.1579 7.14105 22.0046 7.23581 21.8152L9.723 16.841C9.81534 16.8442 9.90799 16.8465 10.0011 16.8465C10.0943 16.8465 10.1869 16.8442 10.2793 16.841L12.7665 21.8152C12.8609 22.0043 13.0636 22.1579 13.3209 22.1579C13.5148 22.1579 13.699 22.0669 13.8167 21.91L15.6227 19.5022H18.6324C18.8472 19.5022 19.0468 19.3908 19.1596 19.2081C19.2726 19.0254 19.2829 18.7973 19.1867 18.605ZM6.57559 20.3637L5.18542 18.5104C5.06839 18.3543 4.88469 18.2625 4.68953 18.2625H2.3728L4.29631 14.4158C5.38849 15.5496 6.81417 16.3601 8.41403 16.6867L6.57559 20.3637ZM3.31761 8.92325C3.31761 5.23798 6.31586 2.23972 10.0011 2.23972C13.6864 2.23972 16.6847 5.23798 16.6847 8.92325C16.6847 12.6085 13.6864 15.6068 10.0011 15.6068C6.31586 15.6068 3.31761 12.6085 3.31761 8.92325ZM15.3126 18.2625C15.1176 18.2625 14.9339 18.3543 14.8167 18.5104L13.4267 20.3637L11.5881 16.6867C13.1881 16.36 14.6139 15.5496 15.7061 14.4155L17.6293 18.2623H15.3126V18.2625Z"
            fill="white"
            stroke="white"
            strokeWidth="0.3"
          />
          <path
            id="wand"
            d="M7.75509 8.5L7.04435 7.12709C6.98302 7.00868 6.98545 6.86615 7.05071 6.75C7.11596 6.63385 7.23487 6.56042 7.3653 6.55578L8.87755 6.50193L9.67906 5.1829C9.74821 5.06915 9.8695 5 10 5C10.1305 5 10.2518 5.06915 10.321 5.1829L11.1224 6.50193L12.6347 6.55578C12.7651 6.56042 12.884 6.63385 12.9493 6.75C13.0145 6.86615 13.017 7.00868 12.9556 7.12709L12.2449 8.5L12.9556 9.87291C13.017 9.99132 13.0145 10.1339 12.9493 10.25C12.884 10.3661 12.7651 10.4396 12.6347 10.4442L11.1224 10.4981L10.321 11.8171C10.2518 11.9309 10.1305 12 10 12C9.8695 12 9.74821 11.9309 9.67906 11.8171L8.87758 10.4981L7.36533 10.4442C7.23489 10.4396 7.11599 10.3661 7.05073 10.25C6.98548 10.1339 6.98304 9.99132 7.04438 9.87291L7.75509 8.5ZM9.10496 9.7279C9.23073 9.73238 9.34615 9.80087 9.41279 9.91059L10 10.877L10.5872 9.91062C10.6539 9.80089 10.7693 9.73243 10.895 9.72793L12.0029 9.68846L11.4822 8.68266C11.4231 8.56846 11.4231 8.43154 11.4822 8.31734L12.0029 7.31154L10.895 7.27207C10.7693 7.26759 10.6538 7.19911 10.5872 7.08938L10 6.12305L9.41282 7.08941C9.34615 7.19913 9.23076 7.26759 9.10499 7.2721L7.9971 7.31157L8.51779 8.31737C8.57689 8.43157 8.57689 8.56851 8.51776 8.68269L7.99708 9.68849L9.10496 9.7279Z"
            fill="white"
            stroke="white"
            strokeWidth="0.4"
          />
        </svg>
      );
    case 'empty-status-embed':
      return (
        <svg
          width="229"
          height="104"
          viewBox="0 0 229 104"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 39.9514V28.0829C0 25.6791 2.66129 24.2197 4.67873 25.5289L20.3031 35.6375C22.7069 37.1828 21.8269 40.8742 18.9939 41.1962L3.36954 42.9775C1.56673 43.1707 0 41.7542 0 39.9514Z"
            fill="#F4F4F4"
          />
          <path
            d="M38.3521 32.9336L20.8606 27.6968C17.3193 26.6452 16.3535 22.0738 19.1651 19.6701L40.9061 1.12686C44.2328 -1.70612 49.2978 1.19125 48.5252 5.50512L44.2542 29.285C43.7821 32.0322 41.0134 33.7277 38.3521 32.9336Z"
            fill="#F4F4F4"
          />
          <path
            d="M194.271 1.1085L202.835 9.11385C204.573 10.7235 203.822 13.6209 201.525 14.2004L183.733 18.6645C181.008 19.3512 178.926 16.2393 180.6 13.9857L189.829 1.51628C190.88 0.0783228 192.962 -0.0933723 194.271 1.1085Z"
            fill="#F4F4F4"
          />
          <path
            d="M9.78629 12.9535C13.3304 12.9535 16.2034 10.0804 16.2034 6.5363C16.2034 2.9922 13.3304 0.119141 9.78629 0.119141C6.2422 0.119141 3.36914 2.9922 3.36914 6.5363C3.36914 10.0804 6.2422 12.9535 9.78629 12.9535Z"
            fill="#F4F4F4"
          />
          <path
            d="M228.999 98.9078C228.999 100.56 228.871 102.17 228.635 103.758H5.19336C3.97003 99.7234 3.32617 95.431 3.32617 90.9883C3.32617 66.5216 23.1356 46.7122 47.5809 46.7122C48.2462 46.7122 48.8901 46.7337 49.5339 46.7551C54.5775 46.9697 59.1489 43.9221 60.9302 39.2219C67.7981 21.2797 85.1823 8.53125 105.55 8.53125C122.612 8.53125 137.593 17.4809 146.049 30.9591C148.56 34.951 153.195 37.1187 157.853 36.3031C160.106 35.9168 162.424 35.7022 164.785 35.7022C182.362 35.7022 197.3 46.9483 202.794 62.637C203.91 65.7919 206.293 68.3674 209.405 69.6336C220.908 74.3767 228.999 85.6872 228.999 98.9078Z"
            fill="#EFEFEF"
          />
          <path
            d="M160.493 103.737H108.147L82.4355 90.9668L136.198 89.0996L160.493 103.737Z"
            fill="#D0D0D0"
          />
          <path
            d="M130.317 59.7617H80.2891V74.4203H130.317V59.7617Z"
            fill="#F7F9FC"
          />
          <path
            d="M130.317 41.6035H80.2891V56.2621H130.317V41.6035Z"
            fill="#F7F9FC"
          />
          <path
            d="M132.464 79.5488H82.4355V90.9667H132.464V79.5488Z"
            fill="#E4E4E4"
          />
          <path
            d="M151.114 77.9395H101.086V92.5981H151.114V77.9395Z"
            fill="#F7F9FC"
          />
          <path
            d="M105.314 69.9336C106.878 69.9336 108.147 68.6652 108.147 67.1006C108.147 65.536 106.878 64.2676 105.314 64.2676C103.749 64.2676 102.48 65.536 102.48 67.1006C102.48 68.6652 103.749 69.9336 105.314 69.9336Z"
            fill="#D0D0D0"
          />
          <path
            d="M125.833 88.666C127.398 88.666 128.666 87.3976 128.666 85.833C128.666 84.2684 127.398 83 125.833 83C124.268 83 123 84.2684 123 85.833C123 87.3976 124.268 88.666 125.833 88.666Z"
            fill="#D0D0D0"
          />
          <path
            d="M105.314 51.7558C106.878 51.7558 108.147 50.4874 108.147 48.9228C108.147 47.3582 106.878 46.0898 105.314 46.0898C103.749 46.0898 102.48 47.3582 102.48 48.9228C102.48 50.4874 103.749 51.7558 105.314 51.7558Z"
            fill="#D0D0D0"
          />
        </svg>
      );

    default:
      return null;
  }
};
